import ky from "ky";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/v1";
const AI_BASE  = import.meta.env.VITE_AI_BASE  ?? "/v1";

// Auth header injection. In production the token is set by the Keycloak/OIDC
// flow via `setAuthToken(...)` once the user logs in. In dev, `npm run dev`
// can populate VITE_DEV_TOKEN + VITE_DEV_PS_CODE from a local .env file —
// these are intentionally NOT compiled into prod bundles (DEV is false there
// and the conditional is tree-shaken by Vite).
let _token: string | null = import.meta.env.DEV
  ? (import.meta.env.VITE_DEV_TOKEN ?? null)
  : null;
let _psCode: string | null = import.meta.env.DEV
  ? (import.meta.env.VITE_DEV_PS_CODE ?? null)
  : null;

export function setAuthToken(token: string | null, psCode: string | null) {
  _token = token;
  _psCode = psCode;
}

export function authHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  if (_token) h.Authorization = _token.startsWith("Bearer ") ? _token : `Bearer ${_token}`;
  if (_psCode) h["X-PS-Code"] = _psCode;
  return h;
}

/** Token without the "Bearer " prefix (for query-string auth on plain links). */
export function bareToken(): string {
  return (_token ?? "").replace(/^Bearer\s+/, "");
}
export function currentPsCode(): string { return _psCode ?? ""; }
function withFetchAuth(extra: Record<string, string> = {}): Record<string, string> {
  return { ...authHeaders(), ...extra };
}

const kyOpts = {
  // Send cookies on cross-origin so the future httpOnly-cookie auth path
  // works automatically. Header-based auth (authHeaders()) is still attached
  // via beforeRequest until the legacy session-storage token is retired.
  credentials: "include" as const,
  hooks: {
    beforeRequest: [
      (req: Request) => {
        for (const [k, v] of Object.entries(authHeaders())) req.headers.set(k, v);
      },
    ],
  },
};
const httpFIR = ky.create({ prefixUrl: API_BASE, timeout: 10_000, retry: 1, ...kyOpts });
const httpAI  = ky.create({ prefixUrl: AI_BASE,  timeout: 30_000, retry: 0, ...kyOpts });

export interface Bilingual { en: string; mr: string; }
export interface SuggestedSection {
  code: string;
  title: Bilingual;
  rationale: Bilingual;
  confidence: number;
}
export interface FIRDraft {
  interaction_id: string;
  summary: Bilingual;
  suggested_sections: SuggestedSection[];
  parties: Bilingual[];
  place_of_offence: Bilingual;
  time_of_offence_iso: string;
  next_steps_checklist: Bilingual[];
  clarifications_needed: Bilingual[];
  sensitivity: "routine" | "sensitive" | "highly_sensitive";
  pocso_jj_pcma_flag: boolean;
}

export async function aiDraftFIR(transcript: string, language: "mr" | "hi" | "en" = "mr") {
  return httpAI
    .post("ai/draft-fir", { json: { transcript, language } })
    .json<FIRDraft>();
}

export async function recordAIDecision(
  interaction_id: string,
  decision: "accepted" | "edited" | "rejected",
  note?: string,
) {
  return httpAI.post("ai/decision", { json: { interaction_id, decision, note } }).json();
}

export interface ComplaintAssistResult {
  summary: { en: string; mr: string };
  category: string;
  urgency: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  suggested_channel: string;
  legal_reasoning: { en: string; mr: string };
  suggested_sections: SuggestedSection[];
  clarifications_needed: { en: string; mr: string }[];
  interaction_id: string;
  provider?: string;
  model?: string;
}

export async function aiComplaintAssist(raw_text: string) {
  // Bilingual legal reasoning over OCR/audio extracts can take well past the
  // 30s httpAI default — give it generous headroom (6 min) over the backend's
  // 300s LLM cap so the client never aborts a request that's still in flight.
  return httpAI.post("ai/complaint-assist", { json: { raw_text }, timeout: 360_000 }).json<ComplaintAssistResult>();
}

export interface LLMInfo {
  provider: "anthropic" | "openai_compat";
  base_url?: string;
  model?: string;
  configured: boolean;
}

export const aiLLMInfo = () => httpAI.get("ai/llm-info").json<LLMInfo>();

export interface LLMTestResult {
  ok: boolean; status_code?: number; latency_ms?: number;
  text?: string; error?: string; model?: string;
  provider: string; base_url?: string;
  usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
}
export const aiLLMTest = (prompt: string, system?: string, max_tokens = 200) =>
  httpAI.post("ai/llm-test", { json: { prompt, system, max_tokens } }).json<LLMTestResult>();

// Per-button AI configuration — every AI button surfaced here so the IO can
// override model / max_tokens / temperature / prompt prefix from /admin/ai-config.
export interface AIButton {
  name: string;
  label_en: string; label_mr: string;
  page: string; endpoint: string;
  default_max_tokens: number; default_temperature: number;
  default_prompt_prefix: string;
  // Optional — present only on buttons that support a separate reasoning
  // model for the "deep" code-path (e.g. case_brief).
  supports_deep_mode?: boolean;
  default_deep_model?: string;
  deep_model_options?: string[];
  // Optional — present only on buttons that also sample uploaded video
  // (e.g. panchanama_draft).
  supports_video_sampling?: boolean;
  default_video_fps?: number;
  default_max_video_frames?: number;
  default_max_video_secs?: number;
  default_max_photos?: number;
  current: {
    model: string | null;
    max_tokens: number | null;
    temperature: number | null;
    prompt_prefix: string | null;
    deep_model?: string | null;
    video_fps?: number | null;
    max_video_frames?: number | null;
    max_video_secs?: number | null;
    max_photos?: number | null;
  };
  global_model: string;
  effective_model: string;
}
export interface AIButtonConfig {
  model?: string | null;
  max_tokens?: number | null;
  temperature?: number | null;
  prompt_prefix?: string | null;
  deep_model?: string | null;
  video_fps?: number | null;
  max_video_frames?: number | null;
  max_video_secs?: number | null;
  max_photos?: number | null;
}
export interface AIButtonTestResult {
  ok: boolean;
  error?: string | null;
  latency_ms: number;
  applied_overrides: Record<string, any>;
  effective_provider: string;
  effective_model: string;
  parsed_output: any;
  raw_text: string;
}
export const aiListButtons = () => httpAI.get("ai/buttons").json<AIButton[]>();
export const aiSaveButton  = (name: string, body: AIButtonConfig) =>
  httpAI.put(`ai/buttons/${name}`, { json: body }).json<{ ok: boolean }>();
export const aiTestButton  = (name: string, sample_text: string) =>
  httpAI.post(`ai/buttons/${name}/test`, { json: { sample_text }, timeout: 60_000 })
        .json<AIButtonTestResult>();

// Feature 006 — AI-usage analytics for the dashboard (GET /v1/ai/analytics, PS-scoped).
export interface AiAnalytics {
  ps_code: string | null;
  generated_at: string;
  interactions: {
    total: number; last_24h: number; last_7d: number;
    accepted: number; rejected: number; pending: number;
    avg_latency_ms: number | null;
    tokens: { input: number; output: number; cache_read: number; cache_write: number };
    by_purpose: { purpose: string; n: number; accepted: number }[];
    by_model: { model: string; n: number }[];
  };
  escalations: { total: number; last_7d: number };
  aio_sahayak: {
    sessions: number; sessions_7d: number; open_now: number; turns: number; tool_calls: number;
    by_tool: { tool_key: string; n: number }[];
    by_status: { status: string; n: number }[];
    by_lang: { lang: string; n: number }[];
  };
  complaint_bot: {
    sessions: number; sessions_7d: number; led_to_complaint: number; led_to_fir: number; tool_calls: number;
    by_tool: { tool_key: string; n: number }[];
  };
  tool_captures: { total: number; last_7d: number; by_tool: { tool_id: string; n: number }[] };
  daily: { day: string; interactions: number; aio_sessions: number; bot_sessions: number }[];
}
export const getAiAnalytics = () => httpAI.get("ai/analytics").json<AiAnalytics>();

export interface Contradiction {
  field: string;
  description_en: string; description_mr: string;
  sources: string[];
  severity: "LOW" | "MEDIUM" | "HIGH";
  suggested_clarification_en: string; suggested_clarification_mr: string;
}
export interface ContradictionReport {
  contradictions: Contradiction[];
  consensus_summary_en: string; consensus_summary_mr: string;
  overall_consistency: "HIGH" | "MODERATE" | "LOW" | "POOR" | "INSUFFICIENT_DATA";
}
export interface ContradictionCheckResult {
  fir_id: string; n_statements: number;
  kinds?: string[];
  accused_statements_present?: boolean;
  advisory?: string | null;
  interaction_id?: string;
  persisted_count?: number;       // 025
  report: ContradictionReport;
  provider?: string; model?: string;
}
export const aiContradictionCheck = (fir_id: string) =>
  httpAI.post("ai/contradiction-check", { json: { fir_id } })
    .json<ContradictionCheckResult>();

// 025 — persisted contradictions
export type ContradictionStatus =
  | "OPEN" | "ACKNOWLEDGED" | "FOLLOW_UP_REQUESTED"
  | "RESOLVED" | "FALSE_POSITIVE" | "WONTFIX"
  | "EXPOSED_AT_TRIAL";

export interface FirContradictionRow {
  id: string;
  kind: string;
  severity: "LOW" | "MEDIUM" | "HIGH";
  description_en: string; description_mr: string | null;
  source_a_kind: string; source_a_id: string | null; source_a_label: string | null;
  source_b_kind: string; source_b_id: string | null; source_b_label: string | null;
  quote_a: string | null; quote_b: string | null;
  suggested_clarification_en: string | null;
  suggested_clarification_mr: string | null;
  status: ContradictionStatus;
  resolution_note: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  follow_up_statement_id: string | null;
  created_at: string | null;
  created_by: string | null;
  // 025 Phase 4 — statement weight + audio metadata for each source slot.
  source_a_flags?: string[];
  source_b_flags?: string[];
  source_a_has_audio?: boolean;
  source_b_has_audio?: boolean;
  effective_severity?: "LOW" | "MEDIUM" | "HIGH";
}

// 025 Phase 4 — printable BSA §145 brief URL (token in query string).
export const contradictionsPrintUrl = (firId: string) =>
  withAuthQS(`/v1/firs/${firId}/contradictions/print`);
// 025 follow-up — .docx variant of the same brief (for APP filing).
export const contradictionsDocxUrl = (firId: string) =>
  withAuthQS(`/v1/firs/${firId}/contradictions/print.docx`);

export const listContradictions = (
  fir_id: string,
  opts?: { status?: ContradictionStatus; severity?: "LOW"|"MEDIUM"|"HIGH" },
) => {
  const qs: Record<string, string> = {};
  if (opts?.status) qs.status = opts.status;
  if (opts?.severity) qs.severity = opts.severity;
  return httpAI.get(`firs/${fir_id}/contradictions`, { searchParams: qs })
    .json<{ fir_id: string; count: number; contradictions: FirContradictionRow[] }>();
};

export const setContradictionStatus = (
  cid: string,
  body: { status: ContradictionStatus; resolution_note?: string; follow_up_statement_id?: string },
) => httpAI.patch(`contradictions/${cid}/status`, { json: body }).json<{ id: string; status: string }>();

export const clarifyContradiction = (
  cid: string,
  body: { note: string; follow_up_statement_id?: string },
) => httpAI.post(`contradictions/${cid}/clarify`, { json: body }).json<{ id: string; status: string }>();

// ---------------------------------------------------------------------------
// #3 — MO pattern analysis
// ---------------------------------------------------------------------------

export interface MOBucket {
  label_en: string; label_mr: string;
  criteria: string;
  matches: any[];
}
export interface MOPatternResult {
  fir_id: string; ps_code: string; lookback_months: number;
  fir: any;
  buckets: { same_sections: MOBucket; same_place: MOBucket; same_time_band: MOBucket; repeat_accused: MOBucket };
  ps_heatmap: { by_weekday: { dow: number; n: number }[]; by_hour: { hr: number; n: number }[] };
  summary: { total_matches: number };
}
export const getFirMOPattern = (firId: string, months = 24) =>
  httpFIR.get(`firs/${firId}/mo-pattern`, { searchParams: { months } }).json<MOPatternResult>();

// ---------------------------------------------------------------------------
// CDR / IPDR / Tower-dump analysis (migration 027)
// ---------------------------------------------------------------------------
export interface CDRDataset {
  id: string;
  kind: string;                       // CDR | IPDR | TOWER_DUMP | SDR | OTHER
  subscriber_msisdn: string | null;
  subscriber_imei: string | null;
  provider: string | null;
  cell_id: string | null;
  period_from: string | null;
  period_to: string | null;
  source_request_ref: string | null;
  file_name: string | null;
  file_object_key: string | null;
  file_sha256: string | null;
  row_count: number;
  status: string;                     // PARSED | PARTIAL | PARSE_ERROR | EMPTY
  parse_error: string | null;
  notes: string | null;
  uploaded_by: string | null;
  uploaded_at: string;
}
export interface CDRAnalysis {
  dataset_id: string;
  subscriber_msisdn: string | null;
  kind: string;
  row_count: number;
  top_contacts: { b_party: string; calls: number; total_sec: number; avg_sec: number; first_at: string | null; last_at: string | null }[];
  by_hour: Record<string, number>;
  by_dow: Record<string, number>;
  cell_towers: { cell_id: string; n: number; lat: number | null; lng: number | null; first_at: string | null; last_at: string | null }[];
  imei_list: { imei: string; n: number; first_at: string | null; last_at: string | null }[];
  by_direction: { direction: string; n: number }[];
  night_activity: { count: number; total: number; pct: number };
}
export interface CDRLinkGraph {
  fir_id: string;
  nodes: { id: string; is_subject: boolean; degree: number }[];
  edges: { source: string; target: string; calls: number; total_sec: number }[];
}
export interface CDRUploadResult {
  id: string; fir_id: string; kind: string;
  subscriber_msisdn: string | null; row_count: number;
  status: string; warnings: string[];
  period_from: string | null; period_to: string | null;
  file_sha256: string; file_object_key: string | null;
}

export const listCDRDatasets = (firId: string) =>
  httpFIR.get(`firs/${firId}/cdr/datasets`).json<CDRDataset[]>();

export function uploadCDRDataset(
  firId: string,
  file: File,
  meta: { kind?: string; subscriber_msisdn?: string; subscriber_imei?: string;
          provider?: string; cell_id?: string; source_request_ref?: string; notes?: string },
) {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(meta).forEach(([k, v]) => { if (v) fd.append(k, v); });
  return httpFIR.post(`firs/${firId}/cdr/datasets`, { body: fd, timeout: 60_000 }).json<CDRUploadResult>();
}

export const getCDRDataset    = (datasetId: string) =>
  httpFIR.get(`cdr/datasets/${datasetId}`).json<any>();
export const getCDRAnalysis   = (datasetId: string, top = 20) =>
  httpFIR.get(`cdr/datasets/${datasetId}/analysis`, { searchParams: { top } }).json<CDRAnalysis>();
export const listCDRRecords   = (datasetId: string, opts: { limit?: number; offset?: number; b_party?: string } = {}) =>
  httpFIR.get(`cdr/datasets/${datasetId}/records`, { searchParams: opts as any }).json<{ total: number; limit: number; offset: number; items: any[] }>();
export const getCDRLinkGraph  = (firId: string, minCalls = 1) =>
  httpFIR.get(`firs/${firId}/cdr/link-graph`, { searchParams: { min_calls: minCalls } }).json<CDRLinkGraph>();
export const deleteCDRDataset = (datasetId: string) =>
  httpFIR.delete(`cdr/datasets/${datasetId}`).then(() => true);
// Raw uploaded telecom file — served by the evidence service at /v1/uploads/{key}.
export const cdrRawFileUrl = (objectKey: string) => withAuthQS(`/v1/uploads/${objectKey}`);

/** Authenticated URL for any uploaded object (audio clip, image, document)
 *  served by the evidence service at GET /v1/uploads/{key}. Use this for
 *  `<audio src>`, `<img src>`, `<video src>` and `<a href target=_blank>` —
 *  those browser-initiated fetches don't carry the Authorization header, so the
 *  `?token=` query-string fallback (see backend/shared/auth.py) is required. */
export const uploadUrl = (objectKey: string) =>
  withAuthQS(`/v1/uploads/${encodeURIComponent(objectKey)}`);

// ---------------------------------------------------------------------------
// #35 — Court bundle (returns HTML; frontend opens in new tab via direct URL)
// ---------------------------------------------------------------------------
export const courtBundleUrl = (firId: string) => withAuthQS(`/v1/firs/${firId}/court-bundle`);
// The FIR rendered as the NCRB I.I.F.-I "First Information Report" form (bilingual
// HTML; opens in a new tab → browser Print / Save-as-PDF).
export const firPrintUrl = (firId: string) => withAuthQS(`/v1/firs/${firId}/print`);

// Charge-sheet documents (charge_sheet service): the BNSS S.193 police-report
// deed (HTML or PDF) and the full court-bundle ZIP.
export const chargeSheetPrintUrl = (csId: string, format: "html" | "pdf" = "html") =>
  withAuthQS(`/v1/charge-sheets/${csId}/print${format === "pdf" ? "?format=pdf" : ""}`);
export const chargeSheetBundleUrl = (csId: string) =>
  withAuthQS(`/v1/charge-sheets/${csId}/court-bundle`);

// ---------------------------------------------------------------------------
// #38 — Zero FIR transfer
// ---------------------------------------------------------------------------
export const zeroFirTransfer = (firId: string, target_ps_code: string, note?: string) =>
  httpFIR.post(`firs/${firId}/zero-fir-transfer`, { json: { target_ps_code, note } })
    .json<{ ok: boolean; transferred_to: string; transferred_at: string }>();

// ---------------------------------------------------------------------------
// #39 — NCR (non-cognisable report)
// ---------------------------------------------------------------------------
export const listNCR    = (status?: string) => httpFIR.get(`registers/ncr`, { searchParams: status ? { status } : {} }).json<any[]>();
export const createNCR  = (body: any) => httpFIR.post(`registers/ncr`, { json: body }).json<{ id: string; ncr_no: string }>();
export const updateNCRStatus = (id: string, status: string, note?: string, converted_fir_id?: string) =>
  httpFIR.patch(`registers/ncr/${id}/status`, { json: { status, note, converted_fir_id } }).json<{ ok: boolean }>();

// ---- spec 018 NCR-dynamic wrappers ----

export const referNcrToMagistrate = (id: string, body: {
  court_id: string; magistrate_name?: string; force?: boolean; force_reason?: string;
}) => httpFIR.post(`registers/ncr/${id}/refer-magistrate`, { json: body }).json<{
  ok: boolean; referral_id: string; ncr_no: string; letter_doc_key?: string; sms_dispatched?: boolean;
}>();

export const recordMagistrateOrder = (id: string, body: {
  order_doc_key: string; court_id: string; magistrate_name: string;
  order_date: string; order_text_excerpt: string;
}) => httpFIR.post(`registers/ncr/${id}/magistrate-order`, { json: body }).json<{
  ok: boolean; magistrate_order_at: string;
}>();

export const convertNcrToFir = (id: string) =>
  httpFIR.post(`registers/ncr/${id}/convert-to-fir`, { json: {} }).json<{
    ok: boolean; fir_id: string; fir_number?: string;
  }>();

export const closeNcr = (id: string, body: {
  kind: "COMPROMISED" | "CONVERTED_TO_FIR" | "UNACTIONABLE" | "WITHDRAWN_BY_INFORMANT" | "REFERRED_NO_RETURN";
  closure_note_en: string; closure_note_mr?: string; closure_evidence_doc_key?: string;
}) => httpFIR.post(`registers/ncr/${id}/closure`, { json: body }).json<{
  closure_report_id: string; status: "CLOSED";
}>();

export const addNcrParty = (id: string, body: {
  role: "ACCUSED" | "WITNESS" | "VICTIM" | "OTHER";
  name_en: string; name_mr?: string; contact?: string; notes?: string;
}) => httpFIR.post(`registers/ncr/${id}/parties`, { json: body }).json<{ party_id: string }>();

export const deleteNcrParty = (id: string, pid: string) =>
  httpFIR.delete(`registers/ncr/${id}/parties/${pid}`).json<{ ok: boolean }>();

export const transferNcr = (id: string, body: { target_ps_code: string; note?: string; force?: boolean }) =>
  httpFIR.post(`registers/ncr/${id}/transfer`, { json: body }).json<{ ok: boolean; target_ps_code: string }>();

export const acceptNcrTransfer = (id: string) =>
  httpFIR.post(`registers/ncr/${id}/transfer/accept`, { json: {} }).json<{ ok: boolean; ps_code: string }>();

export const listNcrTransferInbox = (params: { limit?: number; offset?: number } = {}) =>
  httpFIR.get("registers/ncr/inbox/transfer", { searchParams: params as Record<string, any> }).json<{ items: any[]; total: number }>();

export interface NCRTimelineEvent {
  at: string; kind: string; actor_officer_id?: string | null; payload?: Record<string, unknown>;
}
export const getNcrTimeline = (id: string) =>
  httpFIR.get(`registers/ncr/${id}/timeline`).json<NCRTimelineEvent[]>();

export const ncrReceiptUrl = (id: string) => {
  // Direct PDF link — uses the same VITE_FIR_BASE the other httpFIR wrappers use
  const base = (import.meta as any).env?.VITE_FIR_BASE || "/v1";
  return `${base}/registers/ncr/${id}/receipt`;
};

export interface NCRPartyRow {
  id: string; role: "ACCUSED" | "WITNESS" | "VICTIM" | "OTHER";
  name_en: string; name_mr?: string | null;
  contact?: string | null; notes?: string | null;
  created_at?: string | null;
}

export interface NCRClosureRow {
  id: string; kind: string;
  closure_note_en: string; closure_note_mr?: string | null;
  signed_at?: string | null;
}

export interface NCRDetail {
  id: string; ncr_no: string; ps_code: string;
  received_at: string;
  informant_name_en: string; informant_name_mr: string; informant_mobile?: string;
  summary_en: string; summary_mr: string;
  sections_invoked: string[];
  status: "REGISTERED" | "REFERRED_TO_MAGISTRATE" | "CONVERTED_TO_FIR" | "CLOSED";
  converted_fir_id?: string | null;
  referred_at?: string | null;
  referred_court_id?: string | null;
  magistrate_name?: string | null;
  magistrate_order_doc_key?: string | null;
  magistrate_order_at?: string | null;
  magistrate_order_text_excerpt?: string | null;
  is_zero_ncr?: boolean;
  target_ps_code?: string | null;
  receipt_doc_key?: string | null;
  parties?: NCRPartyRow[];
  closure?: NCRClosureRow | null;
}
export const getNcr = (id: string) =>
  httpFIR.get(`registers/ncr/${id}`).json<NCRDetail>();

// ---------------------------------------------------------------------------
// #40 — FIR closure report
// ---------------------------------------------------------------------------
export const getFirClosure = (firId: string) =>
  httpFIR.get(`firs/${firId}/closure`).json<any>();
export const createFirClosure = (firId: string, body: { classification: "A"|"B"|"C"|"N"; reason_en: string; reason_mr: string }) =>
  httpFIR.post(`firs/${firId}/closure`, { json: body }).json<{ id: string; classification: string }>();
export const approveFirClosure = (firId: string, cid: string, magistrate_filed = false) =>
  httpFIR.patch(`firs/${firId}/closure/${cid}/approve`, { json: { magistrate_filed } }).json<{ ok: boolean }>();

// ---------------------------------------------------------------------------
// #2 / #6 — AI suggestions + diary gap check
// ---------------------------------------------------------------------------

export interface IOSuggestion {
  priority: "URGENT"|"HIGH"|"MEDIUM"|"LOW";
  title_en: string; title_mr: string;
  rationale_en: string; rationale_mr: string;
  suggested_route: string;
}
export interface FIRSuggestionsResult {
  fir_id: string;
  snapshot: any;
  report: { suggestions: IOSuggestion[]; overall_health: "GOOD"|"FAIR"|"POOR" };
  provider?: string; model?: string;
}
export const aiFIRSuggestions = (fir_id: string) =>
  httpAI.post("ai/fir-suggestions", { json: { fir_id }, timeout: 240_000 })
    .json<FIRSuggestionsResult>();

export interface ComplaintSuggestionsResult {
  complaint_id: string;
  snapshot: any;
  report: { suggestions: IOSuggestion[]; overall_health: "GOOD"|"FAIR"|"POOR" };
  provider?: string; model?: string;
}
export const aiComplaintSuggestions = (complaint_id: string) =>
  httpAI.post("ai/complaint-suggestions", { json: { complaint_id }, timeout: 240_000 })
    .json<ComplaintSuggestionsResult>();

// Deep section analysis — per-ingredient coverage grading with verbatim
// quote evidence. Returns the literal prompt + LLM response for officer audit.
export interface SectionIngredientVerdict {
  code: string;
  text_en?: string; text_mr?: string;
  covered: boolean;
  evidence_quote?: string | null;
}
export interface SectionVerdict {
  code: string;
  title_en?: string; title_mr?: string;
  confidence: number;
  rationale_en?: string; rationale_mr?: string;
  cognisable?: boolean; bailable?: boolean;
  max_punishment_text?: string;
  ingredients: SectionIngredientVerdict[];
  missing_ingredients_count?: number;
}
export interface SectionAnalysisResult {
  primary: SectionVerdict | null;
  additional: SectionVerdict[];
  follow_up_questions: { en: string; mr: string }[];
  candidate_pool: string[];
  prompt: string;
  raw_response: string;
  interaction_id?: string;
  provider?: string; model?: string;
}
export const aiSectionAnalysis = (raw_text: string, candidate_sections: string[] = []) =>
  httpAI.post("ai/section-analysis", {
    json: { raw_text, candidate_sections, language: "mr" },
    timeout: 90_000,
  }).json<SectionAnalysisResult>();

export interface DiaryGap {
  kind: "missing_day"|"missing_action"|"contradiction"|"deadline_risk"|"other";
  description_en: string; description_mr: string;
  severity: "LOW"|"MEDIUM"|"HIGH";
}
export interface DiaryGapResult {
  fir_id: string; n_diary_entries: number;
  report: { gaps: DiaryGap[]; coverage_summary_en: string; coverage_summary_mr: string };
  provider?: string; model?: string;
}
export const aiDiaryGapCheck = (fir_id: string) =>
  httpAI.post("ai/diary-gap-check", { json: { fir_id }, timeout: 240_000 }).json<DiaryGapResult>();

// ---------------------------------------------------------------------------
// #34 — BSA cert PDF (HTML, opened in new tab)
// ---------------------------------------------------------------------------
export const bsaS63PdfUrl = (evidenceId: string) =>
  withAuthQS(`/v1/evidence/${evidenceId}/bsa-s63/pdf`);

// ---------------------------------------------------------------------------
// #7-#22 — Generic tool capture (T01-T40)
// ---------------------------------------------------------------------------

export interface ToolDef {
  id: string; name_en: string; name_mr: string;
  auto_creates_evidence: boolean; expects: string[];
}
export const listToolCatalog = () =>
  httpFIR.get(`tools/catalog`).json<{ tools: ToolDef[] }>();

export const toolCapture = (tool_id: string, body: { fir_id?: string; payload?: any; file_object_key?: string; file_sha256?: string }) =>
  httpFIR.post(`tools/${tool_id}/capture`, { json: { tool_id, ...body } })
    .json<{ id: string; tool_id: string; fir_id?: string; auto_created_evidence_id?: string }>();

export const listToolCaptures = (params: { fir_id?: string; tool_id?: string } = {}) =>
  httpFIR.get(`tools/captures`, { searchParams: params as any }).json<any[]>();

// ---------------------------------------------------------------------------
// #23-#33 — External integrations (CCTNS, NAFIS, VAHAN, etc.)
// ---------------------------------------------------------------------------

export interface IntegrationConfig {
  system_code: string; display_name: string; base_url: string | null;
  api_key_ref: string | null;
  mode: "mock"|"live"; enabled: boolean;
  last_sync_at: string | null; last_outcome: string | null; notes: string | null;
  /** direction ("PUSH"|"PULL"|"WEBHOOK_IN") -> "METHOD path-template" the connector exposes */
  operations?: Record<string, string>;
  /** HTTP header the connector puts the secret in (e.g. "Authorization", "x-api-key") */
  auth_header?: string;
  /** connector class name, or null if this system has no dedicated connector yet */
  connector?: string | null;
  /** relative path of this system's inbound-webhook receiver (prefix with the API origin) */
  webhook_url?: string;
}
export const listIntegrations = () =>
  httpFIR.get(`integrations`).json<IntegrationConfig[]>();

/** Admin: edit one external system's connection config (base URL / api-key ref
 *  / mock↔live toggle). The api-key ref is a secret-manager key name, never the
 *  secret itself. 403 unless the caller is an admin/supervisor. */
export const updateIntegrationConfig = (
  system: string,
  body: { base_url?: string | null; api_key_ref?: string | null; mode?: "mock"|"live"; enabled?: boolean; notes?: string | null },
) =>
  httpFIR.put(`integrations/${system}/config`, { json: body }).json<IntegrationConfig>();

export const integrationSync = (system: string, body: { direction?: "PULL"|"PUSH"|"WEBHOOK_IN"; fir_id?: string; payload?: any } = {}) =>
  httpFIR.post(`integrations/${system}/sync`, { json: { direction: body.direction ?? "PULL", ...body } })
    .json<{ id: string; system: string; outcome: string; latency_ms: number; response: any }>();

export const integrationLog = (system: string, limit = 50) =>
  httpFIR.get(`integrations/${system}/log`, { searchParams: { limit } }).json<any[]>();

// MahaLLM 72-field FIR — Section B (complainant) optional extras
export interface ComplainantDetailsInput {
  father_husband_name?: string;
  dob?: string;                  // ISO date YYYY-MM-DD
  age?: number;                  // auto-derived from dob if omitted
  gender?: "M" | "F" | "O";
  nationality?: string;
  caste_category?: "General" | "SC" | "ST" | "OBC" | "VJNT" | "SBC" | "Other";
  occupation?: string;
  addr_line1?: string;
  addr_line2?: string;
  village?: string;
  taluka?: string;
  district?: string;
  state?: string;
  pincode?: string;
  email?: string;
  photo_object_key?: string;
}

// MahaLLM 72-field FIR — Section C (incident) extras
export interface IncidentExtrasInput {
  occurrence_to_at?: string;     // ISO datetime
  gps_latitude?: number;
  gps_longitude?: number;
  direction_from_ps?: string;
  place_type?: "home"|"street"|"public_place"|"highway"|"market"|"school"|"workplace"|"religious_place"|"vehicle"|"online"|"government_office"|"other";
  beat_number?: string;
}

// MahaLLM 72-field FIR — Sections G + H + I (property/delay/IO action)
export interface PropertyDelayIOInput {
  stolen_value_inr?: number;
  stolen_description?: string;
  recovered_value_inr?: number;
  delay_hours?: number;
  delay_reason?: string;
  action_taken?: string;
}

// MahaLLM 72-field FIR — Section J (authentication-at-creation subset)
export interface AuthenticationJInput {
  read_over_complainant?: boolean;
  language_read_in?: "mr" | "hi" | "en" | "other";
  complainant_signature_object_key?: string;
}

// NCRB IIF-I Annexure to §7 — physical features (20-column form).
export interface PhysicalFeaturesInput {
  sex?: "M" | "F" | "O";
  build?: string;
  dob_or_year?: string;
  complexion?: string;
  height_cms?: number;
  identification_mark?: string;
  deformities?: string;
  teeth?: string;
  hair?: string;
  eye?: string;
  habits?: string;
  dress_habits?: string;
  language_dialect?: string;
  burn_mark?: string;
  leucoderma?: string;
  mole?: string;
  scar?: string;
  tattoo?: string;
  others?: string;
}

// MahaLLM 72-field FIR — Section F (accused) + NCRB IIF-I §7 extras.
export interface AccusedInput {
  is_known: boolean;
  name_en?: string;
  name_mr?: string;
  alias?: string;
  relative_name?: string;
  father_name?: string;
  age_approx?: number;
  gender?: "M" | "F" | "O";
  address?: string;
  permanent_address?: string;
  description?: string;
  previous_history?: any[];
  physical_features?: PhysicalFeaturesInput;
}

// NCRB IIF-I §§3(b), 3(c), 4
export interface IIFIRegistrationMeta {
  info_received_at?: string;
  gd_entry_no?: string;
  gd_entry_at?: string;
  info_type?: "written" | "oral";
}
// NCRB IIF-I §5(c)
export interface IIFIJurisdiction {
  outside_jurisdiction?: boolean;
  outside_ps_name?: string;
  outside_ps_district?: string;
  outside_ps_state?: string;
}
// NCRB IIF-I §6(i)
export interface ComplainantPermanentAddress {
  addr_line1?: string;
  addr_line2?: string;
  village?: string;
  taluka?: string;
  district?: string;
  state?: string;
  pincode?: string;
  same_as_present?: boolean;
}
// NCRB IIF-I §6(e–g)
export interface ComplainantIDInput {
  id_type: "UID" | "PASSPORT" | "VOTER" | "RATION" | "DL" | "PAN" | "OTHER";
  id_number: string;
  issue_date?: string;
  issue_place?: string;
  notes?: string;
}
// NCRB IIF-I §§11, 13(3), 13(4), 15
export interface IIFIClosure {
  inquest_ud_case_no?: string;
  refused_reason?: string;
  transferred_to_ps_code?: string;
  transferred_to_district?: string;
  transferred_at?: string;
  dispatched_to_court_at?: string;
}
// NCRB IIF-I §9
export interface PropertyRowInput {
  seq_no: number;
  category?: string;
  prop_type?: string;
  description?: string;
  value_inr?: number;
  recovered?: boolean;
  recovered_value_inr?: number;
}
// NCRB IIF-I Acts & Sections grid (generic — BNS + Arms Act + NDPS + IT, ...)
export interface ActSectionInput {
  seq_no: number;
  act_code: string;
  section_code: string;
  act_title_en?: string;
  act_title_mr?: string;
  rationale_en?: string;
  rationale_mr?: string;
}

export async function createFIR(input: {
  summary: Bilingual;
  occurrence_at: string;
  place_of_offence: Bilingual;
  complainant_id: string;
  sections: string[];
  ai_interaction_id?: string;
  complainant_details?: ComplainantDetailsInput;
  incident_extras?: IncidentExtrasInput;
  accused?: AccusedInput[];
  property_delay_io?: PropertyDelayIOInput;
  authentication?: AuthenticationJInput;
  // NCRB IIF-I alignment (migration 026)
  iifi_registration?: IIFIRegistrationMeta;
  iifi_jurisdiction?: IIFIJurisdiction;
  complainant_perm_address?: ComplainantPermanentAddress;
  complainant_ids?: ComplainantIDInput[];
  iifi_closure?: IIFIClosure;
  property_rows?: PropertyRowInput[];
  acts?: ActSectionInput[];
}) {
  return httpFIR.post("firs", { json: input }).json<{ id: string }>();
}

// Full overwrite of a DRAFT FIR's 72 fields — the /firs/:id/edit "complete &
// sign" page. Same payload shape as createFIR (complainant_id optional here).
export type FIRFullPayload = Omit<Parameters<typeof createFIR>[0], "complainant_id"> & {
  complainant_id?: string | null;
  sensitivity?: string;
  pocso_jj_pcma_flag?: boolean;
  // IIF-1 procedural fields (§3b GD ref, §3c info-received, §4 written/oral,
  // §5c outside-limit P.S., §11 inquest/UD no., §13 structured action, §15 court dispatch)
  procedural?: {
    gd_entry_no?: string;
    gd_entry_at?: string;
    info_received_at?: string;
    info_type?: "written" | "oral" | "";
    outside_jurisdiction?: boolean;
    outside_ps_name?: string;
    outside_ps_district?: string;
    outside_ps_state?: string;
    inquest_ud_case_no?: string;
    dispatched_to_court_at?: string;
    action_taken_type?: "REGISTERED_INVESTIGATING" | "DIRECTED_OFFICER" | "REFUSED" | "TRANSFERRED_JURISDICTION" | "";
    action_directed_officer?: string;
    copy_supplied_free?: boolean;
  };
};
export const replaceFIR = (id: string, body: FIRFullPayload) =>
  httpFIR.put(`firs/${id}`, { json: body }).json<FIR>();

// Add an accused after FIR creation (during investigation)
export const addAccused = (firId: string, body: AccusedInput) =>
  httpFIR.post(`firs/${firId}/accused`, { json: body }).json<{ id: string }>();
export const listAccused = (firId: string) =>
  httpFIR.get(`firs/${firId}/accused`).json<any[]>();
export const listFIRProperty = (firId: string) =>
  httpFIR.get(`firs/${firId}/property`).json<any[]>();
export const listFIRActs = (firId: string) =>
  httpFIR.get(`firs/${firId}/acts`).json<any[]>();

// MahaLLM 72-field FIR — Section J operational endpoints
export const patchFirAuthentication = (firId: string, body: AuthenticationJInput) =>
  httpFIR.patch(`firs/${firId}/authentication`, { json: body }).json<{ ok: boolean }>();
export const signFirIO = (firId: string) =>
  httpFIR.post(`firs/${firId}/sign-io`).json<{ ok: boolean; dsc_serial: string; signed_at: string }>();
export const signFirSHO = (firId: string) =>
  httpFIR.post(`firs/${firId}/sign-sho`).json<{ ok: boolean; dsc_serial: string; signed_at: string }>();
export const cctnsSyncFir = (firId: string) =>
  httpFIR.post(`firs/${firId}/cctns-sync`).json<{ ok: boolean; cctns_reference: string; status: string; synced_at: string }>();
/** Phase K signal — count of mhpol.ai_interaction rows for this FIR (powers
 *  the workflow stepper's "AI analysis" phase + the CasePipelineBar). */
export const getFirAiActivity = (firId: string) =>
  httpFIR.get(`firs/${firId}/ai-activity`).json<{ count: number; last_at: string | null }>();

/** End-to-end "case chain" — slim FIR header + counts for every phase + the
 *  actual charge-sheet and court-filing rows. Single round-trip; powers the
 *  CasePipelineBar and chain chips on the Complaint detail page. */
export interface CaseChain {
  fir: {
    id: string; fir_number: string | null; status: string; ps_code: string;
    copy_supplied_free: boolean | null; registered_at: string | null;
    cctns_reference: string | null; cctns_sync_status: string | null;
  };
  counts: {
    statements: number; evidence: number; panchanamas: number; arrests: number;
    mlc: number; fsl: number; diary: number; ai_runs: number;
  };
  charge_sheets: { id: string; cs_number: string | null; status: string;
    is_supplementary: boolean; parent_cs_id: string | null;
    filed_at: string | null; created_at: string | null }[];
  court_filings: { id: string; charge_sheet_id: string; cs_number: string | null;
    court_name: string | null; status: string;
    next_hearing_at: string | null; filed_at: string | null }[];
}
export const getCaseChain = (firId: string) =>
  httpFIR.get(`cases/${firId}/chain`).json<CaseChain>();
export const generateFirQR = (firId: string) =>
  httpFIR.post(`firs/${firId}/qr`).json<{ payload: string; svg: string }>();
export const getFirAuthentication = (firId: string) =>
  httpFIR.get(`firs/${firId}/authentication`).json<any>();

// ---------------------------------------------------------------------------
// FIR list / detail / sign
// ---------------------------------------------------------------------------

export interface FIRSummary {
  id: string;
  fir_number: string | null;
  case_no?: string | null;          // Case ID — minted at registration (CR-…)
  status: string;
  ps_code: string;
  summary_en: string;
  summary_mr: string;
  sensitivity: string;
  registered_at: string | null;
}

export interface FIR extends FIRSummary {
  pocso_jj_pcma_flag: boolean;
  canonical_sha256: string;
  signed_at: string | null;
  year?: number;
  io_officer_id?: string | null;
  sho_officer_id?: string | null;
  io_signed_at?: string | null;
  sho_signed_at?: string | null;
  sections?: string[];
  // Set when this FIR was auto-created from a citizen complaint. Surfaced as
  // a banner on FIRDetailPage so the IO can jump back to the originating record.
  originating_complaint?: {
    id: string;
    tracking_code: string;
    received_at: string | null;
    status: string;
  } | null;
  // editable scalar columns (the "FIR fields" panel) — all optional/nullable
  complainant_id?: string | null;
  complainant_name_en?: string | null;
  complainant_name_mr?: string | null;
  complainant_mobile?: string | null;
  complainant_email?: string | null;
  complainant_father_husband_name?: string | null;
  complainant_dob?: string | null;
  complainant_age?: number | null;
  complainant_gender?: string | null;
  complainant_nationality?: string | null;
  complainant_caste_category?: string | null;
  complainant_occupation?: string | null;
  complainant_addr_line1?: string | null;
  complainant_addr_line2?: string | null;
  complainant_village?: string | null;
  complainant_taluka?: string | null;
  complainant_district?: string | null;
  complainant_state?: string | null;
  complainant_pincode?: string | null;
  complainant_id_type?: string | null;
  complainant_id_number?: string | null;
  id_verification_method?: string | null;
  id_verified?: boolean | null;
  id_verified_at?: string | null;
  id_verification_note?: string | null;
  complainant_perm_addr_line1?: string | null;
  complainant_perm_addr_line2?: string | null;
  complainant_perm_village?: string | null;
  complainant_perm_taluka?: string | null;
  complainant_perm_district?: string | null;
  complainant_perm_state?: string | null;
  complainant_perm_pincode?: string | null;
  complainant_same_as_present?: boolean | null;
  occurrence_at?: string | null;
  occurrence_to_at?: string | null;
  place_of_offence_en?: string | null;
  place_of_offence_mr?: string | null;
  gps_latitude?: number | null;
  gps_longitude?: number | null;
  direction_from_ps?: string | null;
  place_type?: string | null;
  beat_number?: string | null;
  stolen_value_inr?: number | null;
  stolen_description?: string | null;
  recovered_value_inr?: number | null;
  delay_hours?: number | null;
  delay_reason?: string | null;
  action_taken?: string | null;
  read_over_complainant?: boolean | null;
  language_read_in?: string | null;
  complainant_signature_object_key?: string | null;
  // IIF-1 procedural fields (§3b/3c/4/5c/11/13/15)
  gd_entry_no?: string | null;
  gd_entry_at?: string | null;
  info_received_at?: string | null;
  info_type?: string | null;            // "written" | "oral"
  outside_jurisdiction?: boolean | null;
  outside_ps_name?: string | null;
  outside_ps_district?: string | null;
  outside_ps_state?: string | null;
  inquest_ud_case_no?: string | null;
  dispatched_to_court_at?: string | null;
  action_taken_type?: string | null;    // REGISTERED_INVESTIGATING | DIRECTED_OFFICER | REFUSED | TRANSFERRED_JURISDICTION
  action_directed_officer?: string | null;
  copy_supplied_free?: boolean | null;
  is_zero_fir?: boolean | null;
  zero_fir_target_ps?: string | null;
  zero_fir_transferred_at?: string | null;
  zero_fir_transfer_note?: string | null;
  cctns_reference?: string | null;
  cctns_sync_status?: string | null;
  cctns_synced_at?: string | null;
  qr_payload?: string | null;
  version_no?: string | null;
}

/** Patch the FIR's scalar columns — the "Edit FIR fields" panel. Send only
 *  changed fields; rejected (409) once the FIR is signed/charge-sheeted/closed. */
export type FIRPatch = Partial<Pick<FIR,
  | "summary_en" | "summary_mr" | "sensitivity" | "pocso_jj_pcma_flag"
  | "complainant_name_en" | "complainant_name_mr" | "complainant_mobile" | "complainant_email"
  | "complainant_father_husband_name" | "complainant_dob" | "complainant_age" | "complainant_gender"
  | "complainant_nationality" | "complainant_caste_category" | "complainant_occupation"
  | "complainant_addr_line1" | "complainant_addr_line2" | "complainant_village" | "complainant_taluka"
  | "complainant_district" | "complainant_state" | "complainant_pincode"
  | "complainant_id_type" | "complainant_id_number" | "id_verification_method" | "id_verification_note"
  | "complainant_perm_addr_line1" | "complainant_perm_addr_line2" | "complainant_perm_village"
  | "complainant_perm_taluka" | "complainant_perm_district" | "complainant_perm_state"
  | "complainant_perm_pincode" | "complainant_same_as_present"
  | "occurrence_at" | "occurrence_to_at" | "place_of_offence_en" | "place_of_offence_mr"
  | "gps_latitude" | "gps_longitude" | "direction_from_ps" | "place_type" | "beat_number"
  | "stolen_value_inr" | "stolen_description" | "recovered_value_inr" | "delay_reason" | "action_taken"
  | "is_zero_fir" | "zero_fir_target_ps" | "zero_fir_transfer_note"
>>;

export const patchFIR = (id: string, body: FIRPatch) =>
  httpFIR.patch(`firs/${id}`, { json: body }).json<{ ok: boolean; fields_changed: string[] }>();

// Document files / OCR extracts / audio clips inherited from the complaint at
// conversion time (plus any added later). Mirrors the complaint-side shapes.
export interface FIRAttachment {
  id: string; object_key: string; label: string | null;
  sha256: string | null; content_type: string | null;
  source: "complaint" | "fir"; copied_from_complaint_id: string | null; created_at: string;
}
export interface FIROcrExtract {
  id: string; object_key: string; sha256: string; size_bytes: number;
  content_type: string | null; engine: string; model: string | null;
  src_lang: string | null; raw_text: string; text_mr: string | null;
  page_count: number | null; label: string | null;
  copied_from_complaint_id: string | null; extracted_at: string;
}
export interface FIRAudioClip {
  id: string; object_key: string; sha256: string; size_bytes: number;
  src_lang: string | null; transcript: string | null; transcript_mr: string | null;
  label: string | null; duration_sec: number | null;
  copied_from_complaint_id: string | null; recorded_at: string;
}
export const listFIRAttachments = (firId: string) =>
  httpFIR.get(`firs/${firId}/attachments`).json<FIRAttachment[]>();
export const listFIROcrExtracts = (firId: string) =>
  httpFIR.get(`firs/${firId}/ocr-extracts`).json<FIROcrExtract[]>();
export const listFIRAudioClips = (firId: string) =>
  httpFIR.get(`firs/${firId}/audio-clips`).json<FIRAudioClip[]>();

export async function listFIRs(params: { limit?: number; offset?: number; status?: string | string[] } = {}) {
  const sp: Record<string, string | number> = {
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  };
  if (params.status) {
    sp.status = Array.isArray(params.status) ? params.status.join(",") : params.status;
  }
  return httpFIR
    .get("firs", { searchParams: sp })
    .json<{ total: number; limit: number; offset: number; items: FIRSummary[] }>();
}

export async function getFIR(id: string) {
  return httpFIR.get(`firs/${id}`).json<FIR>();
}

// ── Case File (feature 004) — thin aggregator over the FIR aggregate.
export interface CaseFile {
  case_no: string;
  fir_id: string;
  fir_number: string | null;
  fir_detail_url: string;
  ps_code: string; ps_name: string | null; ps_name_mr?: string | null;
  year: number;
  status: string; stage: string; stage_detail?: string; stage_detail_label?: string;
  mid_transfer: boolean;
  registered_at: string | null;
  occurrence_at: string | null; occurrence_to_at: string | null;
  summary_en: string; summary_mr: string;
  sections: string[];
  sensitivity: string;
  place_of_offence_en: string | null; place_of_offence_mr: string | null;
  complainant: { id: string | null; name_en: string | null; name_mr: string | null };
  accused?: { name_en: string | null; name_mr: string | null; is_known: boolean | null; age_approx: number | null; alias: string | null }[];
  other_acts?: { act_code: string; act_title_en: string | null; act_title_mr: string | null; section_code: string }[];
  io_officer: { id: string; badge_no: string; name_en: string; name_mr: string; rank: string } | null;
  sho_officer: { id: string; badge_no: string; name_en: string; rank: string } | null;
  io_history?: { assigned_at: string | null; reason: string | null; io_officer: { id: string; badge_no: string; name_en: string; rank: string }; assigned_by: { name_en: string; badge_no: string } }[];
  complaint: { id: string; tracking_code: string; received_at: string | null; summary_en: string; summary_mr: string; status: string; complainant_name_en: string | null; complainant_mobile: string | null } | null;
  charge_sheets: { id: string; cs_number: string | null; status: string; is_supplementary: boolean; parent_cs_id: string | null; filed_at: string | null; created_at: string | null }[];
  counts: { statements: number; evidence: number; arrests: number; panchanamas: number; mlc: number; fsl: number; court_filings: number; accused: number; witnesses: number; charge_sheets: number };
  bnss?: { nearest_unsatisfied: { code: string; label: string; due_at: string | null; days_left: number | null; status: string } | null; any_breached: boolean };
  readiness?: { checklist_total: number; checklist_done: number; checklist_na: number; checklist_pct: number | null; section_ingredients_total: number; section_ingredients_proven: number; section_strength_pct: number | null; blockers: string[] };
  parts?: CasePart[];
  timeline_url?: string;
}
export interface CasePart {
  key: string; label_en: string; label_mr: string;
  count: number;
  status: "none" | "in_progress" | "complete" | "attention";
  status_label: string;
  detail_url: string;
  deadline?: { code: string; label: string; due_at: string | null; days_left: number | null; status: string } | null;
  [k: string]: unknown;   // per-part extras (signed, by_status, classification, …)
}
export const getCase = (caseId: string) => httpFIR.get(`cases/${encodeURIComponent(caseId)}`).json<CaseFile>();

export interface IoAssignment {
  assigned_at: string | null;
  reason: string | null;
  io_officer: { id: string; badge_no: string; name_en: string; rank: string };
  prev_io_officer?: { id: string; badge_no: string; name_en: string } | null;
  assigned_by: { id?: string; badge_no: string; name_en: string; rank?: string };
}
export const getCaseIoHistory = (caseId: string) =>
  httpFIR.get(`cases/${encodeURIComponent(caseId)}/io-history`).json<{ case_no: string; fir_id: string; history: IoAssignment[] }>();
export const reassignIO = (caseId: string, body: { officer_id: string; reason: string }) =>
  httpFIR.patch(`cases/${encodeURIComponent(caseId)}/io`, { json: body }).json<{ case_no: string; fir_id: string; io_officer: { id: string; badge_no: string; name_en: string; name_mr: string; rank: string }; audit_seq: number | null }>();
export const listPsOfficers = () =>
  httpFIR.get(`cases/officers`).json<{ id: string; badge_no: string; name_en: string; name_mr: string; rank: string }[]>();

export interface BnssClock {
  code: string; label: string; statute_ref: string; applies: boolean;
  runs_from_event?: string | null; runs_from_at?: string | null;
  limit_days?: number | null; limit_hours?: number | null;
  due_at?: string | null; days_left?: number | null; overrun_days?: number | null;
  status: "pending" | "on_track" | "warning" | "breached" | "satisfied" | "not_applicable";
  note?: string | null;
}
export const getCaseBnssClocks = (caseId: string) =>
  httpFIR.get(`cases/${encodeURIComponent(caseId)}/bnss-clocks`).json<{ case_no: string; fir_id: string; clocks: BnssClock[]; nearest_unsatisfied: { code: string; label: string; due_at: string | null; days_left: number | null; status: string } | null; any_breached: boolean }>();

// ── Case dashboard (feature 004, US5)
export interface CaseRow {
  case_no: string; fir_id: string; fir_number: string | null;
  title: string; stage: string; stage_detail?: string; stage_detail_label?: string; status: string; ps_code: string;
  registered_at: string | null; sensitivity: string | null;
  io_officer: { id: string; badge_no: string; name_en: string; rank: string } | null;
  next_clock: { code: string; label: string; due_at: string | null; days_left: number | null; status: string } | null;
  readiness_pct: number | null; section_strength_pct: number | null;
  alerts: { kind: string; severity: "high" | "warn" | "info"; message: string }[];
}
export const listMyCases = (sort: "deadline" | "stage" | "case_no" = "deadline") =>
  httpFIR.get(`cases`, { searchParams: { view: "mine", sort } }).json<{ mode: string; officer_id: string; count: number; cases: CaseRow[] }>();
export const listStationCaseLoad = (sort: "deadline" | "stage" | "case_no" = "deadline") =>
  httpFIR.get(`cases`, { searchParams: { view: "station", sort } }).json<{ mode: string; ps_code: string; count: number; groups: { io_officer: { id: string; badge_no: string; name_en: string; rank: string } | null; open: number; overdue: number; at_risk: number; cases: CaseRow[] }[] }>();

// Unified case timeline — merges complaint receipt, preliminary-inquiry events,
// GD entries, FIR registration/signing, statements/evidence/panchanamas/arrests,
// MLC/FSL, investigation events, case diary, charge-sheet, closure. View-layer
// aggregation only (the GD / case-diary / audit tables stay separate).
export interface TimelineEvent { at: string; kind: string; label: string; source: string; ref?: string; }
export const getFIRTimeline = (firId: string) =>
  httpFIR.get(`firs/${firId}/timeline`).json<{ fir_id: string; complaint_id: string | null; events: TimelineEvent[]; n_events: number }>();
export const getComplaintTimeline = (cid: string) =>
  httpFIR.get(`complaints/${cid}/timeline`).json<{ complaint_id: string; converted_fir_id: string | null; events: TimelineEvent[]; n_events: number }>();

export async function signFIR(id: string) {
  return httpFIR
    .post(`firs/${id}/sign`)
    .json<{ fir_number: string; registered_at: string; audit_seq: number | null }>();
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export interface TemplateSummary {
  code: string;
  category: string;
  title: string;
  field_count: number;
}

export interface Template extends TemplateSummary {
  fields: string[];
  signers: string[];
  has_audit: boolean;
  file: string;
}

export async function listTemplates(params: { category?: string; q?: string; limit?: number } = {}) {
  const sp: Record<string, string> = { limit: String(params.limit ?? 50) };
  if (params.category) sp.category = params.category;
  if (params.q) sp.q = params.q;
  return httpFIR.get("templates", { searchParams: sp })
    .json<{ total: number; limit: number; offset: number; items: TemplateSummary[] }>();
}

export async function listTemplateCategories() {
  return httpFIR.get("templates/categories")
    .json<{ by_category: Record<string, number>; categories: string[] }>();
}

export async function getTemplate(code: string) {
  return httpFIR.get(`templates/${code}`).json<Template>();
}

// ---------------------------------------------------------------------------
// Audit verify
// ---------------------------------------------------------------------------

export async function verifyAuditChain(psCode: string) {
  return httpFIR.get(`audit/verify/${psCode}`)
    .json<{ ok: boolean; error: string | null; last_seq: number; head_hash: string }>();
}

// ---------------------------------------------------------------------------
// Statements
// ---------------------------------------------------------------------------

export interface Statement {
  id: string; fir_id: string; kind: string;
  deponent_name_en: string; deponent_name_mr: string;
  recorded_at: string; transcript_en: string; transcript_mr: string;
  canonical_sha256: string; signed_at: string | null;
  // S.183 / S.164 magistrate marking (may be absent on a pre-migration backend)
  is_s164?: boolean;
  s164_court?: string | null;
  s164_recorded_at?: string | null;
  s164_marked_by?: string | null;
  s164_marked_at?: string | null;
  // Audio archive + STT provenance
  audio_file_id?: string | null;
  audio_object_key?: string | null;
  audio_sha256?: string | null;
  audio_mime?: string | null;
  audio_duration_sec?: number | null;
  has_audio?: boolean;
  stt_engine?: string | null;
  stt_lang?: string | null;
  transcript_source?: string | null;
  // Derived legal hint
  inadmissible?: boolean;
  inadmissibility_note?: string | null;
}

export const listStatements = (firId: string) =>
  httpFIR.get(`firs/${firId}/statements`).json<Statement[]>();
export const createStatement = (firId: string, body: any) =>
  httpFIR.post(`firs/${firId}/statements`, { json: body }).json<Statement>();
export const getStatement = (id: string) =>
  httpFIR.get(`statements/${id}`).json<Statement>();
export const signStatement = (id: string) =>
  httpFIR.post(`statements/${id}/sign`).json<{ signed_at: string }>();
export const markStatementS164 = (
  id: string, body: { is_s164: boolean; s164_court?: string; s164_recorded_at?: string },
) => httpFIR.patch(`statements/${id}/mark-s164`, { json: body }).json<Statement>();
// Direct link to the archived statement audio (token in the query string so an
// <audio src> element can authenticate — the browser strips the Authorization
// header on media subresource loads).
export const statementAudioUrl = (id: string) => withAuthQS(`/v1/statements/${id}/audio`);

// 025 Phase 2 — per-statement extracted facts.
export type FactKind =
  | "TIME"|"PLACE"|"PERSON"|"WEAPON"|"VEHICLE"
  | "AMOUNT"|"IDENTIFICATION"|"SEQUENCE_STEP"|"OTHER";

export interface StatementFact {
  id: string;
  kind: FactKind;
  key: string | null;
  value_en: string;
  value_mr: string;
  source_lang: string | null;
  extracted_at: string | null;
}

export const listStatementFacts = (sid: string) =>
  httpAI.get(`statements/${sid}/facts`).json<{ statement_id: string; count: number; facts: StatementFact[] }>();

export const extractStatementFacts = (sid: string) =>
  httpAI.post(`ai/statement/${sid}/extract-facts`).json<{ statement_id: string; count: number; facts: StatementFact[] }>();

// 025 Phase 3 — operational endpoints.
export interface WitnessReliabilityRow {
  sid: string | null;
  deponent_name_en: string;
  deponent_name_mr: string;
  s_kind: string;
  total: number; open_count: number; high_open: number; resolved: number;
}
export const getWitnessReliability = (firId: string) =>
  httpAI.get(`firs/${firId}/witness-reliability`)
    .json<{ fir_id: string; count: number; witnesses: WitnessReliabilityRow[] }>();

export interface StaleContradictionRow {
  id: string; fir_id: string; fir_number: string | null; ps_code: string;
  kind: string; severity: "LOW"|"MEDIUM"|"HIGH";
  description_en: string; description_mr: string | null;
  status: ContradictionStatus;
  created_at: string | null; created_by: string | null;
}
export const getStaleContradictions = (opts?: { days?: number; scope?: "mine"|"ps"|"all" }) => {
  const sp: Record<string, string> = {};
  if (opts?.days) sp.days = String(opts.days);
  if (opts?.scope) sp.scope = opts.scope;
  return httpAI.get("dashboard/contradictions-stale", { searchParams: sp })
    .json<{ days: number; scope: string; count: number; rows: StaleContradictionRow[] }>();
};

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

export interface Evidence {
  id: string; fir_id: string; kind: string; qr_code: string;
  description_en: string; description_mr: string;
  seized_at: string; current_holder_officer_id: string;
  storage_location: string | null;
  seal_no: string | null;
  transfers?: { id: string; from_officer_id: string; to_officer_id: string;
                transferred_at: string; purpose: string; canonical_sha256: string }[];
}

export const listEvidence = (firId: string) =>
  httpFIR.get(`firs/${firId}/evidence`).json<Evidence[]>();
export const createEvidence = (firId: string, body: any) =>
  httpFIR.post(`firs/${firId}/evidence`, { json: body }).json<Evidence>();
export const getEvidence = (id: string) =>
  httpFIR.get(`evidence/${id}`).json<Evidence>();
export const transferEvidence = (id: string, body: { to_officer_id: string; purpose: string }) =>
  httpFIR.post(`evidence/${id}/transfer`, { json: body }).json<{ ok: boolean; transfer_hash: string }>();
export const setEvidenceSeal = (id: string, seal_no: string) =>
  httpFIR.patch(`evidence/${id}/seal`, { json: { seal_no } }).json<Evidence>();

// ---------------------------------------------------------------------------
// Panchanamas
// ---------------------------------------------------------------------------

export interface Panchanama {
  id: string;
  fir_id: string | null;          // null when the parent is a complaint (pre-FIR inquiry stage)
  complaint_id?: string | null;
  kind: string;
  conducted_at: string; place_en: string; place_mr: string;
  body_en?: string; body_mr?: string;
  canonical_sha256: string; signed_at: string | null;
  is_draft?: boolean;
  // Per-type structured intake (mirrors the catalog's fields_template) + evidence refs.
  fields_json?: Record<string, unknown> | null;
  attachment_object_keys?: string[] | null;
  lat?: number | null;
  lng?: number | null;
  // Panch witnesses (resolved from citizen rows by GET /v1/panchanamas/{id})
  panch1_citizen_id?: string | null;
  panch1_name_en?: string | null; panch1_name_mr?: string | null; panch1_mobile?: string | null;
  panch2_citizen_id?: string | null;
  panch2_name_en?: string | null; panch2_name_mr?: string | null; panch2_mobile?: string | null;
}

export interface PanchanamaListRow extends Panchanama {
  fir_number: string | null;
  fir_status: string | null;
  attachment_count?: number;
}

export const listAllPanchanamas = (params: {
  kind?: string; signed?: boolean; overdue?: boolean; q?: string;
} = {}) => {
  const sp: Record<string, string> = {};
  if (params.kind)                  sp.kind    = params.kind;
  if (params.signed !== undefined)  sp.signed  = String(params.signed);
  if (params.overdue)               sp.overdue = "true";
  if (params.q && params.q.trim())  sp.q       = params.q.trim();
  return httpFIR.get(`panchanamas`, { searchParams: sp }).json<PanchanamaListRow[]>();
};

// KPI tile counts for the queue page (auto-refreshed every 30 s).
export interface PanchanamaKPIs {
  total: number; today: number; signed: number;
  unsigned: number; overdue: number;
}
export const panchanamaKPIs = () =>
  httpFIR.get(`panchanamas/kpis`).json<PanchanamaKPIs>();

// {kind: count} histogram for the type chip-strip.
export const panchanamaCountByKind = () =>
  httpFIR.get(`panchanamas/by-kind`).json<Record<string, number>>();

// Printable bilingual deed — server returns A4 HTML the browser saves as PDF.
// Same pattern as `rajiNamaPrintUrl`: query-param token for plain-link auth.
export const panchanamaPrintUrl = (pid: string, format: "html" | "pdf" = "html") => {
  const tok = bareToken();
  const t = tok ? `&token=${encodeURIComponent(tok)}` : "";
  return `/v1/panchanamas/${pid}/print?${(format === "pdf" ? "format=pdf" : "format=html")}${t}`;
};

// PATCH partial edits to a not-yet-signed panchanama. 409 if signed.
// Setting is_draft transitions the row: true→false promotes a draft (server
// re-runs SOP validation); false→true demotes it back to a draft.
export interface PanchanamaPatch {
  place?: { en: string; mr: string };
  body?:  { en: string; mr: string };
  fields_json?: any;
  attachment_object_keys?: string[];
  lat?: number; lng?: number;
  is_draft?: boolean;
}
export const updatePanchanama = (pid: string, body: PanchanamaPatch) =>
  httpFIR.patch(`panchanamas/${pid}`, { json: body, timeout: 15_000 })
    .json<{ ok: boolean; canonical_sha256: string }>();

// Recompute canonical SHA-256 from the live row + compare to stored.
export interface PanchanamaVerifyResult {
  matches: boolean;
  stored: string; actual: string;
  signed_at: string | null;
  canonical_bytes: number;
}
export const verifyPanchanamaHash = (pid: string) =>
  httpFIR.post(`panchanamas/${pid}/verify-hash`, { timeout: 10_000 })
    .json<PanchanamaVerifyResult>();

// AI-assisted draft from photos / video frames + audio transcript.
// Pure inference; the IO reviews/edits the returned text and saves through
// the normal create flow.
export interface PanchanamaDraftIn {
  kind: string;
  panchanama_type_code: string;
  fir_id?: string;
  image_object_keys: string[];
  video_object_key?: string;
  audio_transcript?: string;
  audio_lang?: "mr" | "en";
  place_hint?: { en: string; mr: string };
  extra_notes?: string;
}
export interface PanchanamaDraftOut {
  body_en: string;
  body_mr: string;
  fields_json: Record<string, string>;
  timeline?: { from: string; to: string; observed: string }[];
  subject_description?: {
    label: string; height_estimate?: string; build?: string; clothing?: string;
    distinguishing_features?: string; items_carried?: string; caveat?: string;
  }[];
  investigative_assessment?: {
    hypothesis: string; basis?: string; confidence?: "low" | "medium" | "high"; caveat?: string;
  }[];
  confidence: "low" | "medium" | "high";
  warnings: string[];
  model: string;
  provider?: string;
  latency_ms: number;
  image_count: number;
  video_frame_count: number;
  chunked?: boolean;
  segments_analyzed?: number;
  segment_errors?: number;
  frames_examined?: number;
  text_only_fallback?: boolean;
}
export type PanchanamaVideoTimeline = NonNullable<PanchanamaDraftOut["timeline"]>;
export type PanchanamaSubjectDescription = NonNullable<PanchanamaDraftOut["subject_description"]>;
export type PanchanamaInvestigativeAssessment = NonNullable<PanchanamaDraftOut["investigative_assessment"]>;
export const aiDraftPanchanama = (b: PanchanamaDraftIn) =>
  httpAI.post("ai/panchanama-draft", { json: b, timeout: 600_000 })
    .json<PanchanamaDraftOut>();

// Forensic CCTV analysis — same media pipeline, but a human-readable text
// report (no JSON, nothing auto-filled).
export interface CctvAnalysisIn {
  fir_id?: string;
  image_object_keys: string[];
  video_object_key?: string;
  audio_transcript?: string;
  audio_lang?: "mr" | "en";
  camera_id?: string;
  location_context?: { en?: string; mr?: string; text?: string };
  footage_source?: string;
  extra_notes?: string;
  questions?: string;
}
export interface CctvAnalysisOut {
  analysis_text: string;
  model: string;
  provider?: string;
  latency_ms: number;
  image_count: number;
  video_frame_count: number;
  chunked?: boolean;
  segments_analyzed?: number;
  segment_errors?: number;
  frames_examined?: number;
  text_only_fallback?: boolean;
}
export const aiCctvAnalysis = (b: CctvAnalysisIn) =>
  httpAI.post("ai/cctv-analysis", { json: b, timeout: 600_000 })
    .json<CctvAnalysisOut>();

// --- AI evidence review (images/videos in the Evidence workspace) -----------
export interface EvidenceIngredientLink { section: string; ingredient: string; how: Bilingual; }
export interface EvidenceItemReview {
  object_key: string;
  kind: "IMAGE" | "VIDEO_FRAME_SET";
  label?: string | null;
  frame_count?: number | null;
  observation: Bilingual;
  relevance: Bilingual;
  corroborates: Bilingual;
  contradicts: Bilingual;
  evidentiary_value: Bilingual;
  quality_issues: string[];
  chain_of_custody_notes: string[];
  confidence: "low" | "medium" | "high";
}
export interface EvidenceReviewResult {
  overview: Bilingual;
  items: EvidenceItemReview[];
  supports_ingredients: EvidenceIngredientLink[];
  evidence_gaps: Bilingual[];
  next_steps: Bilingual[];
  statutory_reminders: Bilingual[];
  confidence: "low" | "medium" | "high";
  warnings: string[];
  model?: string | null;
  provider?: string | null;
  latency_ms: number;
  image_count: number;
  video_frame_count: number;
  text_only_fallback?: boolean;
  interaction_id?: string;
}
export const aiEvidenceReview = (body: {
  complaint_id?: string; fir_id?: string;
  image_object_keys?: string[]; video_object_keys?: string[];
  sections_override?: string[]; extra_notes?: string;
}) => httpAI.post("ai/evidence-review", { json: body, timeout: 300_000 })
        .json<EvidenceReviewResult>();

export interface PanchanamaRecommendation {
  code: string;
  kind: string;
  name_en: string;
  name_mr: string;
  priority: number;
  why_en: string;
  why_mr: string;
  already_done: boolean;
}
export interface PanchanamaRecommendOut {
  fir_id: string;
  fir_number: string | null;
  sections: string[];
  already_done_kinds: string[];
  recommendations: PanchanamaRecommendation[];
  model: string | null;
  provider: string | null;
  rationale_source: "llm" | "canned";
}
export const aiRecommendPanchanamas = (firId: string) =>
  httpAI.post("ai/panchanama-recommend", { json: { fir_id: firId }, timeout: 60_000 })
    .json<PanchanamaRecommendOut>();

export interface SopVerdict {
  step_no: number;
  verdict: "required_for_case" | "applicable" | "skip";
  why_en: string;
  why_mr: string;
}
export interface SopAddedStep {
  title_en: string; title_mr: string;
  detail_en: string; detail_mr: string;
  required: boolean;
}
export interface PanchanamaSopTailorOut {
  fir_id: string;
  panchanama_type_code: string;
  verdicts: SopVerdict[];
  added_steps: SopAddedStep[];
  rationale_source: "llm" | "static-fallback" | "no-sop";
  model: string | null;
  provider: string | null;
}
export const aiTailorPanchanamaSop = (firId: string, code: string) =>
  httpAI.post("ai/panchanama-sop-tailor", {
    json: { fir_id: firId, panchanama_type_code: code },
    timeout: 90_000,
  }).json<PanchanamaSopTailorOut>();

export const listPanchanamas = (firId: string) =>
  httpFIR.get(`firs/${firId}/panchanamas`).json<Panchanama[]>();
export const createPanchanama = (firId: string, body: any) =>
  httpFIR.post(`firs/${firId}/panchanamas`, { json: body }).json<Panchanama>();
export const getPanchanama = (id: string) =>
  httpFIR.get(`panchanamas/${id}`).json<Panchanama>();
export const signPanchanama = (id: string) =>
  httpFIR.post(`panchanamas/${id}/sign`).json<{ signed_at: string }>();
// Pre-FIR / inquiry-stage panchanamas (parent is a complaint, not an FIR).
export const listPanchanamasForComplaint = (complaintId: string) =>
  httpFIR.get(`complaints/${complaintId}/panchanamas`).json<Panchanama[]>();
export const createPanchanamaForComplaint = (complaintId: string, body: any) =>
  httpFIR.post(`complaints/${complaintId}/panchanamas`, { json: body }).json<Panchanama>();

// Per-FIR required-panchanamas checklist. Server derives "required" by
// unioning panchanama types across the FIR's BNS sections and cross-references
// against existing rows so the UI can show progress + deep-link to drafts.
export interface RequiredPanchanamaItem {
  code: string;
  name_en: string;
  name_mr: string;
  kind: string | null;
  panchanama_id?: string;
}
export interface RequiredPanchanamasOut {
  fir_id: string;
  sections: string[];
  required: RequiredPanchanamaItem[];
  completed: RequiredPanchanamaItem[];
  drafts: RequiredPanchanamaItem[];
  missing: RequiredPanchanamaItem[];
  progress: { done: number; total: number };
}
export const getRequiredPanchanamas = (firId: string) =>
  httpFIR.get(`firs/${firId}/panchanamas/required`).json<RequiredPanchanamasOut>();

// ---------------------------------------------------------------------------
// Arrests
// ---------------------------------------------------------------------------

export interface Arrest {
  id: string; fir_id: string;
  arrestee_name_en: string; arrestee_name_mr: string;
  arrest_kind: string; arrested_at: string; arrest_location: string;
  grounds_of_arrest: string; rights_recital_done: boolean;
  kin_notified_at: string | null; magistrate_intimation_at: string | null;
  hours_since_arrest?: number; magistrate_24h_due?: boolean;
  signed_at: string | null;
}

export const listArrests = () => httpFIR.get(`arrests`).json<Arrest[]>();
export const createArrest = (body: any) => httpFIR.post(`arrests`, { json: body }).json<Arrest>();
export const getArrest = (id: string) => httpFIR.get(`arrests/${id}`).json<Arrest>();
export const intimateMagistrate = (id: string) =>
  httpFIR.post(`arrests/${id}/intimate-magistrate`).json<{ intimated_at: string }>();

// ---- Phase 2/3 — FIR-nested + sub-flow helpers ---------------------------

export interface ArrestRow extends Arrest {
  fir_party_id?: string | null;
  released_at?: string | null;
  warrant_no?: string | null;
  warrant_court?: string | null;
  women_arrest?: boolean; night_arrest?: boolean; juvenile?: boolean;
  memo_object_key?: string | null;
  grounds_object_key?: string | null;
  photo_object_key?: string | null;
  n_intim?: number; n_search?: number; n_medical?: number;
  n_prod?: number; n_remand?: number; n_custody?: number;
}

export const listArrestsForFir = (firId: string) =>
  httpFIR.get(`firs/${firId}/arrests`).json<ArrestRow[]>();

export interface FIRAccused {
  id: string; name_en?: string; name_mr?: string;
  is_known?: boolean; father_name?: string;
  age_approx?: number; gender?: string; address?: string;
  description?: string; previous_history?: string;
}
export const listFirAccused = (firId: string) =>
  httpFIR.get(`firs/${firId}/accused`).json<FIRAccused[]>();

export interface ArrestIntimation {
  id: string; recipient_kind: string; recipient_name: string;
  recipient_phone?: string | null; relation?: string | null;
  channel: string; message_en: string; message_mr: string;
  sent_at: string; delivered_at?: string | null;
  gateway_msg_id?: string | null; failure_reason?: string | null;
}
export const listIntimations = (aid: string) =>
  httpFIR.get(`arrests/${aid}/intimations`).json<ArrestIntimation[]>();
export const addIntimation = (aid: string, body: Partial<ArrestIntimation>) =>
  httpFIR.post(`arrests/${aid}/intimations`, { json: body }).json<{ id: string; ok: true }>();

export interface ArrestSearchItem {
  id: string; item_no: number; description_en: string;
  description_mr?: string | null; qty?: string | null;
  where_found?: string | null; taken_into_custody: boolean;
  evidence_id?: string | null; photo_object_key?: string | null;
  notes?: string | null; recorded_at: string;
}
export const listSearchItems = (aid: string) =>
  httpFIR.get(`arrests/${aid}/search-items`).json<ArrestSearchItem[]>();
export const addSearchItem = (aid: string, body: Partial<ArrestSearchItem>) =>
  httpFIR.post(`arrests/${aid}/search-items`, { json: body }).json<{ id: string; ok: true }>();

export interface ArrestMedicalExam {
  id: string; mlc_id?: string | null;
  examined_at: string; hospital?: string | null;
  doctor_name?: string | null; doctor_reg_no?: string | null;
  summary: string; fit_for_custody?: boolean | null;
  injuries_noted: boolean; injuries_text?: string | null;
  marks_of_violence: boolean; report_object_key?: string | null;
}
export const listMedicalExams = (aid: string) =>
  httpFIR.get(`arrests/${aid}/medical-exam`).json<ArrestMedicalExam[]>();
export const addMedicalExam = (aid: string, body: Partial<ArrestMedicalExam>) =>
  httpFIR.post(`arrests/${aid}/medical-exam`, { json: body }).json<{ id: string; ok: true }>();

export interface ArrestProduction {
  id: string; produced_at: string; court_name: string;
  court_address?: string | null; judge_name?: string | null;
  case_no?: string | null; outcome: string;
  order_text?: string | null; order_object_key?: string | null;
  next_date?: string | null;
}
export const listProductions = (aid: string) =>
  httpFIR.get(`arrests/${aid}/productions`).json<ArrestProduction[]>();
export const addProduction = (aid: string, body: Partial<ArrestProduction>) =>
  httpFIR.post(`arrests/${aid}/productions`, { json: body }).json<{ id: string; ok: true }>();

export interface ArrestRemand {
  id: string; production_id?: string | null;
  kind: "POLICE" | "JUDICIAL"; starts_at: string; ends_at: string;
  days: number; surrender_at?: string | null;
  extension_of?: string | null; extension_count: number;
  notes?: string | null;
}
export const listRemands = (aid: string) =>
  httpFIR.get(`arrests/${aid}/remands`).json<ArrestRemand[]>();
export const addRemand = (aid: string, body: Partial<ArrestRemand>) =>
  httpFIR.post(`arrests/${aid}/remand`, { json: body }).json<{ id: string; ok: true }>();

export interface ArrestCustodyLog {
  id: string; log_date: string; location: string;
  diet_note?: string | null; visit_count: number;
  movement?: string | null; medical_event?: string | null;
  notes?: string | null; recorded_at: string;
}
export const listCustody = (aid: string) =>
  httpFIR.get(`arrests/${aid}/custody`).json<ArrestCustodyLog[]>();
export const addCustody = (aid: string, body: Partial<ArrestCustodyLog>) =>
  httpFIR.post(`arrests/${aid}/custody`, { json: body }).json<{ id: string; ok: true }>();

export const releaseArrest = (
  aid: string,
  body: { reason: string; note?: string; released_at?: string },
) => httpFIR.post(`arrests/${aid}/release`, { json: body }).json<{ released_at: string; reason: string }>();

// Phase 4 — document URLs (browser will redirect to download)
export const arrestMemoUrl    = (aid: string) => `/v1/arrests/${aid}/memo.docx`;
export const arrestGroundsUrl = (aid: string) => `/v1/arrests/${aid}/grounds.docx`;
export const arrestIntimationNoticeUrl = (aid: string, iid: string) =>
  `/v1/arrests/${aid}/intimations/${iid}/notice.docx`;

export const downloadArrestMemo = (aid: string) =>
  httpFIR.get(`arrests/${aid}/memo.docx`, { timeout: 30_000 }).blob();
export const downloadArrestGrounds = (aid: string) =>
  httpFIR.get(`arrests/${aid}/grounds.docx`, { timeout: 30_000 }).blob();

export const uploadArrestPhoto = async (aid: string, file: File): Promise<{ object_key: string; sha256: string; size_bytes: number }> => {
  const fd = new FormData();
  fd.append("file", file);
  return httpFIR.post(`arrests/${aid}/photo`, { body: fd, timeout: 60_000 }).json();
};

// Phase 5 — Search panchanama PDF
export const downloadSearchPanchanama = (aid: string) =>
  httpFIR.get(`arrests/${aid}/search-panchanama.docx`, { timeout: 30_000 }).blob();

// Phase 6 — derived custody-status snapshot
export interface ArrestCustodyStatus {
  state: "RELEASED" | "IN_PO" | "IN_JU" | "REMAND_EXPIRED"
       | "AT_PS_LOCKUP_POST_INTIMATION" | "AWAITING_PRODUCTION"
       | "AWAITING_PRODUCTION_OVERDUE";
  released_at?: string;
  intimated_at?: string;
  hours_since_arrest?: number;
  kind?: "POLICE" | "JUDICIAL";
  days_total?: number;
  days_used?: number;
  ends_at?: string;
}
export const getArrestStatus = (aid: string) =>
  httpFIR.get(`arrests/${aid}/status`).json<ArrestCustodyStatus>();

// Phase 6 — production order doc upload
export const uploadProductionOrder = async (aid: string, pid: string, file: File) => {
  const fd = new FormData(); fd.append("file", file);
  return httpFIR.post(`arrests/${aid}/productions/${pid}/order-doc`,
    { body: fd, timeout: 60_000 }).json<{ object_key: string; sha256: string; size_bytes: number }>();
};

// Phase 7 — AI-46 arrest memo drafter
export interface ArrestMemoDraft {
  grounds_en: string; grounds_mr: string;
  necessity_en?: string; necessity_mr?: string;
  warnings: string[];
  model?: string; provider?: string;
}
export const aiArrestMemoDraft = (body: {
  fir_id: string; fir_party_id?: string; arrestee_name?: string;
  arrest_kind?: string; arrest_location?: string; notes?: string;
}) => httpAI.post(`ai/arrest-memo-draft`, { json: body, timeout: 60_000 })
       .json<ArrestMemoDraft>();

// ---------------------------------------------------------------------------
// Charge Sheets
// ---------------------------------------------------------------------------

export interface ChargeSheet {
  id: string; fir_id: string; cs_number: string | null;
  is_supplementary: boolean; parent_cs_id?: string | null; status: string;
  sections: string[]; accused_count: number; witness_count: number; evidence_count: number;
  summary_en: string; summary_mr: string;
  defects_pending: number; defects_json: any[];
  sho_approved_at?: string | null;
  prosecutor_reviewed_at?: string | null; prosecutor_name?: string | null;
  prosecutor_id?: string | null; prosecutor_objections?: string | null;
  signed_at: string | null; created_at: string;
}

// Assembled-on-read from the FIR aggregate (charge_sheet service /contents).
export interface ChargeSheetAnnexure {
  index_no: number; kind: string; title_en: string; title_mr: string;
  ref?: string | null; date?: string | null; signed?: boolean | null;
  object_key?: string | null; source_table: string; source_id: string | null;
}
export interface ChargeSheetWitness {
  statement_id: string | null; kind: string;
  name_en?: string | null; name_mr?: string | null;
  recorded_at?: string | null; signed: boolean; is_s164: boolean;
}
export interface ChargeSheetMaterialObject {
  evidence_id: string; kind?: string | null; qr_code?: string | null; seal_no?: string | null;
  storage_location?: string | null; description_en?: string | null; description_mr?: string | null;
  seized_at?: string | null; panchanama_kind?: string | null; object_key?: string | null;
}
export interface ChargeSheetContents {
  fir: Record<string, any>;
  sections: string[];
  accused: Record<string, any>[];
  witnesses: ChargeSheetWitness[];
  material_objects: ChargeSheetMaterialObject[];
  annexures: ChargeSheetAnnexure[];
  documents: ChargeSheetAnnexure[];   // alias of annexures
}

export const listChargeSheets = (opts?: { status?: string; fir_id?: string }) => {
  // Either filter is optional; combine when both are set. The FIR-detail
  // "Charge Sheets" tile passes fir_id so the list page can auto-redirect
  // to the single CS (or surface the "create from FIR" CTA when there's none).
  const sp: Record<string, string> = {};
  if (opts?.status) sp.status = opts.status;
  if (opts?.fir_id) sp.fir_id = opts.fir_id;
  return httpFIR.get(`charge-sheets`, Object.keys(sp).length ? { searchParams: sp } : undefined)
    .json<ChargeSheet[]>();
};
export const createChargeSheet = (body: any) =>
  httpFIR.post(`charge-sheets`, { json: body }).json<ChargeSheet>();
export const autoCreateChargeSheet = (firId: string, body?: { is_supplementary?: boolean; parent_cs_id?: string }) =>
  httpFIR.post(`charge-sheets/auto`, { searchParams: { fir_id: firId }, json: body ?? {} }).json<ChargeSheet>();
export const getChargeSheet = (id: string) => httpFIR.get(`charge-sheets/${id}`).json<ChargeSheet>();
export const getChargeSheetContents = (id: string) =>
  httpFIR.get(`charge-sheets/${id}/contents`).json<ChargeSheetContents>();
export interface DefectCheck {
  id: string;
  title_en: string;
  title_mr: string;
  status: "PASS" | "WARN" | "FAIL";
  summary: string;
  ref: string;
  details: string[];
}
export interface DefectScan {
  defects_pending: number;
  defects_failing: number;
  rules_total: number;
  checks: DefectCheck[];
  defects: { id: string; label: string }[];   // legacy short list (PASS rows omitted)
}
export const checkDefects = (id: string) =>
  httpFIR.post(`charge-sheets/${id}/check-defects`).json<DefectScan & {
    s187_bucket_days?: number;
    s187_due_at?: string | null;
    s187_remaining_days?: number | null;
    sanction_required?: boolean;
    sanction_kind?: string | null;
    readiness_score?: number;
  }>();
export const fileChargeSheet = (id: string, body?: { court_id?: string; override_reason?: string }) =>
  httpFIR.post(`charge-sheets/${id}/file`, body ? { json: body } : undefined)
         .json<{ cs_number: string; filed_at: string }>();

// ---- spec 016 — pre-filing readiness wrappers ---------------------------- --

export interface CSReadiness {
  id: string;
  status: string;
  defects_pending: number;
  readiness_score: number;
  sanction_required: boolean;
  sanction_status: "N/A" | "NEEDED" | "APPLIED" | "GRANTED" | "REFUSED" | "WAIVED";
  s187_bucket_days: number;
  s187_due_at: string | null;
  wizard: { step: number; label: string; state: "done" | "current" | "pending" | "blocking";
            count?: number; at?: string | null; objections?: string | null }[];
  sanctions: {
    id: string; kind: string; status: string;
    sanctioning_authority: string;
    applied_at: string | null; granted_at: string | null; refused_at: string | null;
    order_ref: string | null; granted_sections: string[] | null;
  }[];
}
export const getReadiness = (id: string) =>
  httpFIR.get(`charge-sheets/${id}/readiness`).json<CSReadiness>();

// CS-side sanction (spec 016) — distinct from court-side `applySanction` /
// `grantSanction` / `refuseSanction` (spec 015) which target court_filing.
export const applySanctionCS = (id: string, body: {
  kind: "S197_PUBLIC_SERVANT" | "S196_STATE_OFFENCE" | "S188_EXTRA_TERRITORIAL" | "ACB" | "LOKAYUKTA" | "OTHER";
  sanctioning_authority: string;
  authority_master_id?: string;
  accused_party_id?: string;
  notes_en?: string;
  notes_mr?: string;
}) => httpFIR.post(`charge-sheets/${id}/sanction`, { json: body }).json<{ id: string; status: string }>();

export const recordSanctionCS = (id: string, sid: string, body: {
  status: "GRANTED" | "REFUSED" | "WAIVED";
  order_ref?: string;
  order_doc_key?: string;
  granted_sections?: string[];
  notes_en?: string;
  notes_mr?: string;
}) => httpFIR.patch(`charge-sheets/${id}/sanction/${sid}`, { json: body }).json<{ id: string; status: string }>();

export const recordTipEvent = (id: string, body: {
  conducted_at?: string; magistrate_name?: string; memo_doc_key?: string;
  witness_party_ids?: string[]; accused_party_ids?: string[];
  result?: string; notes_en?: string; notes_mr?: string;
}) => httpFIR.post(`charge-sheets/${id}/tip-event`, { json: body }).json<{ id: string }>();

export const pcrPull = (id: string) =>
  httpFIR.post(`charge-sheets/${id}/pcr-pull`).json<{ pulled: number; accused_party_ids: string[] }>();

// ---------------------------------------------------------------------------
// Analytics: KPIs, supervisor queue, audit trail, reports
// ---------------------------------------------------------------------------

export const getKpis = () => httpFIR.get(`kpis`).json<Record<string, any>>();
export const getSupervisorQueue = () => httpFIR.get(`supervisor/queue`).json<{
  firs_to_sign: any[]; charge_sheets_to_review: any[]; arrests_overdue: any[];
}>();
export const getAuditTrail = (recordType: string, recordId: string) =>
  httpFIR.get(`audit-trail/${recordType}/${recordId}`).json<any[]>();
export const getThroughput = () =>
  httpFIR.get(`reports/throughput`).json<{ series: { day: string; n: number }[] }>();

// ---------------------------------------------------------------------------
// Citizen public APIs (no auth)
// ---------------------------------------------------------------------------

export const filePublicComplaint = (body: any) =>
  httpFIR.post(`public/complaints`, { json: body }).json<{
    id: string; tracking_code: string; gd_no?: string;
    acknowledgement_pdf?: { url: string; sha256: string; generated_at: string } | null;
    acknowledgement_pdf_url?: string;
    acknowledgement_pdf_status?: "ready" | "pending";
  }>();

// Complainant's acknowledgement copy of the complaint as registered (BNSS S.173(1)).
// Public — no auth; addressed by complaint id. Returns application/pdf, or 503 while
// the copy is still being prepared (e.g. PDF renderer briefly unavailable).
export const complaintAcknowledgementPdfUrl = (cid: string) =>
  `${API_BASE}/public/complaints/${cid}/acknowledgement.pdf`;
// ───────────────────────────────────────────────────────────────────────────
// BNSS S.173 identity-verification flows (Aadhaar eKYC + Mobile OTP)
// Both pairs talk to the identity service (port 8002) via /v1/identity/*.
// ───────────────────────────────────────────────────────────────────────────

export interface OTPRequestResult {
  txn_id: string;
  sent_to_mobile_last4: string;
  expires_at_ms: number;
  dev_otp?: string;          // populated only when identity service runs in mock mode
}

export const requestAadhaarOTP = (aadhaar: string) =>
  httpFIR.post(`identity/aadhaar/otp`, {
    json: { aadhaar_or_vid: aadhaar.replace(/[^0-9]/g, ""), purpose: "complaint_complainant_identity" },
  }).json<OTPRequestResult>();

export interface AadhaarVerifyResult {
  success: boolean; err_code?: string;
  aadhaar_hash?: string;
  name?: { en: string; mr: string };
  dob?: string; gender?: string;
  address?: { en: string; mr: string };
}
export const verifyAadhaarOTP = (txn_id: string, aadhaar: string, otp: string) =>
  httpFIR.post(`identity/aadhaar/verify`, {
    json: { txn_id, aadhaar_or_vid: aadhaar.replace(/[^0-9]/g, ""), otp },
  }).json<AadhaarVerifyResult>();

export const requestMobileOTP = (mobile: string) =>
  httpFIR.post(`identity/mobile/otp`, {
    json: { mobile, purpose: "complaint_complainant_identity" },
  }).json<OTPRequestResult>();

export interface MobileVerifyResult { success: boolean; error?: string; mobile?: string; verified_at_ms?: number; }
export const verifyMobileOTP = (txn_id: string, mobile: string, otp: string) =>
  httpFIR.post(`identity/mobile/verify`, {
    json: { txn_id, mobile, otp },
  }).json<MobileVerifyResult>();

// ───────────────────────────────────────────────────────────────────────────
// NLP (L3Cube MahaBERT) — used by the complaint form for live entity-extraction
// after the LLM AI-assist pass. Officer-auth via cases service (port 8014).
// ───────────────────────────────────────────────────────────────────────────

export interface NEREntity {
  entity_group: "Date" | "Time" | "Person" | "Location" | "Organization" | "Other" | string;
  word: string;
  score: number;
  start?: number | null;
  end?: number | null;
}
export interface NLPRunResult {
  ok: boolean;
  status_code: number;
  latency_ms: number;
  endpoint: "ner" | "sentiment" | "hate" | "embed" | "generate";
  result?: {
    entities?: NEREntity[];
    label?: string;
    score?: number;
    embedding?: number[];
    text?: string;
  };
  error?: string;
}

export const nlpRun = (endpoint: NLPRunResult["endpoint"], text: string) =>
  httpFIR.post(`nlp/run`, { json: { endpoint, text } }).json<NLPRunResult>();

// ───────────────────────────────────────────────────────────────────────────
// TTS — Marathi readback for citizen ack / status (MMS Marathi via cases proxy)
// Returns an audio Blob the caller drops into <audio> or playback.
// ───────────────────────────────────────────────────────────────────────────

export async function synthesizeTTS(text: string, language: string = "mr"): Promise<Blob> {
  const r = await fetch(`/v1/tts/synthesize`, {
    method: "POST",
    headers: withFetchAuth({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text, language }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`TTS failed (HTTP ${r.status}): ${t.slice(0, 200)}`);
  }
  return await r.blob();
}

export const lookupCitizen = (mobile: string) =>
  httpFIR.get(`public/citizens/lookup`, { searchParams: { mobile } })
    .json<{ found: boolean; citizen_id?: string; name_en?: string; name_mr?: string;
            mobile?: string; dob?: string | null; age?: number | null; gender?: string;
            address_en?: string; address_mr?: string;
            prior_complaints?: number; last_filed_at?: string | null }>();

export const lookupSections = (codes: string[]) =>
  httpFIR.get(`public/sections`, { searchParams: { codes: codes.join(",") } })
    .json<{
      sections: { code: string; title_en: string; title_mr: string;
                  cognisable: boolean; bailable: boolean; max_punishment_text: string }[];
      missing_codes: string[];
      any_cognisable: boolean;
      all_bailable: boolean;
    }>();

// Targeted entity extraction (Fix 3) — pulls structured entities out of a
// transcript so the form can auto-fill incident_at, loss, place, vehicle, IMEI, UPI.
export interface ExtractedEntities {
  incident_at?: string;       // ISO-8601
  loss_value_inr?: number;
  place_en?: string;
  place_mr?: string;
  place_type?: string;        // Residential | Commercial | Road | Railway | Open Area | Forest | River/Water | Other
  cognisable?: string;        // "yes" (→ FIR / BNSS S.173) | "no" (→ NCR / BNSS S.174) | null
  vehicle_reg?: string;
  imei?: string;
  upi_id?: string;
  phone_numbers?: string[];
  witnesses?: { name: string; mobile?: string; address?: string; role?: string }[];
  suspects?: { name: string; description?: string; relationship?: string }[];
  // Speaker's self-identification — fills ③ Complainant identity ONLY where
  // the form fields are still empty (Aadhaar / mobile-lookup data wins).
  complainant?: {
    name_en?: string | null;  name_mr?: string | null;
    age?: number | null;      gender?: "M" | "F" | "O" | null;
    mobile?: string | null;   email?: string | null;
    address_en?: string | null; address_mr?: string | null;
    village_city?: string | null; taluka?: string | null;
    district?: string | null; state?: string | null; pin?: string | null;
    caste?: string | null;    nationality?: string | null;
    id_type?: string | null;  id_number?: string | null;
  };
  // Statutory sensitivity tags inferred from narrative.
  sensitivity_flags?: string[];
  // BNSS S.173 — proposed delay_explanation text when incident is > 24 h old.
  delay_explanation_suggestion?: string | null;
}
export const aiExtractEntities = (
  raw_text: string,
  language = "mr",
  complainant_name?: string,
  ner_entities?: { word: string; entity_group: string; score?: number }[],
) =>
  httpAI.post(`ai/extract-entities`, {
    json: {
      raw_text, language,
      complainant_name: complainant_name || undefined,
      ner_entities: (ner_entities && ner_entities.length) ? ner_entities : undefined,
    },
    // Structured entity extraction runs on the heavy LLM (235B) — same headroom
    // as complaint-assist so the date/place/loss/vehicle fields actually fill.
    timeout: 360_000,
  }).json<{ entities: ExtractedEntities; provider?: string; model?: string }>();

// L3Cube NER convenience helper — reuses the existing NLPRunResult interface
// declared above. Identical wire shape as nlpRun("ner", text); just typed.
export const aiNERRun = (text: string) =>
  httpFIR.post(`nlp/run`, { json: { endpoint: "ner", text }, timeout: 20_000 })
    .json<NLPRunResult>();

// LLM-backed translation (or IndicTrans2 once `ai.translate.provider` is set).
// Used by the Audio Statement block when the complainant speaks something
// other than Marathi — produces a Marathi gloss for the officer.
export interface TranslateResult {
  translation: string; src: string; tgt: string;
  provider: string; model?: string;
}
export const aiTranslate = (text: string, src: string, tgt = "mr") =>
  httpAI.post(`ai/translate`, { json: { text, src, tgt }, timeout: 30_000 })
    .json<TranslateResult>();

// Compoundability check — gate for the Raji-Nama (COMPROMISE) decision path.
// Returns "table_i" (parties), "table_ii" (with magistrate permission),
// "not_compoundable" (illegal to compromise) or "uncertain".
export interface CompoundabilityResult {
  classification: "table_i" | "table_ii" | "not_compoundable" | "uncertain";
  rationale_en: string;
  rationale_mr: string;
  citation: string;
  blocking_sections: string[];
  blocking_flags?: string[];
  sections_evaluated: string[];
  provider: string;
  model?: string;
}
export const aiCompoundabilityCheck = (body: {
  sections: string[]; summary_en?: string; summary_mr?: string;
  sensitivity_flags?: string[];
}) =>
  httpAI.post(`ai/compoundability-check`, { json: body, timeout: 30_000 })
    .json<CompoundabilityResult>();

// Raji-Nama draft — produces standardised bilingual compromise-deed prose
// the SHO reviews and edits before signing.
export interface RajiNamaDraftResult {
  terms_en: string; terms_mr: string;
  provider: string; model?: string;
}
export const aiRajiNamaDraft = (body: {
  party_a: string; party_b: string;
  summary_en?: string; summary_mr?: string;
  sections?: string[]; place?: string;
}) =>
  httpAI.post(`ai/raji-nama-draft`, { json: body, timeout: 60_000 })
    .json<RajiNamaDraftResult>();

// AI-45 Case Brief — 2-page bilingual case overview from the file.
// Compact, Sprint 1: complaint-level only.
export type CaseBriefSectionKey =
  | "header" | "parties" | "sequence" | "sections_law"
  | "evidence" | "witnesses" | "status" | "recommendations";

export interface CaseBriefAnalysis {
  ingredients_met: string[];
  ingredients_missing: string[];
  contradictions: string[];
  evidence_gaps: string[];
  red_flags: string[];
}
export interface CaseBriefResult {
  complaint_id: string | null;
  fir_id: string | null;
  target: string;
  depth: string;
  deep?: boolean;
  thinking_used?: boolean;
  sections: Record<CaseBriefSectionKey, { en: string; mr: string }>;
  analysis?: CaseBriefAnalysis | null;
  brief_md_en: string;
  brief_md_mr: string;
  model: string;
  provider: string;
  generated_at: string;
}
export const aiCaseBrief = (body: {
  complaint_id?: string; fir_id?: string;
  target?: "sho"|"sp"|"pp"|"court";
  depth?: "compact"|"full";
  deep?: boolean;
  /** One-off model override for deep mode (e.g. "kimi-k2-thinking").
   *  Ignored when `deep` is false. Beats the per-button override and the
   *  catalog default; useful when the SHO wants to try a different
   *  reasoning model for a single brief without touching AI Config. */
  deep_model_override?: string;
}) => httpAI.post(`ai/case-brief`, { json: body, timeout: 120_000 })
       .json<CaseBriefResult>();

// ---------------------------------------------------------------------------
// Spec 017 — generic skill runner + officer-edit feedback capture
// ---------------------------------------------------------------------------

export async function aiRunSkill<T = any>(
  skillId: string,
  inputs: Record<string, unknown>,
  opts?: { variant?: string; dry_run?: boolean }
): Promise<T> {
  return httpAI.post(`ai/skills/${skillId}`, {
    json: { inputs, ...(opts ?? {}) }
  }).json<T>();
}

export async function recordOfficerEdit(
  interactionId: string,
  original: string,
  final: string
): Promise<{ ok: boolean; truncated: boolean }> {
  return httpAI.post(`ai/interactions/${interactionId}/officer-edit`, {
    json: { original_text: original, final_text: final }
  }).json<{ ok: boolean; truncated: boolean }>();
}

// AI-50 — Closure / final report (BNSS S.193(8)) draft. Feature 012 Phase 0.
// Returns a recommended classification (A/B/C/N) + bilingual rationale and
// the reason_en / reason_mr the IO can paste into the existing closure form.
// The endpoint is read-only — committing the report still goes through the
// existing `createFirClosure` writer.
export interface ClosureDraftResult {
  fir_id: string;
  classification: "A" | "B" | "C" | "N";
  classification_label_long: string;
  classification_label:      { en: string; mr: string };
  classification_rationale:  { en: string; mr: string };
  reason_en: string;
  reason_mr: string;
  key_facts:               { en: string; mr: string };
  investigation_actions:   { en: string; mr: string };
  evidence_gaps:           { en: string; mr: string };
  alternatives_considered: { en: string; mr: string };
  deep: boolean;
  thinking_used: boolean;
  model: string;
  provider: string;
  generated_at: string;
}
export const aiClosureDraft = (body: {
  fir_id: string; deep?: boolean; deep_model_override?: string;
}) => httpAI.post(`ai/closure-draft`, { json: body, timeout: 180_000 })
       .json<ClosureDraftResult>();

// AI-51 — Hearing brief (BNSS S.193 court date prep). Feature 012 Phase 1.
// One-page bilingual brief: witnesses to bring, evidence to produce,
// ingredients still open, decoded last-order text, defence-cross traps,
// procedural actions. Read-only — IO updates court_filing through its
// own endpoints after the hearing.
export interface HearingBriefWitness {
  party_id: string | null;
  name: string | null;
  role: string | null;
  why_en: string;
  why_mr: string;
}
export interface HearingBriefEvidence {
  evidence_id: string;
  kind: string | null;
  qr_code: string | null;
  why_en: string;
  why_mr: string;
}
export interface HearingBriefIngredient {
  section_code: string;
  ingredient_code: string;
  ingredient_text_en: string;
  ingredient_text_mr: string;
  evidence_needed_en: string;
  evidence_needed_mr: string;
}
export interface HearingBriefResult {
  court_filing_id: string;
  fir_id: string;
  case_identity: {
    fir_number: string | null;
    cs_number: string | null;
    court_name: string | null;
    e_court_filing_id: string | null;
    next_hearing_at: string | null;
    status: string | null;
  };
  summary:              { en: string; mr: string };
  last_order_decoded:   { en: string; mr: string };
  witnesses_to_produce: HearingBriefWitness[];
  evidence_to_produce:  HearingBriefEvidence[];
  ingredients_open:     HearingBriefIngredient[];
  traps:                { en: string; mr: string };
  procedural_actions:   { en: string; mr: string };
  deep: boolean;
  thinking_used: boolean;
  model: string;
  provider: string;
  generated_at: string;
}
export const aiHearingBrief = (body: {
  court_filing_id: string; deep?: boolean; deep_model_override?: string;
}) => httpAI.post(`ai/hearing-brief`, { json: body, timeout: 180_000 })
       .json<HearingBriefResult>();

// AI-52 / AI-53 — Bail-application AI (feature 012 Phase 2).
// `bail_objection_draft` writes the police objection the SHO files; bilingual,
// statutory bars cited only when the FIR sections actually trigger them.
// `bail_application_summary` is the ≤220-word SHO read at the decision gate.
// Both are read-only; the bail row is committed via the existing endpoints.
export interface BailStatutoryBar {
  statute: string;
  section: string | null;
  applies: true;
  rationale_en: string;
  rationale_mr: string;
}
export interface BailObjectionResult {
  bail_application_id: string;
  related_fir_id: string | null;
  related_arrest_id: string | null;
  sections: string[];
  candidate_bars: { statute: string; applies_because_of_sections: string[] }[];
  objection:                    { en: string; mr: string };
  grounds:                      string[];
  statutory_bars:               BailStatutoryBar[];
  witness_tampering_risk:       { en: string; mr: string };
  ongoing_investigation_risk:   { en: string; mr: string };
  conditions_if_granted:        string[];
  deep: boolean;
  thinking_used: boolean;
  model: string;
  provider: string;
  generated_at: string;
}
export interface BailSummaryResult {
  bail_application_id: string;
  related_fir_id: string | null;
  sections: string[];
  candidate_bars: { statute: string; applies_because_of_sections: string[] }[];
  summary:        { en: string; mr: string };
  recommendation: { en: string; mr: string };
  headline_risks: string[];
  applicable_bars: string[];
  deep: boolean;
  thinking_used: boolean;
  model: string;
  provider: string;
  generated_at: string;
}
export const aiBailObjectionDraft = (body: {
  bail_application_id: string; deep?: boolean; deep_model_override?: string;
}) => httpAI.post(`ai/bail-objection-draft`, { json: body, timeout: 180_000 })
       .json<BailObjectionResult>();
export const aiBailApplicationSummary = (body: {
  bail_application_id: string; deep?: boolean; deep_model_override?: string;
}) => httpAI.post(`ai/bail-application-summary`, { json: body, timeout: 120_000 })
       .json<BailSummaryResult>();

// AI-54 / AI-55 — Summons/warrant prefill + proclamation draft (012 Phase 3).
// summons-prefill is called from the Receive modal on the Summons & Warrants
// register; the form fields auto-populate with addressee + body + a service-
// feasibility hint. proclamation-draft fires only on a NOT_SERVED NBW; the
// IO commits via the existing process_doc endpoints.
export interface ServiceFeasibility {
  score: number | null;       // 0..1
  signals: string[];
  verdict_en: string | null;
  verdict_mr: string | null;
}
export interface SummonsPrefillResult {
  fir_id: string | null;
  kind: string;
  court_case_no: string | null;
  court_name: string | null;
  addressee_name: string | null;
  addressee_address: string | null;
  body: { en: string; mr: string };
  service_feasibility: ServiceFeasibility;
  deep: boolean;
  thinking_used: boolean;
  model: string;
  provider: string;
  generated_at: string;
}
export interface ProclamationDraftResult {
  process_doc_id: string;
  fir_id: string | null;
  addressee_name: string;
  order_ref_template: string | null;
  body: { en: string; mr: string };
  appearance_window_days: number;
  attachment_recommended: boolean;
  attachment_rationale: { en: string; mr: string };
  deep: boolean;
  thinking_used: boolean;
  model: string;
  provider: string;
  generated_at: string;
}
export const aiSummonsPrefill = (body: {
  kind: string;
  fir_id?: string | null;
  court_case_no?: string | null;
  court_name?: string | null;
  addressee_hint?: string | null;
  deep?: boolean; deep_model_override?: string;
}) => httpAI.post(`ai/summons-prefill`, { json: body, timeout: 180_000 })
       .json<SummonsPrefillResult>();
export const aiProclamationDraft = (body: {
  process_doc_id: string; deep?: boolean; deep_model_override?: string;
}) => httpAI.post(`ai/proclamation-draft`, { json: body, timeout: 180_000 })
       .json<ProclamationDraftResult>();

// AI-56 — MLC / FSL report summarise + ingredient-link propose (012 Phase 4).
// The AI never writes fir_ingredient_proof. The IO ticks proposals in the UI
// and posts the accepted ones to the existing cases-service writer
// `bulkInsertProofs` (also added this phase, alongside the single-link writer).
export interface LabFindingLink {
  section_code: string;
  ingredient_code: string;
  confidence: number;            // 0..1
  rationale_en: string;
  rationale_mr: string;
}
export interface LabFinding {
  label_en: string;
  label_mr: string;
  value: string | null;
  significance_en: string;
  significance_mr: string;
  ingredient_links: LabFindingLink[];
}
export interface LabReportSummariseResult {
  kind: "mlc" | "fsl";
  request_id: string;
  fir_id: string;
  sections: string[];
  summary: { en: string; mr: string };
  findings: LabFinding[];
  proof_record_type: "mlc_request" | "fsl_request";
  proof_record_id: string;
  deep: boolean;
  thinking_used: boolean;
  model: string;
  provider: string;
  generated_at: string;
}
export const aiLabReportSummarise = (body: {
  kind: "mlc" | "fsl"; request_id: string;
  deep?: boolean; deep_model_override?: string;
}) => httpAI.post(`ai/lab-report-summarise`, { json: body, timeout: 180_000 })
       .json<LabReportSummariseResult>();

// Bulk-insert of accepted AI ingredient-link proposals. Existing single-link
// writer is `POST /v1/firs/{id}/proofs`; this one accepts N in one round-trip
// and returns a partial-success summary (no row is committed unless its FIR
// + catalog checks pass — failures are reported as `skipped`).
export interface BulkProofItem {
  section_code: string;
  ingredient_code: string;
  proof_record_type: "evidence_item" | "statement" | "panchanama"
                   | "mlc_request" | "fsl_request" | "arrest_record" | "note";
  proof_record_id?: string | null;
  note?: string | null;
}
export interface BulkProofsResult {
  fir_id: string;
  inserted: { id: string; section_code: string; ingredient_code: string;
              proof_record_type: string; proof_record_id: string | null }[];
  skipped:  { item: BulkProofItem; reason: string }[];
  summary:  { inserted: number; skipped: number; submitted: number };
}
export const bulkInsertProofs = (firId: string, body: { proofs: BulkProofItem[] }) =>
  httpFIR.post(`firs/${firId}/proofs/bulk-insert`, { json: body, timeout: 60_000 })
         .json<BulkProofsResult>();

// AI-57 / AI-58 — Statement polish + per-witness Q-list (012 Phase 5).
// Both are read-only. Statement polish is paired with the new
// `patchStatementTranscript` writer (also added this phase) that accepts the
// cleaned text and recomputes the canonical_sha256.
export interface StatementPolishInconsistency {
  with_statement_id: string | null;
  summary_en: string;
  summary_mr: string;
}
export interface StatementPolishResult {
  statement_id: string;
  fir_id: string;
  transcript_en: string;
  transcript_mr: string;
  follow_up_qs:    { en: string[]; mr: string[] };
  inconsistencies: StatementPolishInconsistency[];
  changes_made:    { en: string; mr: string };
  deep: boolean;
  thinking_used: boolean;
  model: string;
  provider: string;
  generated_at: string;
}
export interface WitnessQuestionsCoverage {
  section_code: string;
  ingredient_code: string;
  rationale_en: string;
  rationale_mr: string;
}
export interface WitnessQuestionsResult {
  fir_id: string;
  party_id: string;
  party_name: string | null;
  party_role: string | null;
  summary:    { en: string; mr: string };
  questions:  { en: string[]; mr: string[] };
  covers_ingredients: WitnessQuestionsCoverage[];
  deep: boolean;
  thinking_used: boolean;
  model: string;
  provider: string;
  generated_at: string;
}
export const aiStatementPolish = (body: {
  statement_id: string; deep?: boolean; deep_model_override?: string;
}) => httpAI.post(`ai/statement-polish`, { json: body, timeout: 180_000 })
       .json<StatementPolishResult>();
export const aiWitnessQuestions = (body: {
  fir_id: string; party_id: string; deep?: boolean; deep_model_override?: string;
}) => httpAI.post(`ai/witness-questions`, { json: body, timeout: 120_000 })
       .json<WitnessQuestionsResult>();

// FIR-witnesses listing — paired with aiWitnessQuestions. `without_statement`
// filter drives the AI Q-list panel (012 Phase 5 / Phase 8 deferral closed).
export interface FirWitnessParty {
  id: string;
  role: string;
  name_en: string | null;
  name_mr: string | null;
  address: string | null;
  age_approx: number | null;
  gender: string | null;
  has_statement: boolean;
}
export const listFirWitnessParties = (firId: string, withoutStatement = false) =>
  httpFIR.get(`firs/${firId}/witness-parties`, {
    searchParams: withoutStatement ? { without_statement: "true" } : {},
  }).json<FirWitnessParty[]>();

// AI-59 — CDR significant-number summary (012 Phase 6). Bilingual narrative on
// top of the existing /v1/cdr/datasets/{id}/analysis aggregations. The
// numbers (MSISDNs, IMEIs, cell IDs) come from a closed set so the LLM can't
// invent them. Read-only — the dataset rows are not mutated.
export interface CdrTopContact {
  msisdn: string;
  calls: number;
  total_minutes: number;
  why_significant_en: string;
  why_significant_mr: string;
}
export interface CdrAnomaly {
  label_en: string;
  label_mr: string;
  evidence_en: string;
  evidence_mr: string;
  severity: "info" | "warn" | "high";
}
export interface CdrSummaryResult {
  cdr_dataset_id: string;
  fir_id: string | null;
  subscriber_msisdn: string | null;
  period_from: string | null;
  period_to: string | null;
  row_count: number;
  summary:       { en: string; mr: string };
  top_contacts:  CdrTopContact[];
  anomalies:     CdrAnomaly[];
  deep: boolean;
  thinking_used: boolean;
  model: string;
  provider: string;
  generated_at: string;
}
export const aiCdrSummary = (body: {
  cdr_dataset_id: string; deep?: boolean; deep_model_override?: string;
}) => httpAI.post(`ai/cdr-summary`, { json: body, timeout: 180_000 })
       .json<CdrSummaryResult>();

// AI-60 — Whole-FIR ingredient-link proposer (012 Phase 7).
// Reads every evidence/statement/panchanama/MLC/FSL/arrest row on the FIR
// and proposes which (section_code, ingredient_code) pair each one proves.
// IO ticks accept; accepted rows go through the Phase-4 bulk-insert writer.
export interface IngredientLinkProposal {
  source_kind: "evidence_item" | "statement" | "panchanama"
             | "mlc_request"   | "fsl_request" | "arrest_record";
  source_id: string;
  source_label: string | null;
  section_code: string;
  ingredient_code: string;
  confidence: number;
  rationale_en: string;
  rationale_mr: string;
}
export interface IngredientLinkCoverageRow {
  section_code: string;
  ingredient_code: string;
  proposed_links_count: number;
  best_confidence: number;
}
export interface IngredientLinkResult {
  fir_id: string;
  sections: string[];
  open_ingredients_total: number;
  proposals: IngredientLinkProposal[];
  dropped: number;
  coverage: IngredientLinkCoverageRow[];
  proof_record_writer_url: string;
  deep: boolean;
  thinking_used: boolean;
  model: string;
  provider: string;
  generated_at: string;
}
export const aiIngredientLinkPropose = (body: {
  fir_id: string; deep?: boolean; deep_model_override?: string;
}) => httpAI.post(`ai/ingredient-link-propose`, { json: body, timeout: 240_000 })
       .json<IngredientLinkResult>();
// Paired writer in the statement service. Refuses when the row is signed
// or §164-marked; recomputes canonical_sha256.
export const patchStatementTranscript = (sid: string, body: {
  transcript_en: string; transcript_mr: string;
  transcript_source?: "ai_polished" | "stt_edited" | "translated" | "manual";
}) => httpFIR.patch(`statements/${sid}/transcript`, { json: body, timeout: 30_000 })
       .json<{ id: string; canonical_sha256: string; transcript_source: string }>();

// Sprint #3 — repeat-pattern + sensitivity-flag chips for the COMPROMISE
// path of the Decision Gate. Backed by /v1/complaints/{cid}/raji-nama-context.
export interface RajiNamaContextFlag {
  kind: string;            // WOMAN_COMPLAINANT, SENSITIVITY_*, DV_KEYWORD, REPEAT_PARTIES
  tone: "info" | "warn" | "block";
  en: string; mr: string;
}
export interface RajiNamaPriorDeed {
  compromise_deed_id: string;
  complaint_id: string;
  party_a_name: string; party_b_name: string;
  signed_at: string | null;
}
export interface RajiNamaContext {
  complaint_id: string;
  party_a: string; party_b: string;
  flags: RajiNamaContextFlag[];
  prior_compromises: RajiNamaPriorDeed[];
}
export const fetchRajiNamaContext = (cid: string, body: { party_a: string; party_b: string }) =>
  httpFIR.post(`complaints/${cid}/raji-nama-context`, { json: body, timeout: 15_000 })
    .json<RajiNamaContext>();

/**
 * Append `?token=<jwt>&ps=<ps_code>` (or `&token=…` if the path already has a
 * query string) so that browser top-level navigations such as
 * `<a href={…} target="_blank">` can authenticate against the FastAPI
 * services. Required for every PDF / print / HTML endpoint that's hit by a
 * direct link (the browser strips the Authorization header on new-tab opens).
 *
 * The shared `authenticate` dependency in backend/shared/auth.py accepts
 * `?token=` and `?ps=` as a fallback when no Authorization header is present.
 */
export function withAuthQS(path: string, ps_code?: string): string {
  const tok = bareToken();
  const ps  = ps_code ?? currentPsCode();
  if (!tok || !ps) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}token=${encodeURIComponent(tok)}&ps=${encodeURIComponent(ps)}`;
}

/** Claims of the current JWT — used by the UI to gate role-specific actions.
 * Server still enforces; this is best-effort UX only. */
export function currentOfficer(): { officer_id?: string; role?: string; ps_code?: string; rank?: string } {
  try {
    const b64 = bareToken().split(".")[1] || "";
    if (!b64) return {};
    const std = b64.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(b64.length / 4) * 4, "=");
    return JSON.parse(atob(std));
  } catch { return {}; }
}

// Sprint #4 — printable Raji-Nama deed. Endpoint returns server-rendered
// HTML; the detail page opens it in a new tab and the browser handles
// "Save as PDF" / printing. Browser tabs can't send our Authorization
// header on a plain link, so the endpoint also accepts ?token=<jwt>;
// we pass the unsigned dev JWT here.
export const rajiNamaPrintUrl = (cid: string) =>
  withAuthQS(`/v1/complaints/${cid}/raji-nama-print`);

// Forward-geocode (Nominatim by default; admin can swap providers later).
// Used to populate incident lat/lng once the AI fills place_en / place_mr.
export interface GeocodeResult {
  lat?: number; lng?: number;
  display_name?: string; provider?: string;
}
export const aiGeocode = (q: string) =>
  httpAI.get(`ai/geocode`, { searchParams: { q }, timeout: 15_000 })
    .json<GeocodeResult>();

// Answer a fixed list of guided questions from a narrative. Used by the
// New-Complaint Incident-type block where each crime type has 6 standard
// follow-up questions; the LLM tries to extract concise answers from the
// OCR / audio text so the IO only fills in the gaps. Returns one entry per
// input question (in order); null = unanswered by the source.
export interface GuidedAnswerResult {
  answers: (string | null)[];
  provider: string; model?: string;
}
export const aiAnswerGuided = (
  text: string,
  questions: string[],
  language = "mr",
  complainant_name?: string,
) =>
  httpAI.post(`ai/answer-guided`, {
    json: {
      text, questions, language,
      complainant_name: complainant_name || undefined,
    },
    timeout: 30_000,
  }).json<GuidedAnswerResult>();

// ---------------------------------------------------------------------------
// OCR — written-complaint upload pipeline. Provider is configured server-side
// via `ai.ocr.engine` (mistral_ocr today). Returns extracted text + provenance.
// ---------------------------------------------------------------------------
export interface OCRRunResult {
  text: string;
  language?: string | null;
  page_count?: number;
  engine: string;
  model?: string;
}
export const aiOcr = (body: {
  object_key: string;
  content_type?: string;
  label?: string;
}) =>
  httpAI.post(`ai/ocr`, { json: body, timeout: 120_000 }).json<OCRRunResult>();

export interface OCRTestResult {
  ok: boolean;
  engine: string;
  configured: boolean;
  detail: string;
  model?: string;
  ocr_models?: string[];
}
export const ocrTest = () =>
  httpAI.get(`ai/ocr/test`, { timeout: 45_000 }).json<OCRTestResult>();

// Persisted OCR extracts on a complaint.
export interface OCRExtractServer {
  id: string; complaint_id: string;
  object_key: string; sha256: string; size_bytes: number;
  content_type?: string | null;
  engine: string; model?: string | null;
  src_lang?: string | null;
  raw_text: string; text_mr?: string | null;
  page_count?: number | null;
  label?: string | null;
  extracted_at: string;
  extracted_by?: string | null;
}
export const listOcrExtracts = (cid: string) =>
  httpFIR.get(`complaints/${cid}/ocr`).json<OCRExtractServer[]>();
export const addOcrExtract = (cid: string, body: {
  object_key: string; sha256: string; size_bytes: number;
  content_type?: string;
  engine: string; model?: string;
  src_lang?: string;
  raw_text: string; text_mr?: string;
  page_count?: number; label?: string;
}) =>
  httpFIR.post(`complaints/${cid}/ocr`, { json: body }).json<{ id: string; complaint_id: string; engine: string }>();
export const deleteOcrExtract = (cid: string, oid: string) =>
  httpFIR.delete(`complaints/${cid}/ocr/${oid}`).json<{ ok: boolean }>();

// Migration 015 — multi-clip audio per complaint.
export interface AudioClipServer {
  id: string;
  complaint_id: string;
  object_key: string;
  sha256: string;
  size_bytes: number;
  src_lang?: string | null;
  transcript?: string | null;
  transcript_mr?: string | null;
  label?: string | null;
  duration_sec?: number | null;
  recorded_at: string;
  recorded_by?: string | null;
}
export interface AudioClipBody {
  object_key: string; sha256: string; size_bytes: number;
  src_lang?: string;
  transcript?: string;
  transcript_mr?: string;
  label?: string;
  duration_sec?: number;
}
export const listAudioClips = (cid: string) =>
  httpFIR.get(`complaints/${cid}/audio`).json<AudioClipServer[]>();
export const addAudioClip = (cid: string, body: AudioClipBody) =>
  httpFIR.post(`complaints/${cid}/audio`, { json: body, timeout: 30_000 })
    .json<AudioClipServer>();
export const deleteAudioClip = (cid: string, aid: string) =>
  httpFIR.delete(`complaints/${cid}/audio/${aid}`).json<{ ok: boolean }>();

// ---------------------------------------------------------------------------
// Investigation registers — surfaces on FIR detail (Phase 6 of mig 016)
// ---------------------------------------------------------------------------
export interface RegistersForFIR {
  mlc:     any[]; fsl:     any[]; process: any[];
  bail:    any[]; court:   any[]; evidence:any[];
}
export async function listRegistersForFIR(firId: string): Promise<RegistersForFIR> {
  const [mlc, fsl, malk, court, proc, bail] = await Promise.all([
    httpFIR.get(`mlc`).json<any[]>().catch(() => []),
    httpFIR.get(`fsl`).json<any[]>().catch(() => []),
    httpFIR.get(`malkhana`).json<any[]>().catch(() => []),
    httpFIR.get(`court/filings`).json<any[]>().catch(() => []),
    httpFIR.get(`registers/process`).json<any[]>().catch(() => []),
    httpFIR.get(`registers/bail`).json<any[]>().catch(() => []),
  ]);
  return {
    mlc:      mlc.filter((m: any) => m.fir_id === firId),
    fsl:      fsl.filter((f: any) => f.fir_id === firId),
    process:  proc.filter((p: any) => p.fir_id === firId),
    bail:     bail.filter((b: any) => b.related_fir_id === firId),
    court:    court.filter((c: any) => c.fir_id === firId),
    evidence: malk.filter((e: any) => e.fir_id === firId),
  };
}
export const overdueMlc     = () => httpFIR.get(`registers/mlc/overdue`).json<any[]>();
export const overdueFsl     = () => httpFIR.get(`registers/fsl/overdue`).json<any[]>();
export const overdueProcess = () => httpFIR.get(`registers/process/overdue`).json<any[]>();
export const overdueBail    = () => httpFIR.get(`registers/bail/overdue`).json<any[]>();

// Phase 9 — Supervisor dual-control state machine
export interface DualControlReq {
  id: string; ps_code: string; kind: string; status: string;
  related_evidence_id?: string | null;
  related_bail_id?: string | null;
  related_fsl_id?: string | null;
  related_cs_id?: string | null;
  reason: string;
  requested_at: string; requested_by: string;
  decided_at?: string | null; decided_by?: string | null;
  decision_note?: string | null;
}
export interface DualControlBody {
  kind: "EVIDENCE_DESTRUCTION" | "BAIL_RELEASE" | "FSL_HIGH_VALUE_DISPATCH" | "CHARGESHEET_FILING" | "OTHER";
  related_evidence_id?: string;
  related_bail_id?: string;
  related_fsl_id?: string;
  related_cs_id?: string;
  reason: string;
  requester_sig_b64?: string;
}
export const dcRequest = (body: DualControlBody) =>
  httpFIR.post(`dual-control/requests`, { json: body }).json<DualControlReq>();
export const dcList = (status?: string) =>
  httpFIR.get(`dual-control/requests`, { searchParams: status ? { status } : {} })
    .json<DualControlReq[]>();
export const dcDecide = (rid: string, decision: "APPROVED" | "REJECTED",
                         decision_note?: string, approver_sig_b64?: string) =>
  httpFIR.patch(`dual-control/requests/${rid}/decide`,
                { json: { decision, decision_note, approver_sig_b64 } })
    .json<DualControlReq>();
export const dcApply = (rid: string) =>
  httpFIR.post(`dual-control/requests/${rid}/apply`).json<DualControlReq>();

// Phase 12 — eCourts 2.0 submit. Provider switch in app_config:
// `ecourts.provider = mock` (today) → returns synthetic ack;
// `ecourts.provider = real` → POSTs to https://services.ecourts.gov.in.
export interface ECourtAck {
  filing_id: string; ack_number: string; status: string;
  received_at: string; provider: string;
}
export const ecourtSubmit = (filingId: string, notes?: string) =>
  httpFIR.post(`court/filings/${filingId}/e-court-submit`, { json: { notes } })
    .json<{ id: string; e_court_filing_id: string; ack: ECourtAck; provider: string }>();

// Statutory PDF print URLs (Phase 7). Each returns a .docx; open in a new
// tab and the browser handles download. Optional `code` overrides the
// default-per-status template code (e.g. "H-01" instead of "H-02").
export const printUrl = {
  mlc:      (id: string, code?: string) => withAuthQS(`/v1/mlc/${id}/print${code ? `?code=${code}` : ""}`),
  fsl:      (id: string, code?: string) => withAuthQS(`/v1/fsl/${id}/print${code ? `?code=${code}` : ""}`),
  malkhana: (id: string, code?: string) => withAuthQS(`/v1/malkhana/${id}/print${code ? `?code=${code}` : ""}`),
  process:  (id: string, code?: string) => withAuthQS(`/v1/registers/process/${id}/print${code ? `?code=${code}` : ""}`),
  proclamation: (pid: string) => withAuthQS(`/v1/registers/process/proclamations/${pid}/print`),
  attachment:   (aid: string) => withAuthQS(`/v1/registers/process/attachments/${aid}/print`),
  bail:     (id: string, code?: string) => withAuthQS(`/v1/registers/bail/${id}/print${code ? `?code=${code}` : ""}`),
  court:    (id: string, code?: string) => withAuthQS(`/v1/court/filings/${id}/print${code ? `?code=${code}` : ""}`),
};

// ───────────────────────────────────────────────────────────────────────────
// Mig 017 — case-strengthening enrichment APIs
// /sop-status (live SOP gates), /media (image+video evidence)
// ───────────────────────────────────────────────────────────────────────────
export interface SopStatusResult {
  complaint_id: string;
  status: string;
  live:   { sop_identity_verified: boolean; sop_audio_recorded: boolean;
            sop_incident_complete: boolean; sop_evidence_uploaded: boolean;
            sop_ack_sent: boolean };
  frozen: { sop_identity_verified: boolean; sop_audio_recorded: boolean;
            sop_incident_complete: boolean; sop_evidence_uploaded: boolean;
            sop_ack_sent: boolean };
  live_score: number;
  counts: { attachments_legacy: number; audio_clips: number;
            ocr_extracts: number; images: number; videos: number;
            inquiry_events: number };
}
export const getSopStatus = (cid: string) =>
  httpFIR.get(`complaints/${cid}/sop-status`).json<SopStatusResult>();

export interface MediaAsset {
  id: string; complaint_id: string; kind: "IMAGE" | "VIDEO";
  object_key: string; sha256: string; size_bytes: number;
  content_type?: string; label?: string;
  width?: number; height?: number; duration_sec?: number;
  exif_stripped: boolean; recorded_at: string; recorded_by?: string | null;
}
export interface MediaAssetBody {
  kind: "IMAGE" | "VIDEO"; object_key: string; sha256: string; size_bytes: number;
  content_type?: string; label?: string;
  width?: number; height?: number; duration_sec?: number;
  exif_stripped?: boolean;
}
export const listMediaAssets = (cid: string) =>
  httpFIR.get(`complaints/${cid}/media`).json<MediaAsset[]>();
export const addMediaAsset = (cid: string, body: MediaAssetBody) =>
  httpFIR.post(`complaints/${cid}/media`, { json: body, timeout: 30_000 })
    .json<{ id: string; complaint_id: string; kind: string; object_key: string; label?: string }>();
export const deleteMediaAsset = (cid: string, aid: string) =>
  httpFIR.delete(`complaints/${cid}/media/${aid}`).json<{ ok: boolean }>();

// Convenience wrappers around addInquiryEvent for the typed request kinds.
// MLC = Medico-Legal Case; FSL = Forensic Science Lab; CCTV = footage pull request.
export const requestMLC = (cid: string, args: { hospital: string; body_en: string; body_mr?: string; person_name?: string }) =>
  addInquiryEvent(cid, {
    kind: "MLC_REQUEST" as any,
    body_en: `MLC requested at ${args.hospital}: ${args.body_en}`.slice(0, 500),
    body_mr: (args.body_mr ?? `MLC विनंती ${args.hospital}: ${args.body_en}`).slice(0, 500),
    person_name: args.person_name, place_en: args.hospital,
  });
export const requestFSL = (cid: string, args: { lab: string; body_en: string; body_mr?: string }) =>
  addInquiryEvent(cid, {
    kind: "FSL_REQUEST" as any,
    body_en: `FSL test requested at ${args.lab}: ${args.body_en}`.slice(0, 500),
    body_mr: (args.body_mr ?? `FSL चाचणी विनंती ${args.lab}: ${args.body_en}`).slice(0, 500),
    place_en: args.lab,
  });
export const requestCCTVPull = (cid: string, args: { location: string; date_window: string; body_en: string; body_mr?: string }) =>
  addInquiryEvent(cid, {
    kind: "CCTV_PULL_REQUEST" as any,
    body_en: `CCTV pull requested at ${args.location} (${args.date_window}): ${args.body_en}`.slice(0, 500),
    body_mr: (args.body_mr ?? `CCTV फुटेज विनंती ${args.location} (${args.date_window}): ${args.body_en}`).slice(0, 500),
    place_en: args.location,
  });

// Phase 1A — Inquiry checklist (crime-type-derived, AI auto-tick)
export interface ChecklistStep {
  step_code: string; sort_order: number;
  criticality: "CRITICAL" | "RECOMMENDED" | "OPTIONAL";
  title_en: string; title_mr: string;
  rationale_en?: string; rationale_mr?: string;
  expected_kind?: string;
  sample_questions_en?: string[]; sample_questions_mr?: string[];
  state: "PENDING" | "DONE" | "SKIPPED";
  note?: string | null;
  auto_event_id?: string | null; auto_reason?: string | null;
  auto_ticked_now?: boolean;
}
export interface InquiryChecklistResult {
  complaint_id: string; crime_type: string;
  steps: ChecklistStep[];
  counts: { total: number; done: number; critical_pending: number };
}
export const listInquiryChecklist = (cid: string) =>
  httpFIR.get(`complaints/${cid}/inquiry-checklist`).json<InquiryChecklistResult>();
export const patchInquiryChecklistStep = (cid: string, step_code: string, body: { state: "PENDING" | "DONE" | "SKIPPED"; note?: string }) =>
  httpFIR.patch(`complaints/${cid}/inquiry-checklist/${step_code}`, { json: body }).json<{ ok: boolean }>();
// Backend returns parallel `en` / `mr` string arrays (not a `questions` array
// of objects) — the caller zips them. (Older shape kept as optional fallback.)
export const generateChecklistQuestions = (cid: string, step_code: string) =>
  httpFIR.post(`complaints/${cid}/inquiry-checklist/${step_code}/questions`, { timeout: 60_000 })
    .json<{ step_code?: string; en?: string[]; mr?: string[];
            questions?: Array<{ question_en: string; question_mr: string }> }>();

// Phase 2B — AI escalation (opt-in second-pass through a stronger model)
export interface EscalationResult {
  audit_id: string;
  primary_model: string;
  escalation_model: string;
  primary:    ComplaintSuggestionsResult;
  escalated:  ComplaintSuggestionsResult;
  diff_summary: string;
  payload_sha256: string;
}
export const escalateComplaintSuggestions = (cid: string, body: { reason?: string } = {}) =>
  httpFIR.post(`complaints/${cid}/escalate-suggestions`, { json: body, timeout: 300_000 })
    .json<EscalationResult>();

// Phase 3 — FIR-side enrichment mirror
export interface FIRSopStatusResult {
  fir_id: string; status: string;
  live: {
    investigation_statements:  boolean;
    investigation_evidence:    boolean;
    investigation_accused:     boolean;
    investigation_forensic:    boolean;
    investigation_chargesheet: boolean;
  };
  live_score: number;
  counts: {
    statements: number; seizures: number;
    images: number; videos: number;
    panchanamas: number; arrests: number;
    charge_sheets: number; investigation_events: number;
  };
}
export const getFIRSopStatus = (fid: string) =>
  httpFIR.get(`firs/${fid}/sop-status`).json<FIRSopStatusResult>();

export const listFIRMediaAssets = (fid: string) =>
  httpFIR.get(`firs/${fid}/media`).json<MediaAsset[]>();
export const addFIRMediaAsset = (fid: string, body: MediaAssetBody) =>
  httpFIR.post(`firs/${fid}/media`, { json: body, timeout: 30_000 })
    .json<{ id: string; fir_id: string; kind: string; object_key: string; label?: string }>();
export const deleteFIRMediaAsset = (fid: string, aid: string) =>
  httpFIR.delete(`firs/${fid}/media/${aid}`).json<{ ok: boolean }>();

export type InvestigationKind =
  | "STATEMENT_RECORDED" | "EVIDENCE_SEIZED" | "WITNESS_INTERVIEWED"
  | "ACCUSED_INTERROGATED" | "RAID" | "MLC_RESULT_RECEIVED"
  | "FSL_REPORT_RECEIVED" | "CCTV_OBTAINED"
  | "CHARGESHEET_DRAFT_STARTED" | "OTHER";

export interface InvestigationEvent {
  id: string; fir_id: string; kind: InvestigationKind;
  happened_at: string; body_en: string; body_mr: string;
  person_name?: string | null; person_mobile?: string | null;
  place_en?: string | null; place_mr?: string | null;
  lat?: number | null; lng?: number | null;
}
export const listInvestigationEvents = (fid: string) =>
  httpFIR.get(`firs/${fid}/investigation-events`).json<InvestigationEvent[]>();
export const addInvestigationEvent = (fid: string, body: Partial<InvestigationEvent> & { kind: InvestigationKind; body_en: string; body_mr: string }) =>
  httpFIR.post(`firs/${fid}/investigation-events`, { json: body, timeout: 15_000 })
    .json<InvestigationEvent>();

// Phase 4C — SHO dashboard
export interface DashboardComplaint {
  id: string; summary_en: string; complainant_name_en?: string;
  complainant_mobile?: string; status: string;
  received_at?: string; inquiry_due_at?: string;
  live_score: number;
  counts: { audio: number; ocr: number; images: number; videos: number; events: number; attachments: number };
}
export interface DashboardFIR {
  id: string; fir_number?: string; summary_en: string; status: string;
  registered_at?: string; live_score: number;
  counts: { statements: number; seizures: number; images: number; videos: number; panchanamas: number; arrests: number; charge_sheets: number; events: number };
}
export interface NeedsAnIORow {
  case_no: string | null; fir_id: string; fir_number: string | null; title: string;
  stage: string; status: string; registered_at?: string | null;
}
export interface SHODashboardResult {
  ps_code: string;
  complaints: DashboardComplaint[];
  firs: DashboardFIR[];
  needs_an_io?: NeedsAnIORow[];
  summary: { open_complaints: number; open_firs: number; weak_complaints: number; weak_firs: number; needs_an_io?: number };
}
// Feature 006 — station view, station from the token (no path code). Old form kept for back-compat.
export const getSHODashboard = () =>
  httpFIR.get(`dashboard/ps`).json<SHODashboardResult>();

// Feature 006 — role-aware operations-dashboard home payload.
export interface CaseNextClock {
  code: string; label: string; due_at: string | null; days_left: number | null; status: string;
}
export interface DashboardCaseRow {
  case_no: string | null; fir_id: string; fir_number: string | null; title: string;
  stage: string; status: string; stage_detail_label?: string; ps_code?: string;
  next_clock: CaseNextClock | null; readiness_pct: number | null; section_strength_pct?: number | null;
  alerts: { kind: string; severity?: string; message?: string }[];
}
export interface DashboardViewer {
  officer_id: string; badge_no: string; rank: string; roles: string[];
  ps_code: string | null; ps_name: string | null;
  is_io: boolean; is_supervisor: boolean; misconfigured: boolean;
}
export interface DashboardHome {
  viewer: DashboardViewer;
  my_cases: DashboardCaseRow[];
  station: SHODashboardResult | null;
  clocks_summary: ClockChip[];
  cs_deadlines: { items: CSDeadlineRow[]; summary: { overdue: number; warn: number; on_track: number } };
  sos: { count: number; alerts: SOSAlert[] };
  incoming_transfers_count: number | null;
  kpi: Record<string, any>;
  links: { firs: string; my_cases: string; supervisor: string | null; reports: string; audit_trail: string; transfers_incoming: string };
  generated_at: string;
}
export const getDashboardHome = () => httpFIR.get(`dashboard/home`).json<DashboardHome>();

// Feature 006 phase 2 — PS-scoped daily activity for the dashboard throughput chart.
export interface DashboardTrendPoint {
  day: string; firs_registered: number; complaints_received: number; charge_sheets_filed: number;
}
export const getDashboardTrends = (days = 14) =>
  httpFIR.get(`dashboard/trends`, { searchParams: { days } })
    .json<{ days: number; ps_code: string | null; series: DashboardTrendPoint[] }>();

// Phase 5B — Serial-offender search
export interface SerialMatch {
  id: string; received_at: string; status: string;
  summary_en: string;
  complainant_name_en?: string; complainant_mobile?: string;
  suggested_sections?: string[];
  suspects_json?: any;
  converted_fir_id?: string | null;
}
export interface SerialSearchResult {
  query: { ps_code: string; days: number; name?: string; mobile?: string; vehicle_no?: string };
  matches: SerialMatch[];
  count: number;
}
export const serialOffenderSearch = (body: { ps_code: string; days?: number; name?: string; mobile?: string; vehicle_no?: string }) =>
  httpFIR.post(`dedup/serial-search`, { json: { days: 90, ...body } }).json<SerialSearchResult>();

export interface UploadResult {
  object_key: string; sha256: string; size_bytes: number; content_type?: string;
}
export async function uploadAttachment(file: File): Promise<UploadResult> {
  const fd = new FormData();
  fd.append("file", file);
  return httpFIR.post("uploads", { body: fd, timeout: 60_000 }).json<UploadResult>();
}
// Citizen-side, no-auth upload — accepts ANY file format. Backend stores in
// the same OBJ_DIR as the officer-facing /v1/uploads, so the returned
// object_key can be persisted in complaint.attachment_object_keys directly.
export async function uploadPublicAttachment(file: File): Promise<UploadResult> {
  const fd = new FormData();
  fd.append("file", file);
  return httpFIR.post("public/uploads", { body: fd, timeout: 120_000 }).json<UploadResult>();
}
export const fileGrievance = (body: any) =>
  httpFIR.post(`public/grievances`, { json: body }).json<{ id: string; tracking_code: string }>();
export const fileRTI = (body: any) =>
  httpFIR.post(`public/rti`, { json: body }).json<{ id: string; tracking_code: string }>();
export const trackByCode = (code: string) =>
  httpFIR.get(`public/track/${code}`).json<{ kind: string; id: string; status: string }>();

export interface CitizenLookupResult {
  found: boolean;
  citizen_id?: string;
  name_en?: string; name_mr?: string;
  mobile?: string; dob?: string; age?: number; gender?: string;
  address_en?: string; address_mr?: string;
  prior_complaints?: number;
  last_filed_at?: string | null;
}
export const citizenLookup = (mobile: string) =>
  httpFIR.get(`public/citizens/lookup`, { searchParams: { mobile } })
    .json<CitizenLookupResult>();

export const citizenFindOrCreate = (body: {
  mobile: string; name_en: string; name_mr?: string;
  dob?: string; gender?: string;
  address_en?: string; address_mr?: string;
  aadhaar_hash?: string;
}) => httpFIR.post(`public/citizens/find-or-create`, { json: body })
  .json<{ created: boolean; citizen_id: string; mobile: string }>();

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export const adminListOfficers = () => httpFIR.get(`admin/officers`).json<any[]>();
export const adminCreateOfficer = (body: any) => httpFIR.post(`admin/officers`, { json: body }).json<{ id: string }>();
export const adminListPS = () => httpFIR.get(`admin/police-stations`).json<any[]>();
export const adminListDevices = () => httpFIR.get(`admin/devices`).json<any[]>();
export const adminIntegrations = () => httpFIR.get(`admin/integrations`).json<any[]>();

// ---------------------------------------------------------------------------
// Aadhaar identity flow
// ---------------------------------------------------------------------------

export const aadhaarRequestOTP = (aadhaar_or_vid: string) =>
  httpFIR.post(`identity/aadhaar/otp`, { json: { aadhaar_or_vid, purpose: "fir_complainant_identity" } })
    .json<{ txn_id: string; sent_to_mobile_last4: string; expires_at_ms: number }>();

export const aadhaarVerifyOTP = (txn_id: string, aadhaar_or_vid: string, otp: string) =>
  httpFIR.post(`identity/aadhaar/verify`, { json: { txn_id, aadhaar_or_vid, otp } })
    .json<{ success: boolean; err_code: string | null; aadhaar_hash: string;
            name: { en: string; mr: string }; dob: string; gender: string;
            address: { en: string; mr: string } }>();

// ---------------------------------------------------------------------------
// Cases service (complaints/diary/MLC/FSL/malkhana/court/alerts/audit/config/roles/search)
// ---------------------------------------------------------------------------

export interface ComplaintListFilter {
  status?: string;
  channel?: string;
  sensitivity?: string;
  overdue?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}
export const listComplaints = (f: ComplaintListFilter | string = {}) => {
  // Back-compat: old callers pass a status string directly.
  const params: Record<string, any> = typeof f === "string"
    ? (f ? { status: f } : {})
    : Object.fromEntries(Object.entries(f).filter(([_, v]) => v !== undefined && v !== "" && v !== false));
  return httpFIR.get(`complaints`, { searchParams: params }).json<any[]>();
};

export interface ComplaintKPIs {
  total: number; open: number; overdue: number; decided_today: number;
  converted_to_fir: number; disposed_as_nc: number; compromised: number;
  rejected: number; pocso: number; women: number; sc_st_poa: number;
}
export const complaintKPIs = () =>
  httpFIR.get(`complaints/kpis`).json<ComplaintKPIs>();
export const getComplaint = (id: string) => httpFIR.get(`complaints/${id}`).json<any>();
export const triageComplaint = (id: string, status: string, note?: string) =>
  httpFIR.post(`complaints/${id}/triage`, { json: { status, note } }).json<{ ok: boolean }>();

// Post-submit inline edit. Send only the keys you want to change. Backend
// returns 409 once the Decision Gate has fired (BNSS audit chain).
export interface ComplaintPatch {
  summary_en?: string; summary_mr?: string;
  complainant_name_en?: string; complainant_name_mr?: string;
  complainant_mobile?: string;  complainant_email?: string;
  complainant_age?: number;     complainant_gender?: string;
  complainant_address_en?: string; complainant_address_mr?: string;
  complainant_id_type?: string;    complainant_id_number?: string;
  incident_at?: string;
  incident_place_en?: string; incident_place_mr?: string;
  incident_lat?: number;      incident_lng?: number;
  loss_value_inr?: number;
  suggested_sections?: string[];
  sensitivity_flags?:  string[];
  cognisable?: boolean;
  witnesses_json?: any[]; suspects_json?: any[];
}
export const patchComplaint = (id: string, body: ComplaintPatch) =>
  httpFIR.patch(`complaints/${id}`, { json: body }).json<{
    ok: boolean; complaint: any; fields_changed: string[];
  }>();

// BNSS Decision Gate — see backend cases/main.py decide_complaint().
export interface DecisionInput {
  decision: "CONVERT" | "NC" | "COMPROMISE" | "REJECT";
  reason: string;
  sections_csv?: string;
  party_a_name?: string;
  party_b_name?: string;
  party_b_mobile?: string;     // Sprint #5 — drives a 2nd SMS to the respondent
  terms_en?: string;
  terms_mr?: string;
}
export interface DecisionResult {
  ok: boolean;
  decision: string;
  gd_no?: string;
  fir_id?: string;
  fir_number?: string;
  ncr_id?: string;
  ncr_no?: string;
  compromise_deed_id?: string;
}
export const decideComplaint = (id: string, body: DecisionInput) =>
  httpFIR.post(`complaints/${id}/decide`, { json: body, timeout: 30_000 })
    .json<DecisionResult>();

// Pre-FIR inquiry events (spot visit / informal statement / accused contact)
export interface InquiryEvent {
  id: string; complaint_id: string; kind: string; happened_at: string;
  body_en: string; body_mr: string;
  person_name?: string | null; person_mobile?: string | null;
  place_en?: string | null; place_mr?: string | null;
  lat?: number | null; lng?: number | null; recorded_by?: string | null;
}
export const listInquiryEvents = (cid: string) =>
  httpFIR.get(`complaints/${cid}/inquiry-events`).json<InquiryEvent[]>();
export const addInquiryEvent = (cid: string, body: Partial<InquiryEvent> & {
  kind: "SPOT_VISIT" | "INFORMAL_STATEMENT" | "ACCUSED_CONTACT" | "OTHER";
  body_en: string; body_mr: string;
}) => httpFIR.post(`complaints/${cid}/inquiry-events`, { json: body })
        .json<InquiryEvent>();

export const listOverdueInquiries = () =>
  httpFIR.get(`complaints/inquiry-overdue`).json<any[]>();

// ---------------------------------------------------------------------------
// Dedup service — find duplicate complaints / FIRs (port 8015 via nginx).
// ---------------------------------------------------------------------------
export interface DedupReason {
  kind: "mobile_match" | "geo_match" | "time_match" | "section_match" | "text_similarity";
  weight: number;
  detail: string;
}
export interface DedupCandidate {
  id: string;
  kind: "complaint" | "fir";
  number: string | null;
  score: number;
  summary_en: string;
  summary_mr: string;
  received_at: string | null;
  reasons: DedupReason[];
}
export const complaintDuplicates = (cid: string) =>
  httpFIR.get(`complaints/${cid}/duplicates`).json<DedupCandidate[]>();
export const firDuplicates = (fid: string) =>
  httpFIR.get(`firs/${fid}/duplicates`).json<DedupCandidate[]>();
export interface DedupMatchInput {
  summary_en?: string; summary_mr?: string;
  complainant_mobile?: string;
  incident_at?: string;
  incident_lat?: number; incident_lng?: number;
  suggested_sections?: string[];
}
export const dedupMatch = (body: DedupMatchInput) =>
  httpFIR.post(`dedup/match`, { json: body, timeout: 10_000 })
    .json<DedupCandidate[]>();

// Case Diary (024 — BNSS §192 dynamic flow)
export type CaseDiaryKind =
  | "FIR_REGISTERED" | "RECEIPT_INFO" | "DEPARTURE" | "ARRIVAL"
  | "FIELD_VISIT" | "SPOT_INSPECTION" | "PANCHANAMA_PREPARED"
  | "WITNESS_EXAMINED" | "S180_RECORDED" | "S183_RECORDED"
  | "EVIDENCE_SEIZED" | "ARREST_RECORDED"
  | "PRODUCED_BEFORE_MAGISTRATE" | "REMAND_EXTENDED"
  | "MLC_REQUEST" | "MLC_EXAMINED" | "MLC_REPORT_RECEIVED"
  | "FSL_REQUEST" | "FSL_ACK" | "FSL_IN_PROGRESS" | "FSL_RESULT_RECEIVED"
  | "SEARCH_APPLIED" | "SEARCH_EXECUTED" | "SEARCH_OUTCOME"
  | "NOTICE_S35" | "NOTICE_S175"
  | "CS_DRAFTED" | "CS_FILED"
  | "COURT_FILING_ACKED" | "COURT_HEARING" | "COURT_ORDER_RECEIVED"
  | "COURT_PROPERTY_ORDER" | "COURT_JUDGEMENT"
  | "BAIL_APPLICATION" | "BAIL_DECISION"
  | "PROCESS_DOC_RECEIVED" | "PROCESS_DOC_SERVED"
  | "WANTED_FLAGGED" | "WANTED_LIFTED"
  | "INTERROGATION_NOTE" | "CUSTODIAL_NOTE"
  | "REPORT_TO_SP" | "DSR_FILED"
  | "SHO_INSPECTION" | "HANDOVER" | "EOD_ROLLUP"
  | "CORRECTION" | "PRODUCED_IN_COURT_S192" | "OTHER";

export interface DiaryEntryInput {
  body_en: string;
  body_mr?: string;
  kind?: CaseDiaryKind;
  ref_kind?: string;
  ref_id?: string;
  place_text?: string;
  gps_lat?: number;
  gps_lng?: number;
  period_from?: string;
  period_to?: string;
  attachment_object_keys?: string[];
  audio_object_key?: string;
  flags?: string[];
}

export const listDiary = (firId: string, filters: { kind?: string; ref_kind?: string; q?: string } = {}) => {
  const sp: Record<string, string> = {};
  if (filters.kind) sp.kind = filters.kind;
  if (filters.ref_kind) sp.ref_kind = filters.ref_kind;
  if (filters.q) sp.q = filters.q;
  return httpFIR.get(`firs/${firId}/diary`, { searchParams: sp }).json<any[]>();
};
export const addDiary = (firId: string, body: DiaryEntryInput | string, mr?: string) => {
  const payload: DiaryEntryInput = typeof body === "string"
    ? { body_en: body, body_mr: mr ?? "" }   // legacy two-arg call site compat
    : body;
  return httpFIR.post(`firs/${firId}/diary`, { json: payload }).json<any>();
};
export const supersedeDiary = (eid: string, body: { reason: string; body_en: string; body_mr?: string }) =>
  httpFIR.patch(`diary/${eid}/supersede`, { json: body }).json<any>();
export const inspectDiary = (firId: string, body: { note?: string } = {}) =>
  httpFIR.post(`firs/${firId}/diary/inspection`, { json: body }).json<any>();
export const listDiaryInspections = (firId: string) =>
  httpFIR.get(`firs/${firId}/diary/inspections`).json<any[]>();

export interface DiaryMineRow {
  id: string;
  fir_number: string | null;
  ps_code: string;
  registered_at: string;
  fir_status: string;
  summary_en: string | null;
  sections: string[];
  io_name: string | null;
  io_rank: string | null;
  io_badge: string | null;
  cs_number: string | null;
  cs_status: string | null;
  last_entry_at: string | null;
  last_kind: string | null;
  last_body_en: string | null;
  last_auto_fed: boolean | null;
  days_since_last_entry: number | null;
  total_entries: number;
  auto_entries: number;
  manual_entries: number;
  mlc_pending: number;
  fsl_pending: number;
  process_doc_pending: number;
  next_court_hearing_at: string | null;
  is_stale: boolean;
}

export const listDiaryMine = (scope: "mine"|"ps"|"all"|"auto" = "auto", stale_only = false) =>
  httpFIR.get(`diary/mine`, { searchParams: { scope, stale_only: stale_only ? "true" : "false" } })
    .json<{
      scope: string;
      stale_days_threshold: number;
      total: number;
      stale_count: number;
      rows: DiaryMineRow[];
    }>();

// ---------------------------------------------------------------------------
// Phase 5 registers (Station Diary, Missing, Lost&Found, Wanted, Process, Bail, Verify)
// ---------------------------------------------------------------------------

export const listSD = (opts?: { kind?: string; date?: string }) => {
  const sp: Record<string, string> = {};
  if (opts?.kind) sp.kind = opts.kind;
  if (opts?.date) sp.date = opts.date;
  return httpFIR.get(`registers/sd`, { searchParams: sp }).json<any[]>();
};
export const createSD   = (body: any) => httpFIR.post(`registers/sd`, { json: body }).json<{ id: string; sd_no: string }>();
// File a CORRECTION entry against a GD entry (the original is never edited).
export const correctSD  = (id: string, body: { body_en: string; body_mr: string; reason: string }) =>
  httpFIR.post(`registers/sd/${id}/correct`, { json: body }).json<{ id: string; sd_no: string; corrects: string }>();
// Re-walk the tamper-evident GD hash chain for this PS.
export const verifySDChain = () =>
  httpFIR.get(`registers/sd/verify`).json<{ ok: boolean; n: number; first_break_seq?: number; first_break_sd_no?: string; reason?: string }>();
// Open / close the day's General Diary (idempotent) — writes the OPENING / CLOSING entry.
export const openSDDay  = (date?: string) =>
  httpFIR.post(`registers/sd/open-day`, { searchParams: date ? { date } : {} }).json<{ id: string; sd_no: string; already_open: boolean }>();
export const closeSDDay = (date?: string) =>
  httpFIR.post(`registers/sd/close-day`, { searchParams: date ? { date } : {} }).json<{ id: string; sd_no: string; already_closed: boolean }>();
// Printable Daily General Diary register (B-02) for a date — .docx (default) or .pdf.
export const dailyGDUrl = (date: string, format: "docx" | "pdf" = "docx") =>
  withAuthQS(`/v1/registers/sd/print?date=${encodeURIComponent(date)}${format === "pdf" ? "&format=pdf" : ""}`);

export const listMissing   = (status?: string) => httpFIR.get(`registers/missing`, { searchParams: status ? { status } : {} }).json<any[]>();
export const createMissing = (body: any) => httpFIR.post(`registers/missing`, { json: body }).json<{ id: string; register_no: string }>();
export const updateMissingStatus = (id: string, status: string, note?: string) =>
  httpFIR.patch(`registers/missing/${id}/status`, { json: { status, note } }).json<{ ok: boolean }>();

// ---- spec 019 missing-persons-dynamic wrappers ----
export interface MissingTracingEvent {
  id: string; kind: string; note_en?: string | null; note_mr?: string | null;
  doc_key?: string | null; recorded_at: string;
}
export interface MissingMatchCandidate {
  id: string; lf_id: string; score: number; kind: string; status: string;
  reason_json?: Record<string, unknown>; confirmed_at?: string | null;
  confirmation_method?: string | null;
}
export interface MissingDetail {
  id: string; register_no: string; ps_code: string;
  reported_at: string;
  name_en: string; name_mr: string; age?: number | null; gender?: string | null;
  last_seen_at?: string | null; last_seen_place_en?: string | null; last_seen_place_mr?: string | null;
  descriptors_en?: string | null; descriptors_mr?: string | null;
  informant_name?: string | null; informant_mobile?: string | null; informant_relation?: string | null;
  status: string; fir_id?: string | null;
  traced_at?: string | null; traced_note?: string | null;
  vulnerable_category: string[];
  supervisor_notified_at?: string | null; supervisor_acknowledged_at?: string | null;
  cctns_sync_status: string; cctns_reference?: string | null; cctns_synced_at?: string | null;
  poster_doc_key?: string | null;
  is_zero_missing: boolean; transferred_to_ps_code?: string | null;
  tracing_events: MissingTracingEvent[];
  match_candidates: MissingMatchCandidate[];
}
export const getMissing = (id: string) =>
  httpFIR.get(`registers/missing/${id}`).json<MissingDetail>();

export const escalateMissingToFir = (id: string, body: { sections?: string[]; note?: string } = {}) =>
  httpFIR.post(`registers/missing/${id}/escalate-to-fir`, { json: body }).json<{
    ok: boolean; fir_id: string; fir_number?: string;
  }>();

export const recordMissingTracingEvent = (id: string, body: {
  kind: "CIRCULATED"|"BROADCAST"|"HOSPITAL_CHECK"|"MORGUE_CHECK"|"MEDIA_PUBLISHED"|"FAMILY_CONTACTED"|"OTHER";
  note_en?: string; note_mr?: string; doc_key?: string;
}) => httpFIR.post(`registers/missing/${id}/tracing-event`, { json: body }).json<{ event_id: string }>();

export const refreshMissingMatchCandidates = (id: string) =>
  httpFIR.post(`registers/missing/${id}/match-candidates/refresh`, { json: {} }).json<{
    candidates: MissingMatchCandidate[]; threshold: number;
  }>();

export const decideMissingMatchCandidate = (id: string, cid: string, body: {
  status: "CONFIRMED" | "RULED_OUT"; confirmation_method?: string;
}) => httpFIR.patch(`registers/missing/${id}/match-candidates/${cid}`, { json: body }).json<{
  ok: boolean; candidate_status: string;
}>();

export const missingPosterUrl = (id: string) => {
  const base = (import.meta as any).env?.VITE_FIR_BASE || "/v1";
  return `${base}/registers/missing/${id}/poster.pdf`;
};

export const circulateMissingPoster = (id: string, body: { target_ps_codes: string[]; note?: string }) =>
  httpFIR.post(`registers/missing/${id}/circulate`, { json: body }).json<{
    circulated_to: string[]; invalid: string[]; count: number;
  }>();

export const transferMissing = (id: string, body: { target_ps_code: string; note?: string; force?: boolean }) =>
  httpFIR.post(`registers/missing/${id}/transfer`, { json: body }).json<{ ok: boolean; target_ps_code: string }>();

export const acceptMissingTransfer = (id: string) =>
  httpFIR.post(`registers/missing/${id}/transfer/accept`, { json: {} }).json<{ ok: boolean; ps_code: string }>();

export const listMissingTransferInbox = (params: { limit?: number; offset?: number } = {}) =>
  httpFIR.get(`registers/missing/inbox/transfer`, { searchParams: params as Record<string, any> }).json<{
    items: any[]; total: number;
  }>();

export const listLostFound   = (status?: string) => httpFIR.get(`registers/lost-found`, { searchParams: status ? { status } : {} }).json<any[]>();
export const createLostFound = (body: any) => httpFIR.post(`registers/lost-found`, { json: body }).json<{ id: string; register_no: string }>();

// ---- spec 020 lost-found-dynamic wrappers ----
export interface LFFirMatchCandidate {
  id: string; fir_id: string; score: number;
  kind: string; status: string;
  reason_json?: Record<string, unknown>;
  confirmed_at?: string | null;
  note?: string | null;
}
export interface LFDisposal {
  id: string; kind: string;
  initiated_at: string;
  sho_approved_at?: string | null;
  court_order_no?: string | null;
  court_order_date?: string | null;
  court_name?: string | null;
  deed_doc_key?: string | null;
  status: string;
  denied_reason?: string | null;
}
export interface LFDetail {
  id: string; register_no: string; ps_code: string;
  found_at: string;
  found_place_en: string; found_place_mr: string;
  article_en: string; article_mr: string; article_kind: string;
  estimated_value_inr?: number | null;
  finder_name?: string | null; finder_mobile?: string | null;
  custody_location?: string | null;
  status: string;
  claimant_name?: string | null; claimant_mobile?: string | null;
  claimant_id_proof_kind?: string | null; claimant_id_proof_number?: string | null;
  claimant_citizen_id?: string | null;
  returned_at?: string | null; return_note?: string | null;
  return_deed_doc_key?: string | null;
  evidence_id?: string | null; linked_fir_id?: string | null; linked_missing_id?: string | null;
  photo_object_key?: string | null;
  is_perishable: boolean;
  finder_reward_inr?: number | null; reward_paid_at?: string | null;
  disposal_due_at?: string | null;
  is_zero_lf: boolean; transferred_to_ps_code?: string | null;
  fir_match_candidates: LFFirMatchCandidate[];
  disposal?: LFDisposal | null;
  missing_link?: { id: string; register_no: string; name_en: string } | null;
}

export const getLF = (id: string) =>
  httpFIR.get(`registers/lost-found/${id}`).json<LFDetail>();

export const claimLF = (id: string, body: {
  claimant_name: string; claimant_mobile?: string;
  claimant_id_proof_kind?: "AADHAAR"|"PAN"|"VOTER"|"DRIVING"|"OTHER";
  claimant_id_proof_number?: string;
  claimant_id_proof_photo_object_key?: string;
  claimant_citizen_id?: string;
  note?: string;
}) => httpFIR.post(`registers/lost-found/${id}/claim`, { json: body }).json<{ ok: boolean }>();

export const returnLF = (id: string, body: {
  return_note?: string;
  return_deed_signature_object_key?: string;
}) => httpFIR.post(`registers/lost-found/${id}/return`, { json: body }).json<{ ok: boolean; return_deed_doc_key?: string }>();

export const lfReturnDeedUrl = (id: string) => {
  const base = (import.meta as any).env?.VITE_FIR_BASE || "/v1";
  return `${base}/registers/lost-found/${id}/return-deed.pdf`;
};

export const revalueLF = (id: string, body: { estimated_value_inr: number; note?: string }) =>
  httpFIR.post(`registers/lost-found/${id}/revalue`, { json: body }).json<{ ok: boolean }>();

export const refreshLFFirMatches = (id: string) =>
  httpFIR.post(`registers/lost-found/${id}/fir-match-candidates/refresh`, { json: {} }).json<{
    candidates: LFFirMatchCandidate[]; threshold: number;
  }>();

export const decideLFFirMatch = (id: string, cid: string, body: {
  status: "CONFIRMED" | "RULED_OUT"; note?: string;
}) => httpFIR.patch(`registers/lost-found/${id}/fir-match-candidates/${cid}`, { json: body }).json<{
  ok: boolean; candidate_status: string;
}>();

export const listLFDisposalEligible = (params: { limit?: number; offset?: number } = {}) =>
  httpFIR.get("registers/lost-found/disposal-eligible", { searchParams: params as Record<string, any> }).json<{
    items: any[]; total?: number;
  }>();

export const initiateLFDisposal = (id: string, body: {
  kind: "AUCTION"|"DESTRUCTION"|"DONATION"|"PERISHABLE_DESTRUCTION";
  court_order_no?: string; court_order_date?: string; court_name?: string; note?: string;
}) => httpFIR.post(`registers/lost-found/${id}/disposal/initiate`, { json: body }).json<{
  disposal_id: string; status: string;
}>();

export const approveLFDisposal = (id: string) =>
  httpFIR.post(`registers/lost-found/${id}/disposal/approve`, { json: {} }).json<{
    ok: boolean; deed_doc_key?: string; status: string;
  }>();

export const denyLFDisposal = (id: string, reason: string) =>
  httpFIR.post(`registers/lost-found/${id}/disposal/deny`, { json: { reason } }).json<{
    ok: boolean; status: string;
  }>();

export const listWanted   = (status?: string) => httpFIR.get(`registers/wanted`, { searchParams: status ? { status } : {} }).json<any[]>();
export const createWanted = (body: any) => httpFIR.post(`registers/wanted`, { json: body }).json<{ id: string; register_no: string }>();

// ---- spec 021 wanted/absconders dynamic wrappers ----
export type WantedClearKind =
  | "ARRESTED" | "SURRENDERED" | "WRONG_IDENTITY"
  | "DECEASED" | "WITHDRAWN_BY_IO" | "TIME_BARRED";

export type WantedRewardRole = "INFORMANT" | "ARRESTING_OFFICER" | "TIP" | "OTHER";

export type WantedS85Outcome =
  | "pending" | "realised" | "restored"
  | "recovered" | "sold" | "restored_after_surrender";

export interface WantedProclamation {
  id: string;
  court_order_ref?: string | null;
  published_at?: string | null;
  appearance_due_at?: string | null;
  note?: string | null;
}

export interface WantedSurrenderRow {
  id: string;
  surrendered_at?: string | null;
  magistrate_court_id?: string | null;
  magistrate_name?: string | null;
  surrender_note_en?: string | null;
  surrender_note_mr?: string | null;
  certificate_doc_key?: string | null;
}

export interface WantedClearanceRow {
  id: string; kind: WantedClearKind;
  note_en?: string | null; note_mr?: string | null;
  evidence_doc_key?: string | null;
  signed_at?: string | null;
}

export interface WantedS85Attachment {
  id: string;
  court_order_ref?: string | null;
  desc_en?: string | null;
  desc_mr?: string | null;
  location?: string | null;
  attached_at?: string | null;
  outcome?: WantedS85Outcome | string | null;
  outcome_at?: string | null;
  note?: string | null;
}

export interface WantedArrestCandidate {
  id: string;
  arrest_record_id: string;
  score?: number | null;
  status: "PENDING_REVIEW" | "CONFIRMED" | "RULED_OUT" | string;
  reason_json?: Record<string, unknown> | null;
  confirmed_at?: string | null;
  note?: string | null;
}

export interface WantedRewardPayment {
  id: string;
  recipient_name: string;
  recipient_mobile?: string | null;
  recipient_role: WantedRewardRole | string;
  amount_inr: number;
  treasury_ref?: string | null;
  voucher_doc_key?: string | null;
  paid_at?: string | null;
  paid_by?: string | null;
  note?: string | null;
  canonical_sha256?: string | null;
}

export interface WantedRewardSummary {
  announced: number;
  paid_so_far: number;
  remaining: number;
  items: WantedRewardPayment[];
}

export interface WantedDetail {
  id: string; register_no: string; ps_code: string;
  name_en: string; name_mr: string;
  aliases: string[];
  age?: number | null; gender?: string | null;
  descriptors_en?: string | null; descriptors_mr?: string | null;
  related_fir_id?: string | null;
  status: "WANTED" | "ABSCONDER" | "ARRESTED" | "CLEARED" | "SURRENDERED" | string;
  bnss_84_declared: boolean;
  declared_at?: string | null;
  appearance_due_at?: string | null;
  proclamation_id?: string | null;
  proclamation_doc_key?: string | null;
  arrest_record_id?: string | null;
  reward_inr?: number | null;
  last_known_place_en?: string | null;
  last_known_place_mr?: string | null;
  cctns_reference?: string | null;
  cctns_sync_status?: string | null;
  cctns_synced_at?: string | null;
  transferred_to_ps_code?: string | null;
  is_zero_wanted: boolean;
  transfer_received_at?: string | null;
  supervisor_notified_at?: string | null;
  severity?: "HIGH" | "MEDIUM" | "LOW" | null;
  cleared_at?: string | null;
  created_at?: string | null;
  proclamation?: WantedProclamation | null;
  surrender?: WantedSurrenderRow | null;
  clearance?: WantedClearanceRow | null;
  s85_attachments: WantedS85Attachment[];
  arrest_match_candidates: WantedArrestCandidate[];
  reward: WantedRewardSummary;
}

export const wantedGet = (id: string) =>
  httpFIR.get(`registers/wanted/${id}`).json<WantedDetail>();

export const wantedDeclareAbsconder = (id: string, body: {
  court_order_ref: string; appearance_due_date: string;
}) => httpFIR.post(`registers/wanted/${id}/declare-absconder`, { json: body }).json<any>();

export const wantedClear = (id: string, body: {
  kind: WantedClearKind;
  note_en?: string; note_mr?: string;
  evidence_doc_key?: string;
}) => httpFIR.post(`registers/wanted/${id}/clear`, { json: body }).json<any>();

export const wantedSurrender = (id: string, body: {
  magistrate_court_id?: string;
  magistrate_name?: string;
  surrender_note_en: string;
  surrender_note_mr?: string;
}) => httpFIR.post(`registers/wanted/${id}/surrender`, { json: body }).json<any>();

export const wantedRefreshArrestMatch = (id: string) =>
  httpFIR.post(`registers/wanted/${id}/arrest-match-candidates/refresh`, { json: {} }).json<{
    candidates: WantedArrestCandidate[]; threshold?: number;
  }>();

export const wantedUpdateArrestMatch = (id: string, cid: string, body: {
  status: "CONFIRMED" | "RULED_OUT"; note?: string;
}) => httpFIR.patch(`registers/wanted/${id}/arrest-match-candidates/${cid}`, { json: body }).json<any>();

export const wantedS85Initiate = (id: string, body: {
  court_order_ref: string;
  properties: { desc_en: string; desc_mr: string; location: string }[];
}) => httpFIR.post(`registers/wanted/${id}/attachment-of-property/initiate`, { json: body }).json<any>();

export const wantedS85UpdateOutcome = (id: string, aid: string, body: {
  outcome: WantedS85Outcome | string;
  outcome_at?: string;
  note?: string;
}) => httpFIR.patch(`registers/wanted/${id}/attachment-of-property/${aid}`, { json: body }).json<any>();

export const wantedS85List = (id: string) =>
  httpFIR.get(`registers/wanted/${id}/attachment-of-property`).json<{
    proclamation_id: string | null; items: WantedS85Attachment[];
  }>();

export const wantedRewardPay = (id: string, body: {
  recipient_name: string;
  recipient_mobile?: string;
  recipient_role: WantedRewardRole;
  amount_inr: number;
  treasury_ref?: string;
  note?: string;
}) => httpFIR.post(`registers/wanted/${id}/reward/pay`, { json: body }).json<any>();

export const wantedRewardList = (id: string) =>
  httpFIR.get(`registers/wanted/${id}/reward/payments`).json<WantedRewardSummary & {
    wanted_id: string; register_no: string;
  }>();

export const wantedTransferInitiate = (id: string, body: {
  target_ps_code: string; reason: string;
}) => httpFIR.post(`registers/wanted/${id}/transfer`, { json: body }).json<any>();

export const wantedTransferAccept = (id: string) =>
  httpFIR.post(`registers/wanted/${id}/transfer/accept`, { json: {} }).json<any>();

export const wantedInboxTransfer = (params: { limit?: number; offset?: number } = {}) =>
  httpFIR.get(`registers/wanted/inbox/transfer`, { searchParams: params as any }).json<{
    items: any[]; total?: number;
  }>();

export const wantedCctnsSync = (id: string) =>
  httpFIR.post(`registers/wanted/${id}/cctns-sync`, { json: {} }).json<any>();

export const wantedSearch = (params: {
  name: string; ps_code?: string; threshold?: number;
}) => httpFIR.get(`registers/wanted/search`, { searchParams: params as any }).json<any[]>();

export const wantedProclamationNoticeUrl = (id: string) => {
  const base = (import.meta as any).env?.VITE_FIR_BASE || "/v1";
  return `${base}/registers/wanted/${id}/proclamation-notice.pdf`;
};

export const wantedSurrenderCertificateUrl = (id: string) => {
  const base = (import.meta as any).env?.VITE_FIR_BASE || "/v1";
  return `${base}/registers/wanted/${id}/surrender-certificate.pdf`;
};

export const wantedRewardVoucherUrl = (id: string, pid: string) => {
  const base = (import.meta as any).env?.VITE_FIR_BASE || "/v1";
  return `${base}/registers/wanted/${id}/reward/voucher/${pid}.pdf`;
};

export const listProcess   = (params: { status?: string; kind?: string; mine?: boolean } = {}) =>
  httpFIR.get(`registers/process`, { searchParams: params as any }).json<any[]>();
export const listMyProcessDocs = () => listProcess({ mine: true });
export const createProcess = (body: any) => httpFIR.post(`registers/process`, { json: body }).json<{ id: string; register_no: string; canonical_sha256?: string }>();
// Feature 009 — the per-document work view (record + event ledger + linked FIR + next actions).
export const getProcessDoc = (id: string) => httpFIR.get(`registers/process/${id}`).json<any>();
export const serveProcess  = (id: string, body: {
  served: boolean; mode_of_service?: string; person_served_or_witness?: string; place?: string;
  served_note?: string; not_served_reason?: string; attachment_object_keys?: string[];
}) => httpFIR.patch(`registers/process/${id}/serve`, { json: body }).json<any>();
export const executeProcess = (id: string, body: {
  outcome: "executed" | "not_executed" | "cancelled"; place?: string; executing_officer_id?: string;
  arrest_record_id?: string; arrest_production_id?: string; reason?: string; court_recall_ref?: string;
  note?: string; attachment_object_keys?: string[];
}) => httpFIR.post(`registers/process/${id}/execute`, { json: body }).json<any>();
export const correctProcess = (id: string, body: {
  supersedes_event_id: string; corrected_kind: string; reason: string; note?: string;
  mode_of_service?: string; person_served_or_witness?: string; place?: string;
  executing_officer_id?: string; arrest_record_id?: string; not_done_reason?: string; court_ref?: string;
  attachment_object_keys?: string[];
}) => httpFIR.post(`registers/process/${id}/correct`, { json: body }).json<any>();
export const assignProcess  = (id: string, body: { officer_id: string; target_date?: string; note?: string }) =>
  httpFIR.post(`registers/process/${id}/assign`, { json: body }).json<any>();
export const extendProcessDue = (id: string, body: { new_due_date: string; reason: string }) =>
  httpFIR.patch(`registers/process/${id}/extend-due`, { json: body }).json<any>();
export const runProcessSlaSweep = () => httpFIR.post(`registers/process/sla-sweep`).json<{ enabled: boolean; reminded: number; escalated: number }>();
export const initiateProclamation = (id: string, body: { court_order_ref?: string; published_at: string; note?: string }) =>
  httpFIR.post(`registers/process/${id}/proclamation`, { json: body }).json<any>();
export const recordAttachment = (proclamationId: string, body: {
  court_order_ref?: string; property_desc_en?: string; property_desc_mr?: string; property_location?: string; attached_at?: string; note?: string;
}) => httpFIR.post(`registers/process/proclamations/${proclamationId}/attachment`, { json: body }).json<any>();
export const setAttachmentOutcome = (attachmentId: string, body: { outcome: "realised" | "restored"; outcome_at?: string; note?: string }) =>
  httpFIR.patch(`registers/process/attachments/${attachmentId}/outcome`, { json: body }).json<any>();
export const rerouteProcess = (id: string, body: { to_ps_code: string; reason: string }) =>
  httpFIR.post(`registers/process/${id}/reroute`, { json: body }).json<any>();

export const listBail   = (status?: string) => httpFIR.get(`registers/bail`, { searchParams: status ? { status } : {} }).json<any[]>();
export const createBail = (body: any) => httpFIR.post(`registers/bail`, { json: body }).json<{ id: string; register_no: string }>();

export const listVerify   = (params: { status?: string; kind?: string } = {}) =>
  httpFIR.get(`registers/verify`, { searchParams: params as any }).json<any[]>();
export const createVerify = (body: any) => httpFIR.post(`registers/verify`, { json: body }).json<{ id: string; register_no: string }>();

// ── Spec 022 — Verification lifecycle wrappers ────────────────────────────

export interface VerifAdverseSignal {
  type: string;
  ref: string;
  severity: string;
  detail?: string | null;
}

export interface VerifSweepResult {
  id: string;
  has_adverse: boolean;
  signals_json: VerifAdverseSignal[] | null;
  computed_at: string;
  computed_by: string | null;
}

export interface VerifDecision {
  id: string;
  decision: "CLEARED" | "ADVERSE" | "REJECTED";
  decision_note_en: string;
  decision_note_mr?: string | null;
  override_reason_en?: string | null;
  certificate_doc_key?: string | null;
  decided_by: string;
  decided_at: string;
  canonical_sha256?: string | null;
}

export interface VerifSupersede {
  id: string;
  superseded_by_verification_id?: string | null;
  reason_en: string;
  reason_mr?: string | null;
  recorded_by: string;
  recorded_at: string;
}

export interface VerifRequest {
  id: string;
  ps_code: string;
  register_no: string;
  kind: string;
  received_at: string;
  applicant_name_en: string;
  applicant_name_mr?: string | null;
  applicant_mobile?: string | null;
  applicant_address?: string | null;
  aadhaar_hash?: string | null;
  purpose?: string | null;
  requesting_authority?: string | null;
  assigned_to?: string | null;
  field_visit_at?: string | null;
  findings_en?: string | null;
  findings_mr?: string | null;
  status: string;
  decided_at?: string | null;
  entered_by: string;
  priority?: "HIGH" | "MEDIUM" | "LOW" | null;
  sla_due_at?: string | null;
  applicant_father_name_en?: string | null;
  applicant_father_name_mr?: string | null;
  supervisor_notified_at?: string | null;
}

export interface VerifDetail {
  request: VerifRequest;
  decision: VerifDecision | null;
  supporting_docs: Array<{
    id: string; doc_key: string; label?: string | null;
    kind: string; uploaded_by: string; uploaded_at: string;
  }>;
  latest_sweep: VerifSweepResult | null;
  supersede: VerifSupersede | null;
}

export const verifyGet = (id: string) =>
  httpFIR.get(`registers/verify/${id}`).json<VerifDetail>();

export const verifyAssign = (id: string, body: { assigned_to_officer_id: string }) =>
  httpFIR.post(`registers/verify/${id}/assign`, { json: body }).json<any>();

export const verifyFieldVisit = (id: string, body: {
  field_visit_at: string;
  findings_en: string;
  findings_mr?: string;
  supporting_doc_keys?: string[];
}) => httpFIR.post(`registers/verify/${id}/field-visit`, { json: body }).json<any>();

export const verifyDecide = (id: string, body: {
  decision: "CLEARED" | "ADVERSE" | "REJECTED";
  decision_note_en: string;
  decision_note_mr?: string;
  override_reason_en?: string;
}) => httpFIR.post(`registers/verify/${id}/decide`, { json: body }).json<any>();

export const verifySweep = (id: string) =>
  httpFIR.post(`registers/verify/${id}/sweep`, { json: {} }).json<any>();

export const verifySupersede = (id: string, body: {
  superseded_by_verification_id?: string;
  reason_en: string;
  reason_mr?: string;
}) => httpFIR.post(`registers/verify/${id}/supersede`, { json: body }).json<any>();

export const verifyAadhaarOtpInitiate = (id: string, body: { uid: string }) =>
  httpFIR.post(`registers/verify/${id}/aadhaar-otp/initiate`, { json: body }).json<{ otp_session_id: string }>();

export const verifyAadhaarOtpVerify = (id: string, body: { otp_session_id: string; otp: string }) =>
  httpFIR.post(`registers/verify/${id}/aadhaar-otp/verify`, { json: body }).json<any>();

export const verifyCertificatePdfUrl = (id: string) =>
  `${API_BASE}/registers/verify/${id}/verification-certificate.pdf`;

export const verifyKpis = () =>
  httpFIR.get(`kpis/verify`).json<any>();

export const verifyTrack = (ref: string) =>
  httpFIR.get(`citizen/verify/track/${ref}`).json<any>();

// ── Spec 023 — Bail lifecycle typed wrappers ──────────────────────────────

export type BailStatus =
  | "PENDING" | "GRANTED" | "REFUSED"
  | "BOND_EXECUTED" | "BREACHED" | "CLEARED" | "CANCELLED";

export type BailWarningThreshold = "GREEN" | "AMBER" | "RED";

export type BailAuthority =
  | "PS_OFFICER" | "MAGISTRATE" | "SESSIONS_COURT" | "HIGH_COURT" | "SUPREME_COURT";

export type BailConditionKind =
  | "REPORT_DAILY" | "REPORT_WEEKLY" | "SURRENDER_PASSPORT"
  | "NO_LEAVE_JURISDICTION" | "NO_CONTACT_WITNESS" | "NO_INTOXICANTS"
  | "MEDICAL_CHECKIN" | "RESIDE_AT_ADDRESS" | "OTHER";

export type BailRevokeReason =
  | "BREACH" | "SURETY_WITHDRAWN" | "COURT_REVOKED"
  | "ACCUSED_REARRESTED_OTHER_CASE" | "OTHER";

export type BailClearReason =
  | "CHARGESHEET_FILED" | "DISCHARGED" | "ACQUITTED";

export interface BailSuretyIn {
  idx: number;
  name_en: string;
  name_mr?: string | null;
  address?: string | null;
  mobile?: string | null;
  occupation?: string | null;
  id_proof_doc_key?: string | null;
  solvency_inr?: number | null;
}

export interface BailConditionTyped {
  id?: string;
  bail_id?: string;
  kind: BailConditionKind | string;
  value_text?: string | null;
  added_at?: string;
  added_by?: string;
}

export interface BailSuretyVerification {
  id: string;
  surety_id: string;
  verified: boolean;
  solvency_satisfied?: boolean | null;
  address_verified?: boolean | null;
  mobile_verified?: boolean | null;
  prior_surety_count?: number | null;
  notes_en?: string | null;
  notes_mr?: string | null;
  verified_by: string;
  verified_at: string;
}

export interface BailSuretyRow {
  id: string;
  bail_id: string;
  idx: number;
  name_en: string;
  name_mr?: string | null;
  address?: string | null;
  mobile?: string | null;
  occupation?: string | null;
  id_proof_doc_key?: string | null;
  solvency_inr?: number | null;
  created_at: string;
  verification: BailSuretyVerification | null;
}

export interface BailBond {
  id: string;
  bail_id: string;
  bond_format: "BB3" | "BB4";
  bond_doc_key?: string | null;
  executed_at: string;
  executed_by: string;
  canonical_sha256?: string | null;
}

export interface BailViolation {
  id: string;
  bail_id: string;
  condition_kind: BailConditionKind | string;
  violation_date: string;
  note_en: string;
  note_mr?: string | null;
  evidence_doc_key?: string | null;
  recorded_by: string;
  created_at: string;
}

export interface BailRevocation {
  id: string;
  bail_id: string;
  reason: BailRevokeReason;
  reason_note_en?: string | null;
  reason_note_mr?: string | null;
  court_order_ref?: string | null;
  other_arrest_id?: string | null;
  revoked_at: string;
  revoked_by: string;
}

export interface BailRequest {
  id: string;
  ps_code: string;
  register_no: string;
  related_fir_id?: string | null;
  related_arrest_id?: string | null;
  accused_name: string;
  applied_at: string;
  decided_at?: string | null;
  granted_by?: BailAuthority | string | null;
  status: BailStatus;
  bail_amount_inr?: number | null;
  sureties?: any;
  conditions?: string | null;
  refusal_reason?: string | null;
  due_at?: string | null;
  breached_at?: string | null;
  bond_pdf_doc_key?: string | null;
  bond_executed_at?: string | null;
  applicant_signature_doc_key?: string | null;
  sho_grounds_text?: string | null;
  max_punishment_years?: number | null;
  default_bail_clock_at?: string | null;
  default_bail_warning_threshold?: BailWarningThreshold | null;
  anticipatory?: boolean | null;
  court_hearing_event_id?: string | null;
  attachment_object_keys?: string[] | null;
  entered_by: string;
  canonical_sha256?: string | null;
  status_changed_at?: string | null;
  status_changed_by?: string | null;
  status_reason?: string | null;
  updated_at?: string | null;
}

export interface BailDefaultWarning {
  threshold: BailWarningThreshold | null;
  days_remaining: number | null;
  eligible_at: string | null;
}

export interface BailDetail {
  request: BailRequest;
  conditions_typed: BailConditionTyped[];
  sureties: BailSuretyRow[];
  bond: BailBond | null;
  violations: BailViolation[];
  revocation: BailRevocation | null;
  default_bail_warning_threshold: BailWarningThreshold | null;
  default_bail_warning: BailDefaultWarning;
}

export const bailGet = (id: string) =>
  httpFIR.get(`registers/bail/${id}`).json<BailDetail>();

export const bailExecuteBond = (id: string, body: {
  sureties?: BailSuretyIn[];
  applicant_signature_doc_key?: string;
  conditions?: Array<{ kind: BailConditionKind | string; value_text?: string | null }>;
  bond_format?: "BB3" | "BB4";
}) => httpFIR.post(`registers/bail/${id}/execute-bond`, { json: body }).json<any>();

export const bailVerifySurety = (id: string, sidx: number, body: {
  verified: boolean;
  solvency_satisfied?: boolean;
  address_verified?: boolean;
  mobile_verified?: boolean;
  prior_surety_count?: number;
  notes_en?: string;
  notes_mr?: string;
}) => httpFIR.post(`registers/bail/${id}/sureties/${sidx}/verify`, { json: body }).json<any>();

export const bailLogViolation = (id: string, body: {
  condition_kind: BailConditionKind;
  violation_date?: string;
  note_en: string;
  note_mr?: string;
  evidence_doc_key?: string;
}) => httpFIR.post(`registers/bail/${id}/log-violation`, { json: body }).json<any>();

export const bailRevoke = (id: string, body: {
  reason: BailRevokeReason;
  reason_note_en?: string;
  reason_note_mr?: string;
  court_order_ref?: string;
  other_arrest_id?: string;
}) => httpFIR.post(`registers/bail/${id}/revoke`, { json: body }).json<any>();

export const bailClear = (id: string, body: {
  reason: BailClearReason;
  note?: string;
}) => httpFIR.post(`registers/bail/${id}/clear`, { json: body }).json<any>();

export const bailGrantDefaultBail = (id: string, body: {
  sho_grounds_text: string;
}) => httpFIR.post(`registers/bail/${id}/grant-default-bail`, { json: body }).json<any>();

export const bailSuggestWanted = (id: string) =>
  httpFIR.post(`registers/bail/${id}/suggest-wanted`, { json: {} }).json<any>();

export const bailBondPdfUrl = (id: string) =>
  withAuthQS(`/v1/registers/bail/${id}/bond.pdf`);

export const bailKpis = () =>
  httpFIR.get(`kpis/bail`).json<any>();

export const bailTrack = (id: string) =>
  httpFIR.get(`citizen/bail/track/${id}`).json<any>();

// Re-use the existing bail PATCH /decide endpoint with a typed wrapper.
export const bailDecide = (id: string, body: {
  decision: "GRANTED" | "REFUSED" | "MODIFIED" | "CANCELLED";
  granted_by?: BailAuthority | string;
  bail_amount_inr?: number;
  conditions?: string;
  refusal_reason?: string;
  sureties?: any[];
  attachment_object_keys?: string[];
  sho_grounds_text?: string;
}) => httpFIR.patch(`registers/bail/${id}/decide`, { json: body }).json<any>();

export const myProfile        = () => httpFIR.get(`me`).json<any>();
export const myNotifications  = () => httpFIR.get(`me/notifications`).json<any[]>();

// ---------------------------------------------------------------------------
// Section ↔ Evidence ingredient matrix
// ---------------------------------------------------------------------------

export interface IngredientRow {
  section_code: string;
  ingredient_code: string;
  ingredient_text_en: string;
  ingredient_text_mr: string;
  proof_required_en: string;
  proof_required_mr: string;
  evidence_kinds: string[];
  proofs?: ProofRow[];
  status?: "LINKED" | "MISSING";
}
export interface ProofRow {
  id: string; section_code: string; ingredient_code: string;
  proof_record_type: "evidence_item"|"statement"|"panchanama"|"mlc_request"|"fsl_request"|"arrest_record"|"note";
  proof_record_id?: string | null;
  note?: string | null;
  linked_by: string; linked_at: string;
}
export interface SectionMatrix {
  fir_id: string;
  sections: { code: string; title_en: string; title_mr: string;
              ingredients: IngredientRow[];
              ingredient_count: number; ingredients_linked: number }[];
  summary: { total_ingredients: number; linked: number; missing: number; strength_pct: number };
}

export const listSectionIngredients = (codes: string[]) =>
  httpFIR.get(`sections/ingredients`, { searchParams: { codes: codes.join(",") } })
    .json<{ sections: { code: string; ingredients: IngredientRow[] }[]; unknown_sections: string[] }>();

export const getFirSectionMatrix = (firId: string) =>
  httpFIR.get(`firs/${firId}/section-matrix`).json<SectionMatrix>();

export const linkFirProof = (firId: string, body: {
  section_code: string; ingredient_code: string;
  proof_record_type: ProofRow["proof_record_type"];
  proof_record_id?: string | null;
  note?: string | null;
}) => httpFIR.post(`firs/${firId}/proofs`, { json: body }).json<{ id: string }>();

export const unlinkFirProof = (firId: string, proofId: string) =>
  httpFIR.delete(`firs/${firId}/proofs/${proofId}`).json<{ ok: boolean }>();

// ---------------------------------------------------------------------------
// #37 — Arrest 6-rights checklist + 24-hr Magistrate countdown (BNSS S.35)
// ---------------------------------------------------------------------------

export interface ArrestRight {
  right_code: "R1"|"R2"|"R3"|"R4"|"R5"|"R6";
  label_en: string; label_mr: string;
  logged: boolean;
  occurred_at: string | null;
  recorded_by: string | null;
  note: string | null;
}
export interface ArrestRightsView {
  arrest_id: string; fir_id: string;
  arrested_at: string; magistrate_deadline_at: string;
  seconds_to_deadline: number;
  rights: ArrestRight[];
  all_six_logged: boolean;
}

export const getArrestRights = (firId: string, arrestId: string) =>
  httpFIR.get(`firs/${firId}/arrest-rights/${arrestId}`).json<ArrestRightsView>();

export const logArrestRight = (firId: string, arrestId: string, right_code: ArrestRight["right_code"], note?: string) =>
  httpFIR.post(`firs/${firId}/arrest-rights/${arrestId}`, { json: { right_code, note } })
    .json<{ id: string; right_code: string }>();

// ---------------------------------------------------------------------------
// #34 — BSA Section 63 electronic-evidence certificate
// ---------------------------------------------------------------------------

export interface BsaS63Cert {
  id: string; evidence_id: string; cert_no: string;
  device_make_model: string; device_id: string | null; source_description: string;
  file_name: string; file_size_bytes: number; file_sha256: string;
  captured_at: string; captured_by: string;
  custody_chain_brief: string | null;
  cert_text_en: string; cert_text_mr: string; issued_at: string;
}

export const getBsaS63 = (evidenceId: string) =>
  httpFIR.get(`evidence/${evidenceId}/bsa-s63`).json<BsaS63Cert>();

export const issueBsaS63 = (evidenceId: string, body: {
  device_make_model: string;
  device_id?: string;
  source_description: string;
  custody_chain_brief?: string;
  file_sha256_override?: string;
}) => httpFIR.post(`evidence/${evidenceId}/bsa-s63`, { json: body })
  .json<{ id: string; cert_no: string; cert_text_en: string; cert_text_mr: string }>();

// ---------------------------------------------------------------------------
// Spec 028 — BSA §63 batch-issue + CDR flag/demand-letter + KPI
// ---------------------------------------------------------------------------

export const batchIssueBsaS63 = (firId: string) =>
  httpFIR.post(`firs/${firId}/bsa-s63/batch-issue`).json<{ issued: number; skipped: number; errors: { evidence_id: string; reason: string }[] }>();

export interface CdrRecordFlag {
  record_id: string;
  flag: 'KEY_CONTACT' | 'ALIBI' | 'IGNORE' | 'FOLLOW_UP';
  note?: string;
}

export const flagCdrRecord = (recordId: string, body: { flag: string; note?: string }) =>
  httpFIR.post(`cdr/records/${recordId}/flag`, { json: body }).json<CdrRecordFlag>();

export const unflagCdrRecord = (recordId: string) =>
  httpFIR.delete(`cdr/records/${recordId}/flag`);

export const getCdrDemandLetterUrl = (datasetId: string) =>
  withAuthQS(`/v1/cdr/datasets/${datasetId}/demand-letter`);

export const getCdrCommonContacts = (firId: string, datasetIds: string[], minDatasets = 2) =>
  httpFIR.get(`firs/${firId}/cdr/common-contacts`, {
    searchParams: { dataset_ids: datasetIds.join(','), min_datasets: minDatasets }
  }).json<{ b_party: string; datasets_seen: number; total_calls: number; total_sec: number; per_dataset: { dataset_id: string; calls: number }[] }[]>();

export interface DigitalEvidenceKpis {
  digital_items_total: number;
  digital_items_certified: number;
  digital_items_uncertified: number;
  digital_items_cert_overdue: number;
  cdr_datasets_total: number;
}

export const getDigitalEvidenceKpis = () =>
  httpFIR.get('kpis/digital-evidence').json<DigitalEvidenceKpis>();

// ---------------------------------------------------------------------------
// #15 — Panic Button SOS (T38)
// ---------------------------------------------------------------------------

export interface SOSAlert {
  id: string; device_id: string; received_at: string;
  gps_lat: number | null; gps_lng: number | null; address_hint: string | null;
  citizen_name: string | null; citizen_mobile: string | null;
  status: "RECEIVED"|"DISPATCHED"|"EN_ROUTE"|"ON_SCENE"|"RESOLVED"|"FALSE_ALARM";
  responding_officer_id: string | null;
  dispatched_at: string | null; resolved_at: string | null; resolved_note: string | null;
}

export const sosActive = () => httpFIR.get(`emergency/sos/active`).json<{ count: number; alerts: SOSAlert[] }>();
export const sosList   = (status?: string) => httpFIR.get(`emergency/sos`, { searchParams: status ? { status } : {} }).json<SOSAlert[]>();
export const sosUpdate = (id: string, status: SOSAlert["status"], note?: string) =>
  httpFIR.patch(`emergency/sos/${id}/status`, { json: { status, note } }).json<{ ok: boolean }>();

// ---------------------------------------------------------------------------
// #36 — BNSS S.193 60-day charge-sheet countdown
// ---------------------------------------------------------------------------

export interface CSDeadline {
  fir_id: string; applicable: boolean; reason?: string;
  first_arrest_at?: string; deadline_at?: string;
  days_left?: number;
  status?: "ON_TRACK"|"WARN"|"OVERDUE"|"FILED";
  charge_sheet_id?: string | null; charge_sheet_filed_at?: string | null;
}

export const getFIRCSDeadline = (firId: string) =>
  httpFIR.get(`firs/${firId}/cs-deadline`).json<CSDeadline>();

export interface CSDeadlineRow {
  id: string; fir_number: string | null; summary_en: string; summary_mr: string;
  first_arrest_at: string; deadline_at: string;
  days_left: number; status: "ON_TRACK"|"WARN"|"OVERDUE";
}

export const dashboardCSDeadlines = (limit = 10) =>
  httpFIR.get(`dashboard/cs-deadlines`, { searchParams: { limit } })
    .json<{ items: CSDeadlineRow[]; summary: { overdue: number; warn: number; on_track: number } }>();

// Feature 006 — BNSS clock-strip summary for the operations dashboard.
export interface ClockChip {
  code: "S173"|"S193"|"S35"|"MLC"|"FSL"|"BAIL";
  label_en: string; label_mr: string; statute_ref: string; limit_text: string;
  overdue: number; due_soon: number; on_track: number; scope: "mine"|"station";
}
export const getDashboardClocksSummary = (scope: "mine"|"station"|"auto" = "auto") =>
  httpFIR.get(`dashboard/clocks-summary`, { searchParams: { scope } })
    .json<{ scope: "mine"|"station"; clocks: ClockChip[] }>();

// ── MLC register (013 — dynamic new-request form + structured exam) ─────
export type MlcUrgency = "ROUTINE"|"EMERGENCY"|"POCSO"|"WOMAN_VICTIM"|"DEATH_REVIEW"|"CUSTODIAL_DEATH";
export type MlcInjuryClass = "SIMPLE"|"GRIEVOUS"|"DANGEROUS"|"DEATH"|"NA";
export type Gender = "MALE"|"FEMALE"|"OTHER"|"UNKNOWN";
export type ExaminerGender = "MALE"|"FEMALE"|"OTHER";
export type HospitalSpecialty = "GENERAL"|"FORENSIC"|"WOMAN"|"DENTAL"|"RADIOLOGY"|"PSYCH"|"OTHER";

export interface HospitalRow {
  id: string;
  name_en: string;
  name_mr: string | null;
  address: string | null;
  oncall_phone: string | null;
  specialty: HospitalSpecialty;
  woman_doctor_available: boolean;
  active: boolean;
  rank?: number; // present when returned from /v1/hospitals/preferred
}

export interface MlcCreateBody {
  fir_id: string;
  patient_name: string;
  hospital_name?: string;
  patient_citizen_id?: string | null;
  nature_of_injury?: string;
  attachment_object_keys?: string[];
  due_hours?: number | null;
  // 013 additions
  hospital_id?: string | null;
  urgency?: MlcUrgency;
  escort_officer_id?: string | null;
  transport_ref?: string;
  consent_recorded?: boolean;
  consent_note?: string;
  patient_age?: number | null;
  patient_gender?: Gender | null;
}

export interface MlcExamBody {
  mlc_number?: string;
  medical_officer?: string;
  findings_summary?: string;
  report_doc_key?: string;
  attachment_object_keys?: string[];
  // 013 additions
  examiner_name?: string;
  examiner_reg_no?: string;
  examiner_gender?: ExaminerGender;
  examiner_department?: string;
  examiner_gender_departure_reason?: string;
  injury_class?: MlcInjuryClass;
  probable_cause?: string;
  age_estimation?: string;
  followup_advice?: string;
}

export interface MlcReportReceivedBody {
  report_doc_key: string;
  note?: string;
  attachment_object_keys?: string[];
}

export interface MlcCorrectBody {
  reason: string;
  mlc_number?: string;
  medical_officer?: string;
  examiner_name?: string;
  examiner_reg_no?: string;
  examiner_gender?: ExaminerGender;
  examiner_department?: string;
  injury_class?: MlcInjuryClass;
  probable_cause?: string;
  age_estimation?: string;
  findings_summary?: string;
  followup_advice?: string;
}

export interface MlcAiSuggestion {
  nature_of_injury: string;
  urgency: MlcUrgency | "";
  hospital_id: string | null;
  source: {
    fir_narrative_excerpt?: string;
    statement_ids?: string[];
    sections?: string[];
  };
}

export const listMLC = () => httpFIR.get(`mlc`).json<any[]>();
export const getMLC = (id: string) => httpFIR.get(`mlc/${id}`).json<any>();
export const createMLC = (body: MlcCreateBody) => httpFIR.post(`mlc`, { json: body }).json<any>();
export const mlcExamined = (id: string, body: MlcExamBody) =>
  httpFIR.patch(`mlc/${id}/examined`, { json: body }).json<any>();
export const mlcReportReceived = (id: string, body: MlcReportReceivedBody) =>
  httpFIR.patch(`mlc/${id}/report-received`, { json: body }).json<any>();
export const addMlcAttachments = (id: string, keys: string[]) =>
  httpFIR.patch(`mlc/${id}/attachments`, { json: { keys } }).json<any>();
export const mlcCorrect = (id: string, body: MlcCorrectBody) =>
  httpFIR.patch(`mlc/${id}/correct`, { json: body }).json<any>();
export const mlcClose = (id: string, reason?: string) =>
  httpFIR.patch(`mlc/${id}/close`, { json: { reason } }).json<any>();

export const listHospitals = () => httpFIR.get(`hospitals`).json<HospitalRow[]>();
export const listPreferredHospitals = (ps_code: string, urgency?: MlcUrgency) => {
  const q: Record<string, string> = { ps_code };
  if (urgency) q.urgency = urgency;
  return httpFIR.get(`hospitals/preferred`, { searchParams: q }).json<HospitalRow[]>();
};
export const upsertHospital = (body: Partial<HospitalRow> & { id?: string }) =>
  body.id
    ? httpFIR.put(`hospitals/${body.id}`, { json: body }).json<HospitalRow>()
    : httpFIR.post(`hospitals`, { json: body }).json<HospitalRow>();
export const setPreferredHospital = (body: { ps_code: string; hospital_id: string; rank: number }) =>
  httpFIR.post(`hospitals/preferred`, { json: body }).json<any>();

export const aiMlcSuggest = (fir_id: string) =>
  httpAI.post(`ai/mlc/suggest`, { json: { fir_id } }).json<MlcAiSuggestion>();

// ── FSL register (014 — dynamic new-request, structured detail) ────────
export type FslKind = "DNA"|"BALLISTICS"|"TOXICOLOGY"|"NARCOTICS"|"CYBER"|"HANDWRITING"|"FINGERPRINT"|"OTHER";
export type FslUrgency = "ROUTINE"|"EMERGENCY"|"POCSO"|"NDPS"|"NHRC"|"DEATH_REVIEW"|"COURT_DIRECTED";
export type FslTransportMode = "BY_HAND"|"COURIER"|"CARRIER"|"REGISTERED_POST";
export type FslDisposition = "RETURNED"|"CONSUMED"|"DESTROYED_BY_LAB"|"RETAINED_BY_LAB"|"PARTIALLY_RETURNED";

export interface FslLabRow {
  id: string;
  name_en: string;
  name_mr: string | null;
  address: string | null;
  oncall_phone: string | null;
  specialty: FslKind[];
  accreditation_ref: string | null;
  active: boolean;
  rank?: number; // when returned from /v1/fsl-labs/preferred
}

export interface FslSampleInput {
  seq?: number;
  evidence_id?: string | null;
  description_en: string;
  description_mr?: string;
  packaging?: string;
  seal_no?: string;
  weight_g?: number | null;
  marks?: string;
}

export interface FslCreateBody {
  fir_id: string;
  kind: FslKind;
  fsl_lab?: string;          // legacy free-type fallback (when no lab_id)
  lab_id?: string | null;
  urgency?: FslUrgency;
  transport_mode?: FslTransportMode;
  transport_ref?: string;
  dispatched_by?: string | null;
  dispatched_at?: string;
  ndps_inventory_ref?: string;
  sealing_intact?: boolean;
  sample_count?: number;      // back-compat: derived from samples.length when omitted
  samples?: FslSampleInput[];
  attachment_object_keys?: string[];
  due_days?: number | null;
  evidence_id?: string | null;            // legacy single-evidence link (back-compat)
  requires_sho_approval?: boolean | null; // manual override; server auto-decides when null
}

export interface FslAckBody {
  note?: string;
  sealing_intact?: boolean;
  lab_internal_ref?: string;
  lab_received_at?: string;
  lab_counter_signature_key?: string;
  sample_seal_breaks?: { seq: number; note: string }[];
}

export interface FslInProgressBody {
  lab_internal_ref?: string;
  eta_days_from_lab: number;
  note?: string;
}

export interface FslResultBody {
  result_summary: string;
  result_summary_mr?: string;
  result_doc_key?: string;
  attachment_object_keys?: string[];
  examiner_name?: string;
  examiner_designation?: string;
  director_name?: string;
  director_designation?: string;
  report_lab_ref?: string;
  report_signed_at?: string;     // ISO date
  per_sample_results?: { seq: number; result: string }[];
  per_sample_dispositions?: { seq: number; disposition: FslDisposition }[];
}

export interface FslReDispatchBody {
  reason: string;
  new_lab_id?: string | null;
  new_sample_seqs?: number[];
}

export interface FslCorrectBody {
  reason: string;
  lab_id?: string | null;
  urgency?: FslUrgency;
  transport_mode?: FslTransportMode;
  transport_ref?: string;
  dispatched_by?: string | null;
  dispatched_at?: string;
  lab_internal_ref?: string;
  eta_days_from_lab?: number | null;
  examiner_name?: string;
  examiner_designation?: string;
  director_name?: string;
  director_designation?: string;
  report_lab_ref?: string;
  report_signed_at?: string;
  result_summary?: string;
  result_summary_mr?: string;
  ndps_inventory_ref?: string;
}

export interface FslAiSuggestion {
  kind: FslKind | "";
  urgency: FslUrgency | "";
  lab_id: string | null;
  sample_count: number | null;
  source: {
    fir_narrative_excerpt?: string;
    statement_ids?: string[];
    sections?: string[];
    panchanama_id?: string;
  };
}

export const listFSL = () => httpFIR.get(`fsl`).json<any[]>();
export const getFSL = (id: string) => httpFIR.get(`fsl/${id}`).json<any>();
export const createFSL = (body: FslCreateBody) => httpFIR.post(`fsl`, { json: body }).json<any>();
export const fslAcknowledged = (id: string, body: FslAckBody = {}) =>
  httpFIR.patch(`fsl/${id}/acknowledged`, { json: body }).json<any>();
export const fslInProgress = (id: string, body: FslInProgressBody) =>
  httpFIR.patch(`fsl/${id}/in-progress`, { json: body }).json<any>();
export const fslApproveDispatch = (id: string, body: { note?: string } = {}) =>
  httpFIR.patch(`fsl/${id}/approve-dispatch`, { json: body }).json<any>();
export const fslResultReceived = (id: string, body: FslResultBody) =>
  httpFIR.patch(`fsl/${id}/result-received`, { json: body }).json<any>();
export const addFslAttachments = (id: string, keys: string[]) =>
  httpFIR.patch(`fsl/${id}/attachments`, { json: { keys } }).json<any>();
export const fslReDispatch = (id: string, body: FslReDispatchBody) =>
  httpFIR.patch(`fsl/${id}/re-dispatch`, { json: body }).json<any>();
export const fslCorrect = (id: string, body: FslCorrectBody) =>
  httpFIR.patch(`fsl/${id}/correct`, { json: body }).json<any>();
export const fslClose = (id: string, reason?: string) =>
  httpFIR.patch(`fsl/${id}/close`, { json: { reason } }).json<any>();

export const listFslLabs = () => httpFIR.get(`fsl-labs`).json<FslLabRow[]>();
export const listPreferredFslLabs = (ps_code: string, kind: FslKind) =>
  httpFIR.get(`fsl-labs/preferred`, { searchParams: { ps_code, kind } }).json<FslLabRow[]>();
export const upsertFslLab = (body: Partial<FslLabRow> & { id?: string }) =>
  body.id
    ? httpFIR.put(`fsl-labs/${body.id}`, { json: body }).json<FslLabRow>()
    : httpFIR.post(`fsl-labs`, { json: body }).json<FslLabRow>();
export const setPreferredFslLab = (body: { ps_code: string; lab_id: string; kind: FslKind; rank: number }) =>
  httpFIR.post(`fsl-labs/preferred`, { json: body }).json<any>();

export const aiFslSuggest = (fir_id: string) =>
  httpAI.post(`ai/fsl/suggest`, { json: { fir_id } }).json<FslAiSuggestion>();

// ── Malkhana register (feature 005, P0+P1) ──────────────────────────────
export interface MalkhanaRow {
  id: string; malkhana_no: string | null; qr_code: string; rfid_epc: string | null;
  kind: string; description_en: string; description_mr: string; storage_location: string | null;
  seal_no: string | null; status: string; fir_id: string; fir_number: string | null; case_no: string | null;
  transfers: number; n_movements: number; last_movement_at: string | null; last_movement_type: string | null;
  in_store_days: number | null; current_holder_officer_id: string | null; seized_at: string | null;
}
export const listMalkhana = (filters: { status?: string; kind?: string; fir?: string; from?: string; to?: string; flag?: string } = {}) => {
  const sp: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) if (v) sp[k] = v;
  return httpFIR.get(`malkhana`, { searchParams: sp }).json<MalkhanaRow[]>();
};
export interface MalkhanaMovement {
  seq: number; movement_type: string; from_status: string | null; to_status: string | null;
  from_location: string | null; to_location: string | null; to_party: string | null; reason: string | null;
  ref_kind: string | null; ref_id: string | null; court_order_no: string | null; court_name: string | null;
  court_order_date: string | null; rfid_epc: string | null; doc_object_key: string | null; created_at: string | null;
  from_officer: string | null; to_officer: string | null; acknowledged_by: string | null;
}
export const getMalkhanaMovements = (eid: string) =>
  httpFIR.get(`malkhana/${eid}/movements`).json<{ evidence_id: string; malkhana_no: string | null; status: string; movements: MalkhanaMovement[] }>();
export const malkhanaVerify = () =>
  httpFIR.get(`malkhana/verify`).json<{ ok: boolean; n: number; first_break_seq?: number; first_break_no?: string; reason?: string }>();
export const depositMalkhana = (eid: string, body: { storage_location: string; seal_no?: string; rfid_epc?: string; reason?: string }) =>
  httpFIR.post(`malkhana/${eid}/deposit`, { json: body }).json<any>();
export const dispatchMalkhana = (eid: string, body: { destination?: string; fsl_request_id?: string; to_party: string; reason?: string }) =>
  httpFIR.post(`malkhana/${eid}/dispatch`, { json: body }).json<any>();
export const produceInCourtMalkhana = (eid: string, body: { court_name: string; hearing_date?: string; to_officer_id?: string; reason?: string }) =>
  httpFIR.post(`malkhana/${eid}/produce-in-court`, { json: body }).json<any>();
export const returnToMalkhana = (eid: string, body: { src: string; reason?: string }) =>
  httpFIR.post(`malkhana/${eid}/return-to-malkhana`, { json: body }).json<any>();
export const moveMalkhana = (eid: string, body: { to_location: string; reason?: string }) =>
  httpFIR.post(`malkhana/${eid}/move`, { json: body }).json<any>();
export const bindMalkhanaTag = (eid: string, epc: string) =>
  httpFIR.post(`malkhana/${eid}/bind-tag`, { json: { epc } }).json<any>();
export const replaceMalkhanaTag = (eid: string, body: { new_epc: string; old_epc?: string; reason?: string }) =>
  httpFIR.post(`malkhana/${eid}/replace-tag`, { json: body }).json<any>();
export const malkhanaRegisterUrl = (format: "docx" | "pdf" = "docx", filters: { status?: string; fir?: string } = {}) => {
  const qs = new URLSearchParams({ ...(format === "pdf" ? { format: "pdf" } : {}), ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) }).toString();
  return withAuthQS(`/v1/malkhana/register${qs ? "?" + qs : ""}`);
};
export const malkhanaLabelUrl = (eid: string, format: "docx" | "pdf" = "docx") =>
  withAuthQS(`/v1/malkhana/${eid}/label${format === "pdf" ? "?format=pdf" : ""}`);
// ── Malkhana in-charge + disposal (P2) ──
export interface MalkhanaIncharge { officer: { id: string; badge_no: string; name_en: string; name_mr: string; rank: string }; since: string | null; reason: string | null; set_by?: { name_en: string; badge_no: string } | null; }
export const getMalkhanaIncharge = () => httpFIR.get(`malkhana/incharge`).json<{ ps_code: string; incharge: MalkhanaIncharge | null }>();
export const setMalkhanaIncharge = (body: { officer_id: string; reason: string }) => httpFIR.put(`malkhana/incharge`, { json: body }).json<any>();
export const getMalkhanaInchargeHistory = () => httpFIR.get(`malkhana/incharge/history`).json<{ ps_code: string; history: any[] }>();
export const disposeMalkhana = (eid: string, body: { disposition: string; court_order_no: string; court_name: string; court_order_date?: string; order_doc_key: string; recipient?: string; destruction_method?: string; auction_agency?: string; proceeds_inr?: number; reason?: string }) =>
  httpFIR.post(`malkhana/${eid}/dispose`, { json: body }).json<any>();
export const malkhanaDisposalRecordUrl = (eid: string, format: "docx" | "pdf" = "docx") =>
  withAuthQS(`/v1/malkhana/${eid}/disposal-record${format === "pdf" ? "?format=pdf" : ""}`);
// ── AI: OCR a court order → pre-fill the disposal form (feature 007, P1) ──
export const disposalExtract = (eid: string, order_doc_key: string) =>
  httpFIR.post(`malkhana/${eid}/disposal-extract`, { json: { order_doc_key } }).json<{ evidence_id: string; court_order_no?: string; court_name?: string; court_order_date?: string; disposition?: string; articles?: { malkhana_no: string | null; fir_ref: string | null; matched_evidence_id: string }[]; confidence: number; raw_text?: string; error?: string | null }>();

// ── AI: disposal candidates + drafted application; custody narrative; anomalies; NL query (feature 007 P2–P5) ──
export const getDisposalCandidates = () =>
  httpFIR.get(`malkhana/disposal-candidates`).json<{ evidence_id: string; malkhana_no: string | null; qr_code: string; kind: string; description_en: string; description_mr: string; fir_id: string; fir_number: string | null; case_no: string | null; fir_status: string; days_in_store: number; seized_at: string | null; last_movement_at: string | null }[]>();
export async function openDisposalApplication(evidence_ids: string[], court_name: string) {
  const blob = await httpFIR.post(`malkhana/disposal-application`, { json: { evidence_ids, court_name } }).blob();
  const u = URL.createObjectURL(blob); window.open(u, "_blank");
}
export const malkhanaCustodyNarrativeUrl = (eid: string, format: "docx" | "json" = "docx") =>
  withAuthQS(`/v1/malkhana/${eid}/custody-narrative${format === "json" ? "?format=json" : ""}`);
export const getMalkhanaCustodyNarrative = (eid: string) =>
  httpFIR.get(`malkhana/${eid}/custody-narrative`, { searchParams: { format: "json" } }).json<{ evidence_id: string; malkhana_no: string | null; current_status: string; chain_ok: boolean; fir: string | null; timeline: { at: string | null; event: string; by?: string | null; to?: string | null; detail?: string }[]; narrative_en: string; narrative_mr: string }>();
export const runMalkhanaAnomalyScan = () => httpFIR.post(`malkhana/anomaly-scan`).json<{ scanned: boolean; findings?: number; created?: number; reason?: string }>();
export const getMalkhanaAnomalies = (status: "open" | "dismissed" | "escalated" = "open") =>
  httpFIR.get(`malkhana/anomalies`, { searchParams: { status } }).json<{ id: string; kind: string; severity: string; subject_kind: string | null; subject_id: string | null; evidence_json: any; explanation_en: string | null; explanation_mr: string | null; status: string; created_at: string | null; note: string | null; reviewed_by: string | null }[]>();
export const patchMalkhanaAnomaly = (id: string, body: { status: "dismissed" | "escalated"; note?: string }) =>
  httpFIR.patch(`malkhana/anomalies/${id}`, { json: body }).json<{ id: string; status: string }>();
export const malkhanaQuery = (q: string) =>
  httpFIR.post(`malkhana/query`, { json: { q } }).json<{ filters: Record<string, string>; count: number; items: any[]; unparsed?: string[] }>();
export const getMalkhanaAuditAiSummary = (aid: string) =>
  httpFIR.get(`malkhana/audits/${aid}/ai-summary`).json<{ audit_id: string; status: string; ai_discrepancy_note_en: string | null; ai_discrepancy_note_mr: string | null }>();
// ── Malkhana stock audit + gate reads (P3) ──
export interface MalkhanaAuditItem { id: string; evidence_id: string; malkhana_no: string | null; qr_code: string; kind: string; description_en: string; expected_location: string | null; expected_status: string | null; expected_epc: string | null; finding: string; actual_location: string | null; note: string | null; checked_by: string | null; checked_at: string | null; }
export interface MalkhanaAudit { id: string; ps_code: string; status: string; opened_at: string | null; closed_at: string | null; items: MalkhanaAuditItem[]; foreigns: { epc: string; location: string | null; seen_at: string | null }[]; }
export const openMalkhanaAudit = () => httpFIR.post(`malkhana/audits`).json<{ id: string; ps_code: string; status: string; enrolled: number }>();
export const listMalkhanaAudits = () => httpFIR.get(`malkhana/audits`).json<any[]>();
export const getMalkhanaAudit = (aid: string) => httpFIR.get(`malkhana/audits/${aid}`).json<MalkhanaAudit>();
export const malkhanaAuditRfidScan = (aid: string, body: { epcs: string[]; location?: string; reader_id?: string }) => httpFIR.post(`malkhana/audits/${aid}/rfid-scan`, { json: body }).json<{ audit_id: string; scanned: number; matched: number; foreign: number }>();
export const patchMalkhanaAuditItem = (aid: string, eid: string, body: { finding: string; actual_location?: string; note?: string }) => httpFIR.patch(`malkhana/audits/${aid}/items/${eid}`, { json: body }).json<any>();
export const closeMalkhanaAudit = (aid: string) => httpFIR.post(`malkhana/audits/${aid}/close`).json<{ audit_id: string; status: string; checked: number; present: number; missing: number; damaged: number; location_mismatch: number; foreign: number; warning?: string }>();
export const malkhanaAuditReportUrl = (aid: string, format: "docx" | "pdf" = "docx") => withAuthQS(`/v1/malkhana/audits/${aid}/report${format === "pdf" ? "?format=pdf" : ""}`);
export const getMalkhanaGateReads = (opts: { since?: string; direction?: string; alerts_only?: boolean } = {}) => {
  const sp: Record<string, string> = {};
  if (opts.since) sp.since = opts.since;
  if (opts.direction) sp.direction = opts.direction;
  if (opts.alerts_only) sp.alerts_only = "true";
  return httpFIR.get(`malkhana/gate-reads`, { searchParams: sp }).json<{ id: string; epc: string; evidence_id: string | null; malkhana_no: string | null; qr_code: string | null; fir_id: string | null; case_no: string | null; direction: string; gate_id: string | null; read_at: string | null; alert_kind: string | null; matched: boolean }[]>();
};
// ── Court Filings register (015 — dynamic flow, hearing events, sanction, defects) ──
export type CourtKind =
  | "JMFC" | "CJM" | "SESSIONS_COURT" | "DISTRICT_COURT"
  | "HIGH_COURT" | "SUPREME_COURT"
  | "POCSO_SPECIAL" | "NDPS_SPECIAL" | "NIA_SPECIAL" | "CBI_SPECIAL"
  | "PMLA_SPECIAL" | "MCOCA_SPECIAL" | "UAPA_SPECIAL"
  | "MAHILA_COURT" | "FAMILY_COURT" | "SC_ST_POA_DESIGNATED"
  | "JJB" | "CHILDREN_COURT" | "LOK_ADALAT"
  | "ELECTRICITY_THEFT" | "RAILWAY_COURT" | "MOTOR_VEHICLE" | "OTHER";

export type CourtFilingStatusV2 =
  | "DRAFT" | "SIGNED_BY_IO" | "SIGNED_BY_SHO" | "AWAITING_SANCTION"
  | "SUBMITTED_TO_COURT" | "ACK_BY_COURT" | "DEFECTS_RAISED"
  | "COGNIZANCE_TAKEN" | "DOCUMENTS_FURNISHED" | "COMMITTED_TO_SESSIONS"
  | "CHARGES_FRAMED" | "PLEA_RECORDED"
  | "PE_BEGUN" | "PE_CLOSED" | "DE_BEGUN" | "DE_CLOSED"
  | "ACCUSED_EXAMINATION_S351" | "FINAL_ARGUMENTS" | "JUDGEMENT_RESERVED"
  | "JUDGEMENT_PRONOUNCED" | "SENTENCE_HEARING"
  | "PROPERTY_DISPOSAL_ORDERED" | "COMPENSATION_ORDERED"
  | "APPEAL_FILED" | "TRANSFERRED" | "CLOSED";

export type HearingEventKind =
  | "COGNIZANCE_TAKEN" | "DOCUMENTS_FURNISHED" | "COMMITTAL_TO_SESSIONS"
  | "CHARGES_FRAMED" | "PLEA_RECORDED"
  | "PE_BEGUN" | "PE_CLOSED" | "DE_BEGUN" | "DE_CLOSED"
  | "ACCUSED_EXAMINATION_S351" | "FINAL_ARGUMENTS"
  | "JUDGEMENT_RESERVED" | "JUDGEMENT_PRONOUNCED" | "SENTENCE_HEARING"
  | "ADJOURNED" | "ORDER"
  | "DEFECTS_RAISED" | "DEFECTS_CURED"
  | "PROPERTY_ORDER" | "COMPENSATION_ORDER"
  | "COURT_ISSUED_SUMMONS" | "COURT_ISSUED_WARRANT"
  | "CASE_DIARY_PRODUCED" | "TRANSFERRED" | "OTHER";

export type DefectStatus = "OPEN" | "CURED" | "WAIVED";
export type SanctionKind =
  | "S197_PUBLIC_SERVANT" | "S196_STATE_OFFENCE" | "S188_EXTRA_TERRITORIAL"
  | "ACB" | "LOKAYUKTA" | "OTHER";
export type SanctionStatus = "APPLIED" | "GRANTED" | "REFUSED" | "WAIVED";
export type AccusedPlea = "NOT_GUILTY" | "GUILTY" | "NOT_RECORDED";
export type AccusedVerdict = "CONVICTED" | "ACQUITTED" | "DISCHARGED" | "COMPOUNDED" | "PLEA_BARGAINED" | "ABATED_DEATH";
export type AdjournmentFaultParty = "POLICE" | "PROSECUTION" | "COURT" | "DEFENCE" | "ACCUSED" | "ADMINISTRATIVE";

export type PropertyOrderKind =
  | "RETURN_TO_OWNER" | "DESTROY" | "SELL" | "RETAIN_AS_EVIDENCE"
  | "NDPS_FORFEITURE_CONVEYANCE" | "NDPS_FORFEITURE_PROCEEDS"
  | "PMLA_ATTACHMENT" | "BNS_ORGANISED_CRIME_ATTACH"
  | "PERISHABLE_SALE_S506" | "S504_PUBLIC_PROPERTY" | "S505_RESTITUTION"
  | "S508_DESTRUCTION" | "COMPENSATION_PAY_S395";

export interface CourtMasterRow {
  id: string; kind: CourtKind;
  name_en: string; name_mr: string | null;
  jurisdiction: string | null; address: string | null;
  clerk_phone: string | null; working_hours: string | null;
  parent_court_id: string | null;
  active: boolean;
}

export interface MagistrateRow {
  id: string; court_id: string;
  name_en: string; name_mr: string | null;
  designation: string | null; active: boolean;
}

export interface HearingEventInput {
  hearing_at: string; kind: HearingEventKind;
  order_text_en?: string; order_text_mr?: string;
  order_doc_key?: string;
  witnesses_produced?: string[];
  accused_present?: string[];
  articles_produced?: string[];
  next_hearing_at?: string;
  exhibit_marks?: Record<string, string>;
  adjournment_reason?: string;
  fault_party?: AdjournmentFaultParty;
}

export interface PerAccusedOutcomeInput {
  accused_id?: string | null;
  accused_name?: string;
  plea?: AccusedPlea;
  verdict: AccusedVerdict;
  sections_convicted?: string[];
  sentence_text?: string;
  sentence_years?: number | null;
  sentence_fine_inr?: number | null;
  pcr_summary?: string;
}

export const listCourtFilings = () => httpFIR.get(`court/filings`).json<any[]>();
export const getCourtFiling = (id: string) => httpFIR.get(`court/filings/${id}`).json<any>();
export const getCourtFilingFull = (id: string) => httpFIR.get(`court/filings/${id}/full`).json<any>();

// court master
export const listCourtMasters = () => httpFIR.get(`court-masters`).json<CourtMasterRow[]>();
export const upsertCourtMaster = (body: Partial<CourtMasterRow> & { id?: string }) =>
  body.id
    ? httpFIR.put(`court-masters/${body.id}`, { json: body }).json<CourtMasterRow>()
    : httpFIR.post(`court-masters`, { json: body }).json<CourtMasterRow>();
export const listMagistrates = (court_id: string) =>
  httpFIR.get(`court-masters/${court_id}/magistrates`).json<MagistrateRow[]>();
export const upsertMagistrate = (court_id: string, body: Partial<MagistrateRow> & { id?: string }) =>
  body.id
    ? httpFIR.put(`court-masters/${court_id}/magistrates/${body.id}`, { json: body }).json<MagistrateRow>()
    : httpFIR.post(`court-masters/${court_id}/magistrates`, { json: body }).json<MagistrateRow>();

// court filing lifecycle
export const shoApproveFiling = (id: string, body: { note?: string } = {}) =>
  httpFIR.post(`court/filings/${id}/sho-approve-filing`, { json: body }).json<any>();
export const recordReceivedAtCourt = (id: string, body: {
  court_case_no: string; cnr_number?: string;
  received_at_court_at?: string; ack_copy_object_key?: string; note?: string;
}) => httpFIR.post(`court/filings/${id}/received-at-court`, { json: body }).json<any>();
export const recordHearingEvent = (id: string, body: HearingEventInput) =>
  httpFIR.post(`court/filings/${id}/hearing-event`, { json: body }).json<any>();
export const raiseDefect = (id: string, body: {
  defect_text_en: string; defect_text_mr?: string; cure_due_date?: string;
  raised_in_hearing_event_id?: string;
}) => httpFIR.post(`court/filings/${id}/defect`, { json: body }).json<any>();
export const cureDefect = (id: string, did: string, body: { cure_doc_key?: string; cure_note?: string }) =>
  httpFIR.patch(`court/filings/${id}/defect/${did}/cure`, { json: body }).json<any>();
export const applySanction = (id: string, body: {
  kind: SanctionKind; sanctioning_authority: string; applied_at?: string; sla_due_at?: string;
}) => httpFIR.post(`court/filings/${id}/sanction`, { json: body }).json<any>();
export const grantSanction = (id: string, sid: string, body: { granted_at: string; order_ref: string; order_doc_key?: string }) =>
  httpFIR.patch(`court/filings/${id}/sanction/${sid}/grant`, { json: body }).json<any>();
export const refuseSanction = (id: string, sid: string, body: { refused_at: string; note?: string; order_doc_key?: string }) =>
  httpFIR.patch(`court/filings/${id}/sanction/${sid}/refuse`, { json: body }).json<any>();
export const recordPropertyOrder = (id: string, body: {
  order_kind: PropertyOrderKind; articles: string[];
  order_ref: string; order_doc_key?: string; recipient?: string; note?: string;
}) => httpFIR.post(`court/filings/${id}/property-order`, { json: body }).json<any>();
export const recordCourtIssuedSummons = (id: string, body: {
  kind: "SUMMONS" | "BAILABLE_WARRANT" | "NON_BAILABLE_WARRANT" | "PROCLAMATION";
  addressee_name: string; addressee_address?: string;
  return_due_date?: string; note?: string;
}) => httpFIR.post(`court/filings/${id}/court-issued-summons`, { json: body }).json<any>();
export const recordJudgement = (id: string, body: { outcomes: PerAccusedOutcomeInput[] }) =>
  httpFIR.patch(`court/filings/${id}/judgement`, { json: body }).json<any>();
export const recordAppeal = (id: string, body: { appellate_court_id: string; summary?: string }) =>
  httpFIR.post(`court/filings/${id}/appeal`, { json: body }).json<any>();
export const recordTransferred = (id: string, body: { to_court_id: string; reason: string }) =>
  httpFIR.post(`court/filings/${id}/transferred`, { json: body }).json<any>();
export const addBundleVersion = (id: string, body: { object_key: string; sha256?: string; reason: string }) =>
  httpFIR.post(`court/filings/${id}/bundle-version`, { json: body }).json<any>();
export const getNjdgStatus = (id: string) =>
  httpFIR.get(`court/filings/${id}/njdg-status`).json<{
    drift_detected: boolean;
    our_next: string | null; njdg_next: string | null;
    our_status: string | null; njdg_stage: string | null;
    njdg_stage_mapped_to_status_v2?: string | null;
    njdg_last_order?: string | null;
    cnr: string | null; fetched_at: string; provider: string;
    note?: string;
  }>();
export const acceptNjdg = (id: string, body: { accept_next_hearing_at?: boolean; accept_status?: boolean; note?: string } = {}) =>
  httpFIR.patch(`court/filings/${id}/accept-njdg`, { json: body }).json<any>();
export const correctCourtFiling = (id: string, body: {
  reason: string;
  court_id?: string | null;
  court_case_no?: string;
  cnr_number?: string;
  next_hearing_at?: string;
  last_order_text?: string;
  sanction_required?: boolean;
  requires_sho_approval?: boolean;
}) => httpFIR.patch(`court/filings/${id}/correct`, { json: body }).json<any>();
export const listPairviWorklist = (params: { date_from?: string; date_to?: string } = {}) =>
  httpFIR.get(`court/pairvi/mine`, { searchParams: params as any }).json<any[]>();

export const listAlerts = () => httpFIR.get(`alerts`).json<any[]>();
export const listSystemAuditLogs = (params: { record_type?: string; action?: string } = {}) =>
  httpFIR.get(`audit-logs`, { searchParams: params as any }).json<any[]>();

export const listConfigs = () => httpFIR.get(`configs`).json<any[]>();
export const updateConfig = (key: string, value: string) =>
  httpFIR.put(`configs/${key}`, { json: { value } }).json<{ ok: boolean }>();
export const listRoles = () => httpFIR.get(`roles`).json<any[]>();

export const globalSearch = (q: string) =>
  httpFIR.get(`search`, { searchParams: { q } }).json<{
    firs: any[]; statements: any[]; evidence: any[]; citizens: any[] }>();

export async function renderTemplateDocx(
  code: string,
  body: { values: Record<string, string>; fir_ref?: string; place?: string; officer_name?: string },
): Promise<Blob> {
  const res = await fetch(`/v1/templates/${code}/render`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`render failed: HTTP ${res.status}`);
  return await res.blob();
}

// Auto-fill any catalogue template from this FIR's data — joins FIR ↔
// complaint ↔ citizen ↔ police_station ↔ fir_party server-side so the
// .docx comes back with every label populated, not just the 5 we knew
// to send before.
export async function fillTemplateFromFIR(firId: string, code: string, format: "docx" | "pdf" = "docx"): Promise<Blob> {
  const res = await fetch(`/v1/firs/${firId}/template-fill?code=${encodeURIComponent(code)}&format=${format}`, {
    method: "POST", headers: withFetchAuth(),
  });
  if (!res.ok) throw new Error(`template-fill failed: HTTP ${res.status}`);
  return await res.blob();
}

// Render a catalogue template from a complaint's data — the complaint record
// (A-01/A-02 by channel; A-04/A-05/A-06 for women/POCSO/senior) or the
// preliminary inquiry note (code=A-14). `code` omitted → auto-pick.
export async function fillTemplateFromComplaint(cid: string, code?: string, format: "docx" | "pdf" = "docx"): Promise<Blob> {
  const qs = new URLSearchParams({ format }); if (code) qs.set("code", code);
  const res = await fetch(`/v1/complaints/${cid}/template-fill?${qs.toString()}`, {
    method: "POST", headers: withFetchAuth(),
  });
  if (!res.ok) throw new Error(`template-fill failed: HTTP ${res.status}`);
  return await res.blob();
}

/** Fetch a doc blob and trigger a browser download; surfaces a friendly error
 *  (e.g. when PDF conversion isn't available → fall back to .docx). */
export async function downloadDoc(fetcher: () => Promise<Blob>, filename: string): Promise<void> {
  const blob = await fetcher();
  downloadBlob(blob, filename);
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// File uploads (evidence photos etc.)
// ---------------------------------------------------------------------------

export async function uploadFile(file: File): Promise<{
  object_key: string; sha256: string; size_bytes: number; content_type: string; filename: string;
}> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`/v1/uploads`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  if (!res.ok) throw new Error(`upload failed: HTTP ${res.status}`);
  return await res.json();
}

// ---------------------------------------------------------------------------
// Approval workflow
// ---------------------------------------------------------------------------

export const shoApproveCS = (id: string) =>
  httpFIR.post(`charge-sheets/${id}/sho-approve`).json<{ ok: boolean; status: string }>();
export const prosecutorReviewCS = (id: string, body: { decision: string; prosecutor_name: string; objections?: string }) =>
  httpFIR.post(`charge-sheets/${id}/prosecutor-review`, { json: body }).json<{ ok: boolean; status: string }>();

// ---------------------------------------------------------------------------
// Admin officer edit/delete + bulk import
// ---------------------------------------------------------------------------

export const adminUpdateOfficer = (id: string, body: any) =>
  httpFIR.patch(`admin/officers/${id}`, { json: body }).json<{ ok: boolean }>();
export const adminDeleteOfficer = (id: string) =>
  httpFIR.delete(`admin/officers/${id}`).json<{ ok: boolean }>();
export const adminBulkImportOfficers = (csv: string) =>
  httpFIR.post(`admin/officers/bulk-import`, { json: { csv } }).json<{ inserted: number; errors: any[] }>();

export const adminGetAIConfig = () => httpFIR.get(`admin/ai-config`).json<{
  settings: { key: string; value: string; description: string }[];
  components: Record<string, { name: string; provider: string; modules: string[]; endpoint: string | null; config_keys: string[] }>;
}>();
export const adminUpdateAIConfig = (key: string, value: string, description?: string) =>
  httpFIR.put(`admin/ai-config/${encodeURIComponent(key)}`, { json: { value, description } })
    .json<{ ok: boolean }>();
export const adminTestAIComponent = (component: string) =>
  httpFIR.post(`admin/ai-config/test/${component}`).json<{
    component: string; ok: boolean; details?: any; configured?: boolean; error?: string;
  }>();

// Twilio test SMS — routes to citizen service /v1/notify/sms-test.
export interface SMSTestResult {
  notification_id: string;
  rendered_body: string;
  ok: boolean;
  sid?: string;
  twilio_status?: string;
  status_code: number;
  error?: string;
}
export const sendTestSMS = (body: { to: string; template: string; lang: "en"|"mr"; vars: Record<string,string> }) =>
  httpFIR.post(`notify/sms-test`, { json: body, timeout: 20_000 }).json<SMSTestResult>();

// ---------------------------------------------------------------------------
// v10 §3 — Per-FIR investigation checklist (auto-loaded from matched UC)
// ---------------------------------------------------------------------------

export interface FIRChecklistItem {
  id: string;
  item_code: string;
  item_text: string;
  seq: number;
  status: "PENDING" | "COMPLETED" | "NA";
  completed_at: string | null;
  completed_by: string | null;
  note: string | null;
  uc_code: string;
}

export interface FIRChecklistResponse {
  fir_id: string;
  uc_code: string | null;
  items: FIRChecklistItem[];
  totals: { total: number; completed: number; pending: number; na: number };
}

export const getFIRChecklist = (firId: string) =>
  httpFIR.get(`firs/${firId}/checklist`).json<FIRChecklistResponse>();

export const updateFIRChecklistItem = (
  firId: string, itemCode: string,
  body: { status?: "PENDING" | "COMPLETED" | "NA"; note?: string },
) =>
  httpFIR.patch(`firs/${firId}/checklist/${encodeURIComponent(itemCode)}`,
    { json: body }).json<FIRChecklistItem>();

// Case-specific dynamic workflow planning — AI reads the FIR and emits an
// ordered phase list with sub-steps, deadlines, legal basis, critical path.
export interface AISubStep {
  text_en: string;
  text_mr?: string;
  category?: "EVIDENCE" | "STATEMENT" | "FORENSIC" | "ARREST" | "LEGAL" | "ADMIN";
  deadline_hours?: number;
  drafting_hint_en?: string;
  drafting_hint_mr?: string;
}
export interface AIWorkflowPhase {
  code: string;
  name_en: string;
  name_mr?: string;
  priority?: "URGENT" | "HIGH" | "MEDIUM" | "LOW";
  estimated_days?: number;
  depends_on?: string[];
  legal_basis?: string;
  sub_steps: AISubStep[];
  templates?: string[];
}
export interface AIWorkflowPlan {
  case_summary_en?: string;
  case_summary_mr?: string;
  complexity?: "SIMPLE" | "MODERATE" | "COMPLEX" | "HIGHLY_COMPLEX";
  estimated_duration_days?: number;
  phases: AIWorkflowPhase[];
  critical_path: string[];
  risks: { risk_en: string; mitigation_en?: string; severity?: "HIGH" | "MEDIUM" | "LOW" }[];
}
export const aiPlanFIRWorkflow = (firId: string) =>
  httpFIR.post(`firs/${firId}/workflow/ai-plan`, { timeout: 120_000 })
    .json<{ fir_id: string; plan: AIWorkflowPlan }>();

// AI augment: ask LLM for extra case-specific checklist items with drafting hints.
// Server persists rows with item_code prefix "AI-<n>" and drafting hint in `note`
// (JSON blob — see ChecklistItemHint type below for the parsed shape).
export const aiAugmentFIRChecklist = (firId: string) =>
  httpFIR.post(`firs/${firId}/checklist/ai-augment`, { timeout: 90_000 })
    .json<{ fir_id: string; inserted: number; requested: number }>();

// Parsed shape of the JSON we encode in fir_checklist_item.note for AI rows.
export interface ChecklistItemHint {
  ai?: boolean;
  drafting_hint_en?: string;
  drafting_hint_mr?: string;
  category?: "EVIDENCE" | "STATEMENT" | "FORENSIC" | "ARREST" | "LEGAL" | "OTHER";
  priority?: "URGENT" | "HIGH" | "MEDIUM" | "LOW";
  text_mr?: string;
}
export function parseChecklistHint(note: string | null): ChecklistItemHint | null {
  if (!note) return null;
  try {
    const o = JSON.parse(note);
    return typeof o === "object" && o && o.ai ? (o as ChecklistItemHint) : null;
  } catch { return null; }
}

// Tiny public endpoint: read the current STT engine setting (used by VoiceInput
// to pick browser vs server). Goes through the admin endpoint, which already
// allows the IO dev role.
export async function getSTTEngine(): Promise<string> {
  try {
    const cfg = await adminGetAIConfig();
    return cfg.settings.find((s) => s.key === "ai.stt.engine")?.value ?? "browser_web_speech";
  } catch {
    return "browser_web_speech";
  }
}

export async function getTTSEngine(): Promise<string> {
  try {
    const cfg = await adminGetAIConfig();
    return cfg.settings.find((s) => s.key === "ai.tts.engine")?.value ?? "browser_speech_synth";
  } catch {
    return "browser_speech_synth";
  }
}

// Server-side TTS proxy — POST {text, language} → audio/wav blob
export const ttsServerHealth = () =>
  httpFIR.get(`tts/health`).json<{ ok: boolean; configured: boolean;
    url?: string; status_code?: number; latency_ms?: number; error?: string;
    upstream_model?: string | null }>();

export async function ttsServerSynthesize(text: string, language = "mr"): Promise<Blob> {
  const res = await fetch(`/v1/tts/synthesize`, {
    method: "POST",
    headers: withFetchAuth({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text, language }),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`TTS failed (HTTP ${res.status}): ${t.slice(0, 200)}`);
  }
  return await res.blob();
}

// Server-side STT proxy (forwards to whatever ai.stt.server_url is)
export const sttServerHealth = () =>
  httpFIR.get(`stt/health`).json<{ ok: boolean; configured: boolean;
    url?: string; status_code?: number; latency_ms?: number; error?: string }>();

export async function sttServerTranscribe(audio: Blob, language = "mr"): Promise<{
  text: string; language: string; raw: any; audio_size_bytes: number; engine: string;
}> {
  // AI4Bharat indic-conformer (and many ASR servers) cannot decode WebM/Opus —
  // transcode in-browser to 16 kHz mono WAV first. Already-WAV blobs pass through.
  let upload = audio;
  let filename = "audio.wav";
  let transcoded = false;
  const origSize = audio.size;
  const origType = audio.type;
  if (!audio.type.includes("wav") && !audio.type.includes("wave")) {
    try {
      const { blobToWav16kMono } = await import("@/lib/audio");
      upload = await blobToWav16kMono(audio);
      transcoded = true;
    } catch (e) {
      console.warn("[stt] WAV transcode failed, sending original:", e);
      upload = audio;
      filename = audio.type.includes("webm") ? "audio.webm" : "audio.bin";
    }
  }
  const sizeKB = (upload.size / 1024).toFixed(1);
  console.log(`[stt] orig=${origSize}B (${origType}) → upload=${upload.size}B (${upload.type}) transcoded=${transcoded} file=${filename}`);
  const fd = new FormData();
  fd.append("file", upload, filename);
  fd.append("language", language);
  // Explicit timeout — STT can take 5-15s on a real audio clip.
  const ctrl = new AbortController();
  const t0 = performance.now();
  const timeoutMs = 60_000;
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(`/v1/stt/transcribe`, {
      method: "POST",
      headers: withFetchAuth(),
      body: fd,
      signal: ctrl.signal,
    });
  } catch (e: any) {
    const ms = Math.round(performance.now() - t0);
    if (e?.name === "AbortError")
      throw new Error(`STT timed out after ${ms}ms (>${timeoutMs / 1000}s) — audio ${sizeKB}KB. Check ai.stt.server_url upstream.`);
    throw new Error(`STT network failure (${e?.message ?? e}) after ${ms}ms — audio ${sizeKB}KB ${upload.type}. ` +
                    `Check that /v1/stt/transcribe is reachable (open DevTools → Network).`);
  } finally {
    clearTimeout(timer);
  }
  const ms = Math.round(performance.now() - t0);
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`STT HTTP ${res.status} after ${ms}ms (${sizeKB}KB ${upload.type}): ${t.slice(0, 200)}`);
  }
  return await res.json();
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export const sendNotification = (body: any) =>
  httpFIR.post(`notify`, { json: body }).json<{ id: string; status: string }>();
export const listCitizenNotifications = (citizenId: string) =>
  httpFIR.get(`notifications/${citizenId}`).json<any[]>();

// ---------------------------------------------------------------------------
// AIO Sahayak voice chatbot — sessions, turns, tool calls
// ---------------------------------------------------------------------------

export type BotMode  = "AUTONOMOUS" | "HIL_PER_STEP";
export type BotState =
  | "GREET" | "IDENTIFY" | "NARRATIVE" | "CLARIFY" | "SUSPECTS"
  | "WITNESSES" | "LOSS" | "ATTACHMENTS" | "REVIEW" | "SUBMITTED"
  | "AWAIT_DECISION" | "DECIDED" | "POST_DECISION" | "CLOSED";

export interface BotSession {
  id: string;
  ps_code: string;
  mode: BotMode;
  state: BotState;
  citizen_lang: string | null;
  scratch: Record<string, any>;
  complaint_id: string | null;
  fir_id: string | null;
  opened_by: string | null;
  last_poll_at: string | null;
  opened_at: string;
  closed_at: string | null;
}

export interface BotTurn {
  id: string;
  seq: number;
  role: "BOT" | "CITIZEN" | "OFFICER";
  text: string;
  text_mr: string | null;
  src_lang: string | null;
  audio_object_key: string | null;
  state_before: BotState | null;
  state_after:  BotState | null;
  hil_status: "PENDING" | "APPROVED" | "EDITED" | "SKIPPED" | null;
  hil_payload: Record<string, any> | null;
  hil_decided_at: string | null;
  created_at: string;
}

export interface BotToolCall {
  id: string;
  turn_id: string;
  tool_key: string;
  tool_kind: "READ" | "WRITE";
  args: Record<string, any>;
  args_final: Record<string, any> | null;
  status: "PENDING" | "APPROVED" | "EDITED" | "DENIED"
        | "EXECUTED" | "FAILED" | "AUTO_APPROVED";
  result: any;
  http_status: number | null;
  error: string | null;
  decided_at: string | null;
  executed_at: string | null;
  created_at: string;
}

export const botStartSession = (input: {
  mode?: BotMode; citizen_lang?: string;
  fir_id?: string; complaint_id?: string;
} = {}) =>
  httpFIR.post("bot/sessions", { json: input }).json<{
    id: string; mode: BotMode; state: BotState; citizen_lang: string;
  }>();

export const botGetSession = (sid: string) =>
  httpFIR.get(`bot/sessions/${sid}`).json<{
    session: BotSession; turns: BotTurn[]; tool_calls: BotToolCall[];
  }>();

export const botSendUtterance = (
  sid: string,
  body: { text?: string; audio_object_key?: string; src_lang?: string; text_mr?: string;
          // Feature 012 — when set, the server advances the bot state on this turn
          // regardless of `plan.state_advance` (officer's "↻ Force advance" button).
          force_advance?: boolean },
) =>
  // The server runs translate + entity-extract + planner LLM calls (and
  // sometimes a tool) before replying — routinely 20–40 s. Don't let the
  // default 10 s client timeout kill an in-flight turn.
  httpFIR.post(`bot/sessions/${sid}/utterance`, { json: body, timeout: 120_000 }).json<{
    session_id: string; state: BotState; say: string;
    citizen_turn_id: string; bot_turn_id: string;
    any_pending_tool: boolean;
    executed_results: { tool_call_id: string; tool_key: string;
                        status: string; http_status: number | null; result: any;
                        error: string | null }[];
    planner_error: string | null;
    crime_uc: string | null;
    crime_name: string | null;
    investigation_steps: string[];
    // Feature 012 — present when the LLM asked the bot to stay in the current
    // state for another follow-up; the officer pane uses these to show a "↺
    // follow-up" pill and to gauge when to press "↻ Force advance".
    followup_reason?: string | null;
    followup_cap_hit?: boolean;
    followup_stays?: Record<string, number>;
  }>();

export const botDecideTurn = (
  sid: string, tid: string,
  body: { action: "APPROVE" | "EDIT" | "SKIP"; edits?: Record<string, any> },
) => httpFIR.post(`bot/sessions/${sid}/turns/${tid}/decide`, { json: body }).json<{
  turn_id: string; action: string; session_state: BotState; scratch_keys: string[];
}>();

export const botDecideToolCall = (
  sid: string, tcid: string,
  body: { action: "APPROVE" | "EDIT" | "DENY"; args_edits?: Record<string, any> },
) => httpFIR.post(`bot/sessions/${sid}/tool-calls/${tcid}/decide`, { json: body, timeout: 60_000 }).json<{
  tool_call_id?: string; id?: string; status: string;
  http_status?: number | null; result?: any; error?: string | null;
}>();

export const botSubmit = (sid: string) =>
  httpFIR.post(`bot/sessions/${sid}/submit`, { timeout: 60_000 }).json<{
    id: string; complaint_id: string; state: BotState; tracking: any;
  }>();

export const botPoll = (sid: string) =>
  httpFIR.post(`bot/sessions/${sid}/poll`, { timeout: 60_000 }).json<{
    events: { kind: string; spoken_en: string; spoken_mr: string; data: any }[];
    state: BotState;
  }>();

export const botClose = (sid: string) =>
  httpFIR.post(`bot/sessions/${sid}/close`).json<{ id: string; state: BotState }>();

export const botSetMode = (sid: string, mode: BotMode) =>
  httpFIR.patch(`bot/sessions/${sid}`, { json: { mode } }).json<{
    id: string; mode: BotMode;
  }>();

// ---------------------------------------------------------------------------
// AIO Sahayak admin — settings + tool policy
// ---------------------------------------------------------------------------

export interface AIOTool {
  key: string;
  kind: "READ" | "WRITE";
  label_en: string;
  label_mr: string | null;
  method: string;
  url: string | null;
  args_schema: Record<string, any>;
  speak_template_en: string | null;
  speak_template_mr: string | null;
  permission: "AUTO_APPROVE_READ" | "REQUIRE_APPROVAL" | "BLOCKED";
  rate_limit_per_session: number | null;
  notes: string | null;
  default_permission: string | null;
  overridden: boolean;
}

export interface AIOSettings {
  default_mode: BotMode;
  default_lang: string;
  poll_interval_sec: number;
  max_tools_per_turn: number;
  prompt_prefix: string | null;
}

export const aioListTools = () =>
  httpFIR.get("aio/tools").json<AIOTool[]>();

export const aioGetSettings = () =>
  httpFIR.get("aio/settings").json<AIOSettings>();

export const aioPatchSettings = (body: Partial<AIOSettings>) =>
  httpFIR.patch("aio/settings", { json: body }).json<AIOSettings>();

export const aioPatchTool = (
  toolKey: string,
  body: {
    permission?: AIOTool["permission"];
    rate_limit_per_session?: number | null;
    notes?: string | null;
    reset?: boolean;
  },
) => httpFIR.patch(`aio/tools/${encodeURIComponent(toolKey)}`, { json: body }).json<{
  tool_key: string;
  permission?: AIOTool["permission"];
  rate_limit_per_session?: number | null;
  notes?: string | null;
  reset?: boolean;
}>();

// ---------------------------------------------------------------------------
// Case transfer between police stations — specs/001-case-transfer/
// ---------------------------------------------------------------------------
export type CaseTransferReason = "JURISDICTION" | "ADMINISTRATIVE" | "COURT_ORDER" | "OTHER";
export type CaseTransferStatus =
  | "PENDING_APPROVAL" | "PENDING_ACCEPTANCE" | "ACCEPTED" | "REJECTED" | "CANCELLED";
export interface CaseTransfer {
  id: string;
  fir_id: string;
  src_ps_code: string;
  dest_ps_code: string;
  reason: CaseTransferReason;
  note: string;
  status: CaseTransferStatus;
  pre_transfer_fir_status: string;
  initiated_by: string;
  initiated_at: string;
  approved_by: string | null;
  approved_at: string | null;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  cancelled_by: string | null;
  cancelled_at: string | null;
  physical_custody_acknowledged: boolean;
  overdue: boolean;
  overdue_notified_at: string | null;
  reassignment_summary: Record<string, number> | null;
  // present on the /v1/transfers/incoming listing rows:
  src_name_en?: string;
  dest_name_en?: string;
  is_overdue?: boolean;
}

export const initiateTransfer = (firId: string, body: { dest_ps_code: string; reason: CaseTransferReason; note: string }) =>
  httpFIR.post(`firs/${firId}/transfer`, { json: body }).json<CaseTransfer>();
export const approveTransfer = (firId: string) =>
  httpFIR.post(`firs/${firId}/transfer/approve`, { json: {} }).json<CaseTransfer>();
export const rejectTransfer = (firId: string, reason: string) =>
  httpFIR.post(`firs/${firId}/transfer/reject`, { json: { reason } }).json<CaseTransfer>();
export const cancelTransfer = (firId: string) =>
  httpFIR.post(`firs/${firId}/transfer/cancel`, { json: {} }).json<CaseTransfer>();
export const acceptTransfer = (firId: string, physical_custody_acknowledged: boolean) =>
  httpFIR.post(`firs/${firId}/transfer/accept`, { json: { physical_custody_acknowledged } }).json<CaseTransfer>();
export const getTransfer = (firId: string) =>
  httpFIR.get(`firs/${firId}/transfer`).json<{ transfer: CaseTransfer | null }>();
export const listIncomingTransfers = (role: "destination" | "supervisor" = "destination") =>
  httpFIR.get(`transfers/incoming`, { searchParams: { role } }).json<{ items: CaseTransfer[] }>();
export interface PoliceStation { code: string; name_en: string; name_mr: string; district_code: string; }
export const listPoliceStations = () => httpFIR.get(`police-stations`).json<PoliceStation[]>();

// ---------------------------------------------------------------------------
// AIO Sahayak — voice assistant (specs/002-aio-sahayak-voice-assistant/)
// Backed by the cases service (/v1/assistant/* — nginx routes /v1/assistant -> mhpol_cases)
// ---------------------------------------------------------------------------
export type AssistantRole = "USER" | "ASSISTANT" | "SYSTEM";
export type AssistantSessionMode = "OFFICER" | "KIOSK";
export type AssistantToolCallStatus =
  | "PENDING" | "APPROVED" | "EDITED" | "DENIED" | "EXECUTED" | "FAILED" | "AUTO_APPROVED";
export interface AssistantSession {
  id: string;
  ps_code: string;
  mode: AssistantSessionMode;
  lang: string | null;
  context: Record<string, unknown>;
  opened_by: string | null;
  opened_at: string;
  last_active_at: string;
  closed_at: string | null;
}
export interface AssistantTurn {
  id: string;
  session_id: string;
  seq: number;
  role: AssistantRole;
  text: string | null;
  text_mr: string | null;
  src_lang: string | null;
  audio_object_key: string | null;
  created_at: string;
}
export interface AssistantToolCall {
  id: string;
  session_id: string;
  turn_id: string | null;
  tool_key: string;
  kind: "READ" | "WRITE";
  args: Record<string, unknown>;
  args_final: Record<string, unknown> | null;
  status: AssistantToolCallStatus;
  result: unknown;
  http_status: number | null;
  error: string | null;
  decided_at: string | null;
  executed_at: string | null;
  created_at: string;
}
export interface AssistantUiAction { type: "nav.go" | "start_complaint" | "open_card"; [k: string]: unknown; }
export interface AssistantUtteranceResult {
  turn: AssistantTurn;
  say: string;
  say_mr: string;
  tool_calls: AssistantToolCall[];
  ui_actions: AssistantUiAction[];
  suggestions: string[];
}

export const assistantStartSession = (input: { mode?: AssistantSessionMode; lang?: string } = {}) =>
  httpFIR.post("assistant/sessions", { json: input }).json<{ id: string; mode: AssistantSessionMode; lang: string | null }>();
export const assistantGetSession = (sid: string) =>
  httpFIR.get(`assistant/sessions/${sid}`).json<{ session: AssistantSession; turns: AssistantTurn[]; tool_calls: AssistantToolCall[] }>();
export const assistantSendUtterance = (
  sid: string,
  body: { text?: string; audio_object_key?: string; src_lang?: string },
) => httpFIR.post(`assistant/sessions/${sid}/utterance`, { json: body, timeout: 120_000 }).json<AssistantUtteranceResult>();
export const assistantDecideToolCall = (
  sid: string, tcid: string,
  body: { action: "APPROVE" | "EDIT" | "DENY"; args_edits?: Record<string, unknown> },
) => httpFIR.post(`assistant/sessions/${sid}/tool-calls/${tcid}/decide`, { json: body, timeout: 60_000 }).json<AssistantToolCall>();
export const assistantClose = (sid: string) =>
  httpFIR.post(`assistant/sessions/${sid}/close`, { json: {} }).json<{ ok: boolean }>();

// ---------------------------------------------------------------------------
// 027 — Document drafting workbench (mhpol@drafting via nginx)
// ---------------------------------------------------------------------------

export type DocKind =
  | "NOTICE_S35" | "NOTICE_S175" | "SUMMONS" | "SANCTION_REQUEST"
  | "MLC_COVER" | "FSL_COVER" | "SEARCH_REQUISITION_S93"
  | "REMAND_APPLICATION_S187" | "PROPERTY_FORWARDING_S503"
  | "INQUIRY_LETTER";

export type DraftStatus =
  | "DRAFT" | "READY_FOR_SHO" | "APPROVED" | "RETURNED" | "FINALISED";

export const DOC_KIND_LABEL: Record<DocKind, { en: string; mr: string }> = {
  NOTICE_S35:                { en: "Notice u/s 35 BNSS",          mr: "कलम ३५ नोटीस" },
  NOTICE_S175:               { en: "Notice u/s 175 BNSS",         mr: "कलम १७५ नोटीस" },
  SUMMONS:                   { en: "Summons",                     mr: "समन्स" },
  SANCTION_REQUEST:          { en: "Sanction request",            mr: "मंजुरी विनंती" },
  MLC_COVER:                 { en: "MLC cover letter",            mr: "MLC संदर्भ पत्र" },
  FSL_COVER:                 { en: "FSL forwarding letter",       mr: "FSL संदर्भ पत्र" },
  SEARCH_REQUISITION_S93:    { en: "Search requisition u/s 93",   mr: "कलम ९३ झडती मागणी" },
  REMAND_APPLICATION_S187:   { en: "Remand application u/s 187",  mr: "कलम १८७ रिमांड अर्ज" },
  PROPERTY_FORWARDING_S503:  { en: "Property forwarding",         mr: "मुद्देमाल सुपूर्द" },
  INQUIRY_LETTER:            { en: "Inquiry letter",              mr: "चौकशी पत्र" },
};

export interface DocTemplate {
  id: string;
  kind: DocKind;
  version: number;
  title_en: string;
  title_mr: string;
  slots: string[];
  statute_refs: string[];
}

export interface IODraft {
  id: string;
  fir_id: string;
  kind: DocKind;
  template_version: number;
  drafter_id: string;
  body_en: string;
  body_mr: string;
  status: DraftStatus;
  supervisor_id: string | null;
  supervisor_note: string | null;
  supervisor_decided_at: string | null;
  finalised_object_key: string | null;
  finalised_at: string | null;
  created_at: string;
  updated_at: string;
  title_en?: string;
  title_mr?: string;
}

export interface IODraftRow {
  id: string;
  fir_id?: string;
  kind: DocKind;
  status: DraftStatus;
  template_version?: number;
  drafter_id?: string;
  created_at: string;
  updated_at: string;
  finalised_at: string | null;
}

export const listDocTemplates = (opts?: { kind?: DocKind }) => {
  const qs: Record<string, string> = {};
  if (opts?.kind) qs.kind = opts.kind;
  return httpFIR.get("doc-templates", { searchParams: qs })
    .json<{ count: number; rows: DocTemplate[] }>();
};

export const createDraft = (firId: string, kind: DocKind) =>
  httpFIR.post(`firs/${firId}/drafts`, { json: { kind } })
    .json<{ id: string; kind: DocKind; template_version: number; status: DraftStatus;
            body_en: string; body_mr: string; resolved_slots: Record<string, string> }>();

export const getDraft = (id: string) =>
  httpFIR.get(`io-drafts/${id}`).json<IODraft>();

export const patchDraft = (id: string, body: {
  body_en?: string; body_mr?: string;
  status?: DraftStatus; supervisor_note?: string;
}) => httpFIR.patch(`io-drafts/${id}`, { json: body })
       .json<{ id: string; status: DraftStatus; noop?: boolean }>();

export const listDraftsForFir = (firId: string) =>
  httpFIR.get(`firs/${firId}/drafts`).json<{ count: number; rows: IODraftRow[] }>();

// 027 follow-up — fetch the slot context the workbench passes to
// <SmartTextarea slotCtx={…}> so slash-inserted snippets with
// {{accused_name_mr}} / {{section_codes}} / {{io_designation_mr}}
// resolve at insert time (server-side resolver = same logic used at
// draft-create).
export const getFirSlotContext = (firId: string) =>
  httpFIR.get(`firs/${firId}/slot-context`).json<Record<string, string>>();

export const listMyDrafts = (opts?: { status?: DraftStatus }) => {
  const qs: Record<string, string> = {};
  if (opts?.status) qs.status = opts.status;
  return httpFIR.get("io-drafts/mine", { searchParams: qs })
    .json<{ count: number; rows: IODraftRow[] }>();
};

// 027 Phase 3 — finalise (snapshot + diary + status) and print URL.
export const finaliseDraft = (id: string) =>
  httpFIR.post(`io-drafts/${id}/finalise`).json<{
    id: string; status: DraftStatus;
    finalised_object_key: string; sha256: string; size_bytes: number;
  }>();

export const draftPrintUrl = (id: string) => withAuthQS(`/v1/io-drafts/${id}/print`);

// 027 Phase 4 — supervisor queue.
export interface SupervisorDraftRow extends IODraftRow {
  supervisor_note: string | null;
  fir_number: string | null;
  ps_code: string;
  title_en: string;
  title_mr: string;
  drafter_name_en: string | null;
  drafter_name_mr: string | null;
  drafter_badge_no: string | null;
  drafter_rank: string | null;
}

export const listSupervisorDrafts = (status: DraftStatus = "READY_FOR_SHO") =>
  httpFIR.get("io-drafts/ps", { searchParams: { status } })
    .json<{ count: number; rows: SupervisorDraftRow[] }>();

export const decideDraft = (id: string, decision: "APPROVED" | "RETURNED", note?: string) =>
  patchDraft(id, { status: decision, supervisor_note: note });

// 027 Phase 5 — admin stats for the dashboard tile.
export interface DraftingStats {
  ps_code: string | null;
  pending_sho: number;
  by_kind: { kind: DocKind; total: number; finalised: number; pending_sho: number; returned: number }[];
  avg_time_to_finalise_s: number;
  finalised_30d: number;
  recent_drafters: { name: string | null; rank: string | null; drafts: number }[];
}
export const getDraftingStats = () =>
  httpFIR.get("admin/drafting-stats").json<DraftingStats>();

// 027 — .docx export of the print render. Browser triggers a download.
export const draftDocxUrl = (id: string) => withAuthQS(`/v1/io-drafts/${id}/print.docx`);

// 025 FP telemetry — supervisor dashboard tile.
export interface ContradictionFPStats {
  ps_code: string | null;
  days: number;
  total: number;
  by_kind: {
    kind: string;
    open: number; resolved: number; false_positive: number;
    exposed_at_trial: number; other: number;
    high: number; total: number;
    fp_rate: number | null;   // null when no decided rows yet
  }[];
}
export const getContradictionsFPStats = (days = 30) =>
  httpFIR.get("admin/contradictions-fp-stats", { searchParams: { days: String(days) } })
    .json<ContradictionFPStats>();

// ---------------------------------------------------------------------------
// 029 — Grievance & RTI Dynamic
// ---------------------------------------------------------------------------

export const listGrievances = (params?: Record<string, string>) =>
  httpFIR.get("registers/grievances", { searchParams: params ?? {} }).json<any[]>();

export const getGrievance = (id: string) =>
  httpFIR.get(`registers/grievances/${id}`).json<any>();

export const assignGrievance = (id: string, body: { officer_id: string }) =>
  httpFIR.post(`registers/grievances/${id}/assign`, { json: body }).json<any>();

export const respondGrievance = (
  id: string,
  body: { response_en: string; response_mr: string; decision: "RESOLVED" | "REJECTED" },
) => httpFIR.post(`registers/grievances/${id}/respond`, { json: body }).json<any>();

export const escalateGrievance = (
  id: string,
  body: { escalated_to: string; reason_en: string; reason_mr: string },
) => httpFIR.post(`registers/grievances/${id}/escalate`, { json: body }).json<any>();

export const grievanceResponseLetterUrl = (id: string) =>
  withAuthQS(`/v1/registers/grievances/${id}/response-letter`);

export const getGrievanceKpis = () =>
  httpFIR.get("kpis/grievances").json<any>();

export const listRti = (params?: Record<string, string>) =>
  httpFIR.get("registers/rti", { searchParams: params ?? {} }).json<any[]>();

export const getRti = (id: string) =>
  httpFIR.get(`registers/rti/${id}`).json<any>();

export const assignRti = (
  id: string,
  body: { officer_id: string; life_liberty?: boolean; fee_exempt?: boolean },
) => httpFIR.post(`registers/rti/${id}/assign`, { json: body }).json<any>();

export const supplyRti = (
  id: string,
  body: { response_text: string; response_doc_key?: string; fee_charged_inr?: number },
) => httpFIR.post(`registers/rti/${id}/supply`, { json: body }).json<any>();

export const rejectRti = (
  id: string,
  body: { rejection_reason_en: string; rejection_section: string },
) => httpFIR.post(`registers/rti/${id}/reject`, { json: body }).json<any>();

export const transferRti = (
  id: string,
  body: { transfer_to_authority: string; reason_en: string },
) => httpFIR.post(`registers/rti/${id}/transfer`, { json: body }).json<any>();

export const fileRtiAppeal = (
  id: string,
  body: { appeal_level: "FIRST" | "SECOND"; filed_at: string; authority_name: string; authority_ref?: string },
) => httpFIR.post(`registers/rti/${id}/appeal`, { json: body }).json<any>();

export const rtiResponseLetterUrl = (id: string) =>
  withAuthQS(`/v1/registers/rti/${id}/response-letter`);

export const getRtiKpis = () =>
  httpFIR.get("kpis/rti").json<any>();

// Lockup Register (030)
export const admitLockup = (body: any) => httpFIR.post(`lockup`, { json: body }).json<any>();
export const getActiveLockup = () => httpFIR.get(`lockup/active`).json<any[]>();
export const getLockup = (eid: string) => httpFIR.get(`lockup/${eid}`).json<any>();
export const releaseLockup = (eid: string, body: any) => httpFIR.patch(`lockup/${eid}/release`, { json: body }).json<any>();
export const addLockupDiary = (eid: string, body: any) => httpFIR.post(`lockup/${eid}/diary`, { json: body }).json<any>();
export const getLockupDiary = (eid: string) => httpFIR.get(`lockup/${eid}/diary`).json<any[]>();
export const lockupRegisterPrintUrl = (date?: string) => withAuthQS(`/v1/lockup/register-print${date ? `?date=${date}` : ''}`);
export const addLockupVisitor = (eid: string, body: any) => httpFIR.post(`lockup/${eid}/visitors`, { json: body }).json<any>();
export const checkoutLockupVisitor = (eid: string, vid: string) => httpFIR.patch(`lockup/${eid}/visitors/${vid}/checkout`).json<any>();
export const getLockupVisitors = (eid: string) => httpFIR.get(`lockup/${eid}/visitors`).json<any[]>();
export const getPsVisitorLog = (params?: Record<string, string>) => httpFIR.get(`ps/visitor-log`, { searchParams: params }).json<any[]>();
export const psVisitorLogPrintUrl = (date?: string) => withAuthQS(`/v1/ps/visitor-log/print${date ? `?date=${date}` : ''}`);
// Custody & Remand (030)
export const getRemandExpiry = () => httpFIR.get(`arrests/remand-expiry`).json<any[]>();
export const getOverdueProduction = () => httpFIR.get(`arrests/overdue-production`).json<any[]>();
export const getCustodyKpis = () => httpFIR.get(`kpis/custody`).json<any>();

// 031 — Court Hearing Lifecycle
export const sendHearingReminder = (filingId: string, channel: string) =>
  httpFIR.post(`court/filings/${filingId}/send-reminder`, { json: { channel } }).json<{ log_id: string; channel: string; message_en: string }>();

export const getUpcomingHearings = (days?: number) =>
  httpFIR.get(`court/upcoming${days !== undefined ? `?days=${days}` : ''}`).json<any[]>();

export const getCourtCalendar = (from?: string, to?: string) => {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  return httpFIR.get(`court/calendar${qs ? '?' + qs : ''}`).json<{ date: string; hearings: any[] }[]>();
};

export const getAdjournmentStats = (months?: number) =>
  httpFIR.get(`court/adjournment-stats${months !== undefined ? `?months=${months}` : ''}`).json<any>();

export const addWitness = (filingId: string, eventId: string, body: any) =>
  httpFIR.post(`court/filings/${filingId}/hearing-events/${eventId}/witnesses`, { json: body }).json<{ witness_id: string }>();

export const updateWitness = (filingId: string, eventId: string, witnessId: string, body: any) =>
  httpFIR.patch(`court/filings/${filingId}/hearing-events/${eventId}/witnesses/${witnessId}`, { json: body }).json<{ updated: boolean }>();

export const listWitnessesForEvent = (filingId: string, eventId: string) =>
  httpFIR.get(`court/filings/${filingId}/hearing-events/${eventId}/witnesses`).json<any[]>();

export const listWitnessesForFiling = (filingId: string) =>
  httpFIR.get(`court/filings/${filingId}/witnesses`).json<any[]>();

export const getCourtKpis = () =>
  httpFIR.get(`kpis/court`).json<{
    upcoming_7d: number;
    hearing_imminent: number;
    witness_no_show: number;
    adjourned_30d: number;
    active_filings: number;
  }>();

// 032 — Beat Patrol & Duty Roster
export const createDutyRoster = (body: any) =>
  httpFIR.post('duty-roster', { json: body }).json<any>();

export const batchCreateDutyRoster = (entries: any[]) =>
  httpFIR.post('duty-roster/batch', { json: { entries } }).json<any>();

export const getDutyRoster = (date?: string, shift?: string) => {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (shift) params.set('shift', shift);
  const qs = params.toString();
  return httpFIR.get(`duty-roster${qs ? '?' + qs : ''}`).json<any[]>();
};

export const reportForDuty = (rid: string) =>
  httpFIR.patch(`duty-roster/${rid}/report`).json<any>();

export const markAbsent = (rid: string, absent_reason?: string) =>
  httpFIR.patch(`duty-roster/${rid}/absent`, { json: { absent_reason } }).json<any>();

export const dismissOfficer = (rid: string) =>
  httpFIR.patch(`duty-roster/${rid}/dismiss`).json<any>();

export const addPatrolLog = (rid: string, body: any) =>
  httpFIR.post(`duty-roster/${rid}/patrol-log`, { json: body }).json<any>();

export const checkoutPatrolLog = (rid: string, lid: string, incidents_noted?: string) =>
  httpFIR.patch(`duty-roster/${rid}/patrol-log/${lid}/checkout`, { json: { incidents_noted } }).json<any>();

export const listPatrolLog = (rid: string) =>
  httpFIR.get(`duty-roster/${rid}/patrol-log`).json<any[]>();

export const getDutyCoverageGaps = (date?: string) =>
  httpFIR.get(`duty-roster/coverage-gaps${date ? '?date=' + date : ''}`).json<{ threshold_hours: number; gaps: any[] }>();

export const dutyRosterPrintUrl = (date: string, shift?: string) => {
  const params = new URLSearchParams({ date });
  if (shift) params.set('shift', shift);
  return withAuthQS(`/v1/duty-roster/print?${params.toString()}`);
};

export const createPcrDeployment = (body: any) =>
  httpFIR.post('pcr-deployment', { json: body }).json<any>();

export const pcrReturn = (did: string, body: any) =>
  httpFIR.patch(`pcr-deployment/${did}/return`, { json: body }).json<any>();

export const listPcrDeployments = (date?: string) =>
  httpFIR.get(`pcr-deployment${date ? '?date=' + date : ''}`).json<any[]>();

export const getDutyKpis = () =>
  httpFIR.get('kpis/duty').json<{
    on_duty: number;
    absent_today: number;
    beats_covered: number;
    pcr_deployed: number;
    coverage_gaps: number;
  }>();

// 033 — UDR (Unnatural Death Register)
export const createUdr = (body: any) =>
  httpFIR.post('udr', { json: body }).json<any>();

export const listUdrs = (year?: number, verdict?: string, open?: boolean) => {
  const p = new URLSearchParams();
  if (year) p.set('year', String(year));
  if (verdict) p.set('verdict', verdict);
  if (open !== undefined) p.set('open', String(open));
  const qs = p.toString();
  return httpFIR.get(`udr${qs ? '?' + qs : ''}`).json<any[]>();
};

export const getUdr = (uid: string) =>
  httpFIR.get(`udr/${uid}`).json<any>();

export const updateUdrIo = (uid: string, io_officer_id: string) =>
  httpFIR.patch(`udr/${uid}/io`, { json: { io_officer_id } }).json<any>();

export const recordInquest = (uid: string, body: any) =>
  httpFIR.patch(`udr/${uid}/inquest`, { json: body }).json<any>();

export const addInquestWitness = (uid: string, body: any) =>
  httpFIR.post(`udr/${uid}/inquest-witnesses`, { json: body }).json<any>();

export const recordVerdict = (uid: string, body: any) =>
  httpFIR.patch(`udr/${uid}/verdict`, { json: body }).json<any>();

export const linkUdrFir = (uid: string, fir_id: string) =>
  httpFIR.patch(`udr/${uid}/link-fir`, { json: { fir_id } }).json<any>();

export const linkUdrMlc = (uid: string, mlc_id: string) =>
  httpFIR.patch(`udr/${uid}/link-mlc`, { json: { mlc_id } }).json<any>();

export const updateBodyStatus = (uid: string, body_status: string, notes?: string) =>
  httpFIR.patch(`udr/${uid}/body-status`, { json: { body_status, notes } }).json<any>();

export const closeUdr = (uid: string, closure_note?: string) =>
  httpFIR.patch(`udr/${uid}/close`, { json: { closure_note } }).json<any>();

export const addPostmortem = (uid: string, body: any) =>
  httpFIR.post(`udr/${uid}/postmortem`, { json: body }).json<any>();

export const markPmReceived = (uid: string) =>
  httpFIR.patch(`udr/${uid}/postmortem/report-received`, { json: {} }).json<any>();

export const getPendingInquest = () =>
  httpFIR.get('udr/pending-inquest').json<any>();

export const udrPrintUrl = (year: number) => `/v1/udr/print?year=${year}`;

export const getUdrKpis = () =>
  httpFIR.get('kpis/udr').json<any>();

// 034 — Arms & Ammunition Register
export const addWeapon = (body: any) => httpFIR.post('arms/register', { json: body }).json<any>();
export const listWeapons = () => httpFIR.get('arms/register').json<any>();
export const getWeapon = (wid: string) => httpFIR.get(`arms/register/${wid}`).json<any>();
export const issueWeapon = (wid: string, body: any) => httpFIR.post(`arms/register/${wid}/issue`, { json: body }).json<any>();
export const returnWeapon = (wid: string, iid: string, return_note?: string) =>
  httpFIR.patch(`arms/register/${wid}/issue/${iid}/return`, { json: { return_note } }).json<any>();
export const listWeaponIssues = (wid: string) => httpFIR.get(`arms/register/${wid}/issues`).json<any>();
export const drawAmmo = (wid: string, body: any) => httpFIR.post(`arms/register/${wid}/ammo`, { json: body }).json<any>();
export const returnAmmo = (wid: string, aid: string, rounds_returned: number) =>
  httpFIR.patch(`arms/register/${wid}/ammo/${aid}/return`, { json: { rounds_returned } }).json<any>();
export const listCurrentlyIssued = () => httpFIR.get('arms/issued').json<any>();
export const verifyWeapon = (wid: string) => httpFIR.patch(`arms/register/${wid}/verify`, { json: {} }).json<any>();
export const armsPrintUrl = () => '/v1/arms/print';

// 035 — Prevention Detention (PD) Register
export const createPdCase = (body: object) => httpFIR.post('pd', { json: body }).json<any>();
export const listPdCases = (params?: Record<string, string>) => httpFIR.get('pd', params && Object.keys(params).length ? { searchParams: params } : {}).json<any[]>();
export const getPdCase = (pid: string) => httpFIR.get(`pd/${pid}`).json<any>();
export const recordPdInquiry = (pid: string, body: object) => httpFIR.patch(`pd/${pid}/inquiry`, { json: body }).json<any>();
export const executePdBond = (pid: string, body: object) => httpFIR.post(`pd/${pid}/bond`, { json: body }).json<any>();
export const recordPdBreach = (pid: string, body: object) => httpFIR.post(`pd/${pid}/breach`, { json: body }).json<any>();
export const getPdExpiring = () => httpFIR.get('pd/expiring').json<any>();
export const dischargePdCase = (pid: string, body: object) => httpFIR.patch(`pd/${pid}/discharge`, { json: body }).json<any>();
export const pdPrintUrl = () => '/v1/pd/print';
export const getPdKpis = () => httpFIR.get('kpis/pd').json<any>();
export const getArmsKpis = () => httpFIR.get('kpis/arms').json<any>();

// --- 036 Traffic Accident Register ---
export const logAccident    = (body: object) => httpFIR.post('tar', { json: body }).json<any>();
export const listAccidents  = (params?: Record<string,string>) => httpFIR.get('tar', { searchParams: params || {} }).json<any>();
export const getAccident    = (tid: string) => httpFIR.get(`tar/${tid}`).json<any>();
export const addTarVehicle  = (tid: string, body: object) => httpFIR.post(`tar/${tid}/vehicle`, { json: body }).json<any>();
export const linkTarFir     = (tid: string, body: object) => httpFIR.patch(`tar/${tid}/link-fir`, { json: body }).json<any>();
export const linkTarMlc     = (tid: string, body: object) => httpFIR.patch(`tar/${tid}/link-mlc`, { json: body }).json<any>();
export const closeAccident  = (tid: string, body: object) => httpFIR.patch(`tar/${tid}/close`, { json: body }).json<any>();
export const tarPrintUrl    = (year?: number) => `/v1/tar/print${year ? `?year=${year}` : ''}`;
export const getTarKpis     = () => httpFIR.get('kpis/tar').json<any>();

// --- 037 Bandobast & Event Security Register ---
export const createBandobast         = (body: object) => httpFIR.post('bandobast', { json: body }).json<any>();
export const listBandobast           = (params?: Record<string,string>) => httpFIR.get('bandobast', { searchParams: params || {} }).json<any>();
export const getBandobast            = (bid: string) => httpFIR.get(`bandobast/${bid}`).json<any>();
export const activateBandobast       = (bid: string) => httpFIR.patch(`bandobast/${bid}/activate`).json<any>();
export const closeBandobast          = (bid: string, body: object) => httpFIR.patch(`bandobast/${bid}/close`, { json: body }).json<any>();
export const cancelBandobast         = (bid: string) => httpFIR.patch(`bandobast/${bid}/cancel`).json<any>();
export const addBandobastDeployment  = (bid: string, body: object) => httpFIR.post(`bandobast/${bid}/deployment`, { json: body }).json<any>();
export const upcomingBandobast       = () => httpFIR.get('bandobast/upcoming').json<any>();
export const bandobastPrintUrl       = (year?: number) => `/v1/bandobast/print${year ? `?year=${year}` : ''}`;
export const getBandobastKpis        = () => httpFIR.get('kpis/bandobast').json<any>();

// --- 038 General Diary / Roznamcha ---
export const gdKpis        = () => httpFIR.get('kpis/gd').json<any>();
export const listGd        = (params?: Record<string,string>) => httpFIR.get('gd', { searchParams: params || {} }).json<any>();
export const createGdEntry = (body: object) => httpFIR.post('gd', { json: body }).json<any>();
export const getGdEntry    = (eid: string) => httpFIR.get(`gd/${eid}`).json<any>();
export const gdFollowup    = (eid: string, body: object) => httpFIR.patch(`gd/${eid}/followup`, { json: body }).json<any>();
export const gdPrintUrl    = (date?: string) => `/v1/gd/print${date ? `?date=${date}` : ''}`;
export const gdToday       = () => httpFIR.get('gd/today').json<any>();

// --- 039 Missing Persons Register (KPI + print; list/create/status from Spec 019) ---
export const missingKpis     = () => httpFIR.get('kpis/missing').json<any>();
export const missingPrintUrl = (year?: number) => `/v1/missing/print${year ? `?year=${year}` : ''}`;
export const escalateMissing = (id: string) =>
  httpFIR.post(`registers/missing/${id}/escalate-to-fir`, { json: {} }).json<any>();

// --- 040 Warrant & Process Register ---
export const warrantKpis     = () => httpFIR.get('kpis/warrants').json<any>();
export const listWarrants    = (params?: Record<string,string>) => httpFIR.get('warrants', { searchParams: params || {} }).json<any>();
export const createWarrant   = (body: object) => httpFIR.post('warrants', { json: body }).json<any>();
export const getWarrant      = (wid: string) => httpFIR.get(`warrants/${wid}`).json<any>();
export const serveWarrant    = (wid: string, body: object) => httpFIR.patch(`warrants/${wid}/serve`, { json: body }).json<any>();
export const returnWarrant   = (wid: string, body: object) => httpFIR.patch(`warrants/${wid}/return`, { json: body }).json<any>();
export const cancelWarrant   = (wid: string) => httpFIR.patch(`warrants/${wid}/cancel`).json<any>();
export const warrantPrintUrl = (year?: number) => `/v1/warrants/print${year ? `?year=${year}` : ''}`;

// --- 041 Arrest Register ---
export const arrestRegKpis    = () => httpFIR.get('kpis/arrest-reg').json<any>();
export const listArrestReg    = (params?: Record<string,string>) => httpFIR.get('arrest-reg', { searchParams: params || {} }).json<any>();
export const createArrestReg  = (body: object) => httpFIR.post('arrest-reg', { json: body }).json<any>();
export const getArrestReg     = (aid: string) => httpFIR.get(`arrest-reg/${aid}`).json<any>();
export const produceArrestReg = (aid: string, body: object) => httpFIR.patch(`arrest-reg/${aid}/produce`, { json: body }).json<any>();
export const updateArrestBail = (aid: string, body: object) => httpFIR.patch(`arrest-reg/${aid}/bail`, { json: body }).json<any>();
export const releaseArrestReg = (aid: string, body: object) => httpFIR.patch(`arrest-reg/${aid}/release`, { json: body }).json<any>();
export const arrestRegPrintUrl = (year?: number, custodyFilter?: string) => {
  const params = new URLSearchParams();
  if (year) params.set('year', String(year));
  if (custodyFilter) params.set('custody_filter', custodyFilter);
  const qs = params.toString();
  return `/v1/arrest-reg/print${qs ? `?${qs}` : ''}`;
};

// --- 042 NC Register ---
export const ncKpis      = () => httpFIR.get('kpis/nc').json<any>();
export const listNc      = (params?: Record<string,string>) => httpFIR.get('nc', { searchParams: params || {} }).json<any>();
export const createNc    = (body: object) => httpFIR.post('nc', { json: body }).json<any>();
export const getNc       = (nid: string) => httpFIR.get(`nc/${nid}`).json<any>();
export const referNc     = (nid: string, body: object) => httpFIR.patch(`nc/${nid}/refer`, { json: body }).json<any>();
export const closeNc     = (nid: string, body: object) => httpFIR.patch(`nc/${nid}/close`, { json: body }).json<any>();
export const ncPrintUrl  = (year?: number) => `/v1/nc/print${year ? `?year=${year}` : ''}`;

// Spec 043 — History Sheeter Register
export const hsKpis      = () => httpFIR.get('kpis/history-sheeters').json<any>();
export const listHs      = (params?: Record<string,string>) => httpFIR.get('history-sheeters', { searchParams: params || {} }).json<any>();
export const createHs    = (body: object) => httpFIR.post('history-sheeters', { json: body }).json<any>();
export const getHs       = (hid: string) => httpFIR.get(`history-sheeters/${hid}`).json<any>();
export const reviewHs    = (hid: string, body: object) => httpFIR.patch(`history-sheeters/${hid}/review`, { json: body }).json<any>();
export const closeHs     = (hid: string, body: object) => httpFIR.patch(`history-sheeters/${hid}/close`, { json: body }).json<any>();
export const transferHs  = (hid: string, body: object) => httpFIR.patch(`history-sheeters/${hid}/transfer`, { json: body }).json<any>();
export const hsPrintUrl  = (year?: number) => `/v1/history-sheeters/print${year ? `?year=${year}` : ''}`;

// Spec 044 — Summons Register
export const summonsRegKpis    = () => httpFIR.get('kpis/summons-reg').json<any>();
export const listSummonsReg    = (params?: Record<string,string>) => httpFIR.get('summons-reg', { searchParams: params || {} }).json<any>();
export const createSummonsReg  = (body: object) => httpFIR.post('summons-reg', { json: body }).json<any>();
export const getSummonsReg     = (sid: string) => httpFIR.get(`summons-reg/${sid}`).json<any>();
export const assignSummonsReg  = (sid: string, body: object) => httpFIR.patch(`summons-reg/${sid}/assign`, { json: body }).json<any>();
export const serveSummonsReg   = (sid: string, body: object) => httpFIR.patch(`summons-reg/${sid}/serve`, { json: body }).json<any>();
export const returnSummonsReg  = (sid: string, body: object) => httpFIR.patch(`summons-reg/${sid}/return`, { json: body }).json<any>();
export const summonsRegPrintUrl = (year?: number) => `/v1/summons-reg/print${year ? `?year=${year}` : ''}`;

// Spec 045 — Accidental Death Register
export const adrKpis         = () => httpFIR.get('kpis/accidental-deaths').json<any>();
export const listAdr         = (params?: Record<string,string>) => httpFIR.get('accidental-deaths', { searchParams: params || {} }).json<any>();
export const createAdr       = (body: object) => httpFIR.post('accidental-deaths', { json: body }).json<any>();
export const getAdr          = (aid: string) => httpFIR.get(`accidental-deaths/${aid}`).json<any>();
export const orderAdrInquest = (aid: string, body: object) => httpFIR.patch(`accidental-deaths/${aid}/order-inquest`, { json: body }).json<any>();
export const referAdrToFir   = (aid: string, body: object) => httpFIR.patch(`accidental-deaths/${aid}/refer-to-fir`, { json: body }).json<any>();
export const closeAdr        = (aid: string, body: object) => httpFIR.patch(`accidental-deaths/${aid}/close`, { json: body }).json<any>();
export const adrPrintUrl     = (year?: number) => `/v1/accidental-deaths/print${year ? `?year=${year}` : ''}`;

// Spec 046 — Court Challan Register

export const challanRegKpis    = () => httpFIR.get('kpis/challan-reg').json<any>();
export const listChallanReg    = (params?: Record<string,string>) => httpFIR.get('challan-reg', { searchParams: params || {} }).json<any>();
export const createChallanReg  = (body: object) => httpFIR.post('challan-reg', { json: body }).json<any>();
export const getChallanReg     = (cid: string) => httpFIR.get(`challan-reg/${cid}`).json<any>();
export const admitChallanReg   = (cid: string, body: object) => httpFIR.patch(`challan-reg/${cid}/admit`, { json: body }).json<any>();
export const returnChallanReg  = (cid: string, body: object) => httpFIR.patch(`challan-reg/${cid}/return`, { json: body }).json<any>();
export const disposeChallanReg = (cid: string, body: object) => httpFIR.patch(`challan-reg/${cid}/dispose`, { json: body }).json<any>();
export const challanRegPrintUrl = (year?: number) => `/v1/challan-reg/print${year ? `?year=${year}` : ''}`;

// AI Case Diary — entry drafter + nudges
export const aiDiaryEntryDraft = (body: { fir_id: string; rough_notes: string; kind: string }) =>
  httpFIR.post('ai/diary-entry-draft', { json: body, timeout: 30_000 })
    .json<{ body_en: string; body_mr: string; suggested_kind: string }>();

export const getFIRDiaryNudges = (firId: string) =>
  httpFIR.get(`firs/${firId}/diary/nudges`)
    .json<{
      checklist_pending: number;
      checklist_items: { item_code: string; item_text: string; item_text_mr?: string }[];
      fsl_pending: number;
      mlc_pending: number;
      process_docs_pending: number;
      remand_expiring_hours: number | null;
      next_court_hearing_days: number | null;
    }>();

// ---------------------------------------------------------------------------
// Visitor & Application Intake Register (Phase 1–2 backend)
// Backed by backend/services/visitor/main.py. The dev proxy routes
// /v1/visitor/* to localhost:8006 (see vite.config.ts).
// ---------------------------------------------------------------------------

export type VisitorPurposeCategory =
  | "COMPLAINT" | "GRIEVANCE" | "RTI" | "NCR" | "VERIFICATION"
  | "LOST_FOUND" | "MISSING_PERSON" | "NOTICE_REPLY" | "BAIL"
  | "CHARACTER_CERTIFICATE" | "NOC" | "GENERAL_APPLICATION"
  | "INFORMATION_ONLY" | "OTHER";

export type VisitorStatus =
  | "RECEIVED" | "TRIAGED" | "ROUTED" | "IN_ACTION" | "ACTION_DONE"
  | "CITIZEN_INFORMED" | "SATISFIED" | "REJECTED" | "ABANDONED" | "REOPENED";

export type VisitorLinkKind =
  | "COMPLAINT" | "FIR" | "NCR" | "GRIEVANCE" | "RTI"
  | "NOTICE_S35" | "NOTICE_S41A" | "NOTICE_S175"
  | "VERIFICATION" | "MISSING_PERSON" | "LOST_FOUND"
  | "BAIL" | "PROCESS_DOC" | "STATION_DIARY" | "OTHER";

export interface VisitorIntakePayload {
  ps_code: string;
  channel?: "WALK_IN" | "PORTAL" | "POST" | "EMAIL" | "PHONE" | "WHATSAPP" | "COURT_REFERRAL" | "INTER_PS_TRANSFER" | "OTHER";
  visitor_name_en?: string;
  visitor_name_mr?: string;
  visitor_mobile?: string;
  visitor_age?: number;
  visitor_gender?: "M" | "F" | "O";
  visitor_address_en?: string;
  visitor_address_mr?: string;
  visitor_id_type?: "AADHAAR" | "PAN" | "DL" | "VOTER" | "PASSPORT" | "OTHER";
  visitor_id_number?: string;
  visitor_photo_key?: string;
  visitor_id_proof_key?: string;
  identity_verified?: boolean;
  identity_method?: "AADHAAR_OTP" | "OFFICER_ATTEST" | "NA" | "UNVERIFIED";
  companions?: { name: string; relation?: string; mobile?: string }[];
  purpose_category?: VisitorPurposeCategory;
  purpose_text?: string;
  purpose_text_mr?: string;
  language?: "mr" | "en" | "hi";
  letter_object_key?: string;
  letter_pages?: number;
  letter_received_in_lang?: "mr" | "en" | "hi";
}

export interface VisitorIntakeResult {
  id: string;
  vap_no: string;
  tracking_code: string;
  ps_code: string;
  visited_at: string;
  status: VisitorStatus;
  gd_no: string;
  genesis_event_seq: number;
  citizen_id: string | null;
  next_action: string;
}

export interface VisitorEvent {
  seq: number;
  at: string;
  kind: string;
  actor_kind: string;
  actor_id: string | null;
  body_en: string | null;
  body_mr: string | null;
  ref_kind: string | null;
  ref_id: string | null;
  payload_json: Record<string, unknown>;
  canonical_sha256: string;
}

export interface VisitorRow {
  id: string;
  ps_code: string;
  vap_no: string;
  tracking_code: string;
  visited_at: string | null;
  exit_at: string | null;
  channel: string;
  purpose_category: VisitorPurposeCategory | null;
  purpose_text: string | null;
  purpose_text_mr: string | null;
  language: string;
  visitor_name_en: string | null;
  visitor_name_mr: string | null;
  visitor_mobile: string | null;
  visitor_age: number | null;
  visitor_gender: string | null;
  visitor_address_en: string | null;
  visitor_address_mr: string | null;
  identity_verified: boolean;
  identity_method: string | null;
  companion_count: number;
  companions_json: { name: string; relation?: string; mobile?: string }[];
  letter_object_key: string | null;
  letter_ocr_text: string | null;
  letter_ocr_text_mr: string | null;
  status: VisitorStatus;
  sla_due_at: string | null;
  routed_to: VisitorLinkKind | null;
  routed_ref_id: string | null;
  routed_ref_no: string | null;
  routed_at: string | null;
  satisfaction_rating: number | null;
  gd_no: string | null;
  recent_events: VisitorEvent[];
  links: { child_kind: VisitorLinkKind; child_id: string; child_ref_no: string | null; linked_at: string; note?: string }[];
}

export interface VisitorListItem {
  id: string;
  ps_code: string;
  vap_no: string;
  tracking_code: string;
  visited_at: string;
  visitor_name_en: string | null;
  visitor_name_mr: string | null;
  visitor_mobile: string | null;
  purpose_category: VisitorPurposeCategory | null;
  purpose_text: string | null;
  status: VisitorStatus;
  sla_due_at: string | null;
  assigned_to: string | null;
  routed_to: VisitorLinkKind | null;
  routed_ref_no: string | null;
  overdue: boolean;
}

export const visitorIntake = (body: VisitorIntakePayload) =>
  httpFIR.post("visitor/intake", { json: body }).json<VisitorIntakeResult>();

export const getVisitor = (id: string) =>
  httpFIR.get(`visitor/${id}`).json<VisitorRow>();

export const getVisitorTimeline = (id: string, limit = 200) =>
  httpFIR.get(`visitor/${id}/timeline`, { searchParams: { limit } })
    .json<{ vap_id: string; events: VisitorEvent[]; count: number; chain_ok: boolean }>();

export const appendVisitorEvent = (
  id: string,
  body: { kind: string; actor_kind?: string; body_en?: string; body_mr?: string;
          ref_kind?: string; ref_id?: string; payload?: Record<string, unknown> },
) => httpFIR.post(`visitor/${id}/event`, { json: body }).json<{ id: number; seq: number; at: string; canonical_sha256: string }>();

export const triageVisitor = (
  id: string,
  body: {
    purpose_category: VisitorPurposeCategory;
    purpose_text?: string;
    purpose_text_mr?: string;
    language?: "mr" | "en" | "hi";
    assigned_to?: string;
    sla_hours_override?: number;
    letter_object_key?: string;
    letter_ocr_text?: string;
    letter_ocr_text_mr?: string;
    letter_pages?: number;
    note_en?: string;
    note_mr?: string;
  },
) => httpFIR.post(`visitor/${id}/triage`, { json: body })
       .json<{ id: string; status: VisitorStatus; purpose_category: string; sla_due_at: string | null; sla_hours: number | null; triaged_at: string; next_action: string }>();

export const routeVisitor = (
  id: string,
  body: {
    target_kind: VisitorLinkKind;
    mode?: "link_existing" | "create_stub";
    existing_ref_id?: string;
    existing_ref_no?: string;
    grievance?: { subject: string; body_en: string; body_mr: string; fir_id?: string };
    rti?: { subject: string; body: string; fee_paid_inr?: number; life_liberty?: boolean };
    note_en?: string;
    note_mr?: string;
  },
) => httpFIR.post(`visitor/${id}/route`, { json: body })
       .json<{ id: string; status: VisitorStatus; routed_to: VisitorLinkKind; routed_ref_id: string; routed_ref_no: string; routed_at: string }>();

export const assignVisitor = (
  id: string,
  body: { assigned_to: string; start_action?: boolean; note_en?: string; note_mr?: string },
) => httpFIR.post(`visitor/${id}/assign`, { json: body })
       .json<{ id: string; status: VisitorStatus; assigned_to: string }>();

export interface VisitorClassifySuggestion {
  category: VisitorPurposeCategory;
  confidence: number;
  raw_score: number;
  hits: string[];
  reason_en: string;
  reason_mr: string;
}

export interface VisitorClassifyResult {
  vap_id: string;
  method: string;
  top: VisitorPurposeCategory;
  top_confidence: number;
  sla_hours_top: number | null;
  suggestions: VisitorClassifySuggestion[];
  event_seq: number;
  applied_ocr: boolean;
}

export const classifyVisitor = (
  id: string,
  body: { letter_ocr_text?: string; letter_ocr_text_mr?: string; extra_text?: string } = {},
) => httpFIR.post(`visitor/${id}/classify`, { json: body }).json<VisitorClassifyResult>();

export const visitorActionDone = (id: string, body: { outcome_en?: string; outcome_mr?: string }) =>
  httpFIR.post(`visitor/${id}/action-done`, { json: body })
    .json<{ id: string; status: VisitorStatus; decision_made_at: string }>();

export const visitorInformCitizen = (
  id: string,
  body: {
    channel?: "IN_PERSON" | "SMS" | "WHATSAPP" | "EMAIL" | "PHONE" | "POST";
    note_en?: string;
    note_mr?: string;
    send_sms?: boolean;
  },
) => httpFIR.post(`visitor/${id}/inform-citizen`, { json: body })
       .json<{ id: string; status: VisitorStatus; channel: string; notification_id: string | null; sms_enqueued: boolean }>();

export const visitorSatisfaction = (
  id: string,
  body: {
    rating: number;
    note_en?: string;
    note_mr?: string;
    signature_object_key?: string;
    channel?: "IN_PERSON" | "PORTAL" | "SMS" | "WHATSAPP" | "IVR" | "EMAIL";
  },
) => httpFIR.post(`visitor/${id}/satisfaction`, { json: body })
       .json<{ id: string; status: VisitorStatus; rating: number; needs_followup: boolean }>();

export const visitorReject = (id: string, body: { reason_en: string; reason_mr?: string }) =>
  httpFIR.post(`visitor/${id}/reject`, { json: body })
    .json<{ id: string; status: VisitorStatus; rejection_reason: string }>();

export const visitorAbandon = (id: string, body: { note_en?: string; note_mr?: string }) =>
  httpFIR.post(`visitor/${id}/abandon`, { json: body })
    .json<{ id: string; status: VisitorStatus }>();

export const visitorReopen = (
  id: string,
  body: { reason_en: string; reason_mr?: string; escalate_to_grievance?: boolean },
) => httpFIR.post(`visitor/${id}/reopen`, { json: body })
       .json<{ id: string; status: VisitorStatus; reopened_count: number;
               escalated_grievance_id: string | null; escalated_grievance_no: string | null }>();

export interface VisitorKpis {
  window_days: number;
  total: number;
  open: number;
  overdue: number;
  satisfied_recent: number;
  avg_minutes_to_route: number | null;
  avg_hours_to_close: number | null;
  avg_hours_at_station: number | null;
  satisfaction: { rated: number; satisfied: number; rate: number | null; avg_rating: number | null };
  reopen: { total_in_window: number; reopened: number; rate: number | null };
  by_status: { status: VisitorStatus; n: number }[];
  by_purpose: { category: string; n: number }[];
  daily_intake: { day: string; n: number }[];
}

export const getVisitorKpis = (days = 14) =>
  httpFIR.get("visitor-kpis", { searchParams: { days } }).json<VisitorKpis>();

// The journey PDF is opened in a new tab via <a href target="_blank">, which
// strips the Authorization header — fall back to ?token= query-string auth
// (same convention as the contradictions/raji-nama print URLs).
export const visitorJourneyPdfUrl = (id: string) =>
  withAuthQS(`/v1/visitor/${id}/journey.pdf`);

export const listVisitors = (params: {
  status?: string;
  purpose_category?: VisitorPurposeCategory;
  assigned_to?: string;
  overdue?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
} = {}) => {
  const sp: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp[k] = String(v);
  }
  return httpFIR.get("visitor", { searchParams: sp })
    .json<{ items: VisitorListItem[]; total: number; limit: number; offset: number }>();
};
